import { useState, useEffect, useRef, lazy, Suspense } from 'react';

/* ─────────────────────────────────────────────
   FONT LOADER — injects preconnect + stylesheet
   link tags into <head> once, non-blocking.
   Avoids @import inside <style> which blocks
   the entire render until fonts download.
───────────────────────────────────────────── */
function useFonts() {
  useEffect(() => {
    if (document.getElementById('kfx-fonts')) return; // already injected
    const preconnect1 = Object.assign(document.createElement('link'), {
      rel: 'preconnect', href: 'https://fonts.googleapis.com',
    });
    const preconnect2 = Object.assign(document.createElement('link'), {
      rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous',
    });
    const stylesheet = Object.assign(document.createElement('link'), {
      id:   'kfx-fonts',
      rel:  'stylesheet',
      href: 'https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,300;12..96,400;12..96,500;12..96,600;12..96,700;12..96,800&family=DM+Mono:ital,wght@0,300;0,400;0,500;1,400&display=swap',
    });
    document.head.append(preconnect1, preconnect2, stylesheet);
  }, []);
}
import FeedbackWidget from './FeedbackWidget.jsx';

/* ─────────────────────────────────────────────
   LAZY SECTION — renders children only once
   the sentinel enters the viewport. Prevents
   below-fold components from running JS,
   building state, or painting until needed.
───────────────────────────────────────────── */
function LazySection({ children, rootMargin = '200px' }) {
  const ref    = useRef(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (show) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setShow(true); io.disconnect(); }
    }, { rootMargin });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [show, rootMargin]);
  // Sentinel div preserves layout space even before render
  return <div ref={ref}>{show ? children : <div style={{ minHeight: 1 }} />}</div>;
}
function DrawdownChart({ result, peak, dd }) {
  const W = 480, H = 180, PAD = { top:16, right:12, bottom:28, left:46 };
  const iW = W - PAD.left - PAD.right;
  const iH = H - PAD.top  - PAD.bottom;

  const start  = peak * (1 - dd / 100);
  const target = peak;
  const pct    = result ? parseFloat(result.recPct) / 100 : 0;
  const medM   = result?.medMonths ? parseFloat(result.medMonths) : 6;
  const months = 12;

  // Build data points
  const pts = Array.from({ length: months + 1 }, (_, i) => {
    const progress  = Math.min(i / Math.max(medM, 0.1), 1);
    const recovered = start + (target - start) * Math.pow(progress, 0.65) * pct;
    const failDrop  = (1 - pct) * 0.55;
    const failed    = start * (1 - failDrop * Math.min(i / Math.max(medM * 1.4, 1), 1));
    return {
      month: i,
      rec:   Math.max(recovered, start * 0.3),
      fail:  Math.max(failed,   start * 0.15),
    };
  });

  const minV = Math.min(...pts.map(p => p.fail)) * 0.95;
  const maxV = target * 1.06;
  const toX  = i  => PAD.left + (i / months) * iW;
  const toY  = v  => PAD.top  + iH - ((v - minV) / (maxV - minV)) * iH;

  // Build SVG path strings
  const recPath  = pts.map((p, i) => `${i===0?'M':'L'}${toX(p.month).toFixed(1)},${toY(p.rec).toFixed(1)}`).join(' ');
  const failPath = pts.map((p, i) => `${i===0?'M':'L'}${toX(p.month).toFixed(1)},${toY(p.fail).toFixed(1)}`).join(' ');

  // Area fills (path + bottom close)
  const baseY    = toY(minV);
  const recArea  = `${recPath} L${toX(months).toFixed(1)},${baseY} L${toX(0).toFixed(1)},${baseY} Z`;
  const failArea = `${failPath} L${toX(months).toFixed(1)},${baseY} L${toX(0).toFixed(1)},${baseY} Z`;

  // Y-axis ticks
  const yTicks = [minV, start, (start+target)/2, target].map(v => ({
    v, y: toY(v), label: `$${(v/1000).toFixed(0)}k`
  }));
  // X-axis ticks
  const xTicks = [0,2,4,6,8,10,12];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', height:'auto', display:'block', overflow:'visible' }} aria-hidden="true">
      <defs>
        <linearGradient id="kfxGradRec" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#2ECC8A" stopOpacity="0.28" />
          <stop offset="100%" stopColor="#2ECC8A" stopOpacity="0.03" />
        </linearGradient>
        <linearGradient id="kfxGradFail" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#E05555" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#E05555" stopOpacity="0.02" />
        </linearGradient>
      </defs>

      {/* Grid lines */}
      {yTicks.map(t => (
        <line key={t.v} x1={PAD.left} x2={W-PAD.right} y1={t.y} y2={t.y}
          stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
      ))}

      {/* Target reference line */}
      <line x1={PAD.left} x2={W-PAD.right} y1={toY(target)} y2={toY(target)}
        stroke="rgba(212,168,67,0.5)" strokeWidth="1.2" strokeDasharray="5,4" />
      <text x={PAD.left+4} y={toY(target)-5} fill="rgba(212,168,67,0.7)"
        fontSize="8" fontFamily="'DM Mono',monospace">PEAK ${peak.toLocaleString()}</text>

      {/* Start reference line */}
      <line x1={PAD.left} x2={W-PAD.right} y1={toY(start)} y2={toY(start)}
        stroke="rgba(255,255,255,0.18)" strokeWidth="1" strokeDasharray="3,3" />
      <text x={PAD.left+4} y={toY(start)+13} fill="rgba(255,255,255,0.35)"
        fontSize="8" fontFamily="'DM Mono',monospace">START ${Math.round(start).toLocaleString()}</text>

      {/* Area fills */}
      <path d={failArea} fill="url(#kfxGradFail)" />
      <path d={recArea}  fill="url(#kfxGradRec)"  />

      {/* Lines */}
      <path d={failPath} fill="none" stroke="#E05555" strokeWidth="1.5"
        strokeDasharray="5,3" opacity="0.75" />
      <path d={recPath}  fill="none" stroke="#2ECC8A" strokeWidth="2"
        opacity="0.9" />

      {/* End dots */}
      <circle cx={toX(months)} cy={toY(pts[months].rec)}  r="3.5" fill="#2ECC8A" opacity="0.9" />
      <circle cx={toX(months)} cy={toY(pts[months].fail)} r="3"   fill="#E05555" opacity="0.75" />

      {/* Y axis labels */}
      {yTicks.map(t => (
        <text key={t.v} x={PAD.left-4} y={t.y+4} textAnchor="end"
          fill="var(--text-28)" fontSize="8" fontFamily="'DM Mono',monospace">
          {t.label}
        </text>
      ))}

      {/* X axis labels */}
      {xTicks.map(i => (
        <text key={i} x={toX(i)} y={H-6} textAnchor="middle"
          fill="var(--text-28)" fontSize="8" fontFamily="'DM Mono',monospace">
          {i===0 ? 'Now' : `M${i}`}
        </text>
      ))}
    </svg>
  );
}


function EquityCurve() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let raf = null;
    let offset = 0;
    let visible = false; // only animate when in viewport

    // ── Resize: throttled with ResizeObserver ──
    let resizePending = false;
    const applyResize = () => {
      resizePending = false;
      canvas.width  = canvas.offsetWidth  * devicePixelRatio;
      canvas.height = canvas.offsetHeight * devicePixelRatio;
      ctx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
      cachedGradient = null; // invalidate gradient on resize
    };
    const resize = () => {
      if (!resizePending) { resizePending = true; requestAnimationFrame(applyResize); }
    };
    applyResize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    // ── Data points (generated once) ──
    let val = 10000;
    const pts = Array.from({ length: 200 }, (_, i) => {
      val += (Math.random() - 0.44) * 180 + Math.sin(i * 0.08) * 40;
      return Math.max(8000, val);
    });
    const visiblePts = pts.slice(0, 160);
    const minV = Math.min(...visiblePts);
    const maxV = Math.max(...visiblePts);
    const range = maxV - minV || 1;

    // ── Gradient cache — recreated only after resize ──
    let cachedGradient = null;
    const getGradient = (H) => {
      if (!cachedGradient) {
        cachedGradient = ctx.createLinearGradient(0, 0, 0, H);
        cachedGradient.addColorStop(0,   'rgba(212,168,67,0.18)');
        cachedGradient.addColorStop(0.6, 'rgba(212,168,67,0.04)');
        cachedGradient.addColorStop(1,   'rgba(212,168,67,0)');
      }
      return cachedGradient;
    };

    // ── Draw ──
    const draw = () => {
      if (!visible) { raf = null; return; } // pause when off-screen
      offset += 0.25;
      const W = canvas.offsetWidth, H = canvas.offsetHeight;
      ctx.clearRect(0, 0, W, H);
      const toX = i => (i / (visiblePts.length - 1)) * W;
      const toY = v => H * 0.12 + ((maxV - v) / range) * H * 0.76;

      // Area fill
      ctx.beginPath();
      ctx.moveTo(toX(0), H);
      visiblePts.forEach((v, i) => ctx.lineTo(toX(i), toY(v)));
      ctx.lineTo(toX(visiblePts.length - 1), H);
      ctx.closePath();
      ctx.fillStyle = getGradient(H);
      ctx.fill();

      // Line
      ctx.beginPath();
      visiblePts.forEach((v, i) => i === 0 ? ctx.moveTo(toX(i), toY(v)) : ctx.lineTo(toX(i), toY(v)));
      ctx.strokeStyle = 'rgba(212,168,67,0.55)';
      ctx.lineWidth   = 1.5;
      ctx.stroke();

      // Pulsing dot
      const last  = visiblePts[visiblePts.length - 1];
      const lx    = toX(visiblePts.length - 1);
      const ly    = toY(last);
      const pulse = (Math.sin(offset * 0.08) + 1) / 2;
      ctx.beginPath(); ctx.arc(lx, ly, 3 + pulse * 2, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(212,168,67,${0.4 + pulse * 0.5})`; ctx.fill();
      ctx.beginPath(); ctx.arc(lx, ly, 3, 0, Math.PI * 2);
      ctx.fillStyle = '#D4A843'; ctx.fill();

      raf = requestAnimationFrame(draw);
    };

    // ── Intersection Observer: only run RAF when visible ──
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible && !raf) draw(); // resume
    }, { threshold: 0.1 });
    io.observe(canvas);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
    };
  }, []);
  return <canvas ref={ref} style={{ position:'absolute', inset:0, width:'100%', height:'100%' }} />;
}

/* ─────────────────────────────────────────────
   ANIMATED COUNTER
───────────────────────────────────────────── */
function Counter({ target, suffix = '', prefix = '', decimals = 0 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      obs.disconnect();
      const start = performance.now();
      const dur = 1800;
      const tick = now => {
        const p = Math.min((now - start) / dur, 1);
        const ease = 1 - Math.pow(1 - p, 4);
        setVal(+(ease * target).toFixed(decimals));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target, decimals]);
  return <span ref={ref}>{prefix}{typeof val === 'number' && decimals > 0 ? val.toFixed(decimals) : val.toLocaleString()}{suffix}</span>;
}

/* ─────────────────────────────────────────────
   SCROLL-REVEAL WRAPPER
───────────────────────────────────────────── */
function Reveal({ children, delay = 0, style = {} }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold: 0.12 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(28px)',
      transition: `opacity 0.65s ease ${delay}ms, transform 0.65s ease ${delay}ms`,
      ...style,
    }}>
      {children}
    </div>
  );
}

/* ─────────────────────────────────────────────
   FEATURE ICON — SVG icon set (replaces emoji)
───────────────────────────────────────────── */
function FeatureIcon({ name }) {
  const s = { width:20, height:20, stroke:'var(--gold)', fill:'none', strokeWidth:1.75, strokeLinecap:'round', strokeLinejoin:'round' };
  switch (name) {
    case 'journal': return (
      <svg {...s} viewBox="0 0 24 24" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
    );
    case 'analytics': return (
      <svg {...s} viewBox="0 0 24 24" aria-hidden="true"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
    );
    case 'ai': return (
      <svg {...s} viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/></svg>
    );
    case 'simulator': return (
      <svg {...s} viewBox="0 0 24 24" aria-hidden="true"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
    );
    case 'grade': return (
      <svg {...s} viewBox="0 0 24 24" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
    );
    case 'discipline': return (
      <svg {...s} viewBox="0 0 24 24" aria-hidden="true"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
    );
    default: return (
      <svg {...s} viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10"/></svg>
    );
  }
}

/* ─────────────────────────────────────────────
   TRADE LOG MOCKUP
───────────────────────────────────────────── */
const MOCK_TRADES = [
  { pair:'XAUUSD', dir:'BUY',  rr:'+3.2R', pnl:'+$320', time:'09:14', grade:'A', win:true  },
  { pair:'EURUSD', dir:'SELL', rr:'-1.0R', pnl:'-$100', time:'11:02', grade:'B', win:false },
  { pair:'GBPJPY', dir:'BUY',  rr:'+2.1R', pnl:'+$210', time:'13:45', grade:'A', win:true  },
  { pair:'USDJPY', dir:'SELL', rr:'+1.8R', pnl:'+$180', time:'15:30', grade:'A', win:true  },
  { pair:'EURUSD', dir:'BUY',  rr:'-1.0R', pnl:'-$100', time:'16:05', grade:'C', win:false },
];
function TradeMockup() {
  const [visible, setVisible] = useState(1);
  useEffect(() => {
    const t = setInterval(() => setVisible(v => v < MOCK_TRADES.length ? v + 1 : v), 900);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={{ background:'var(--surface3)', borderRadius:16, border:'1px solid rgba(212,168,67,0.15)', overflow:'hidden', fontFamily:"'DM Mono',monospace" }}>
      <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--overlay-6)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div className="u-flex u-gap-6">
          {['#FF5F57','#FEBC2E','#28C840'].map(c => <div key={c} style={{ width:9, height:9, borderRadius:'50%', background:c }} />)}
        </div>
        <span style={{ fontSize:10, color:'rgba(212,168,67,0.5)', letterSpacing:'0.1em' }}>KFX JOURNAL — TRADE LOG</span>
        <div style={{ width:44 }} />
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'72px 44px 1fr 64px 64px 32px', gap:6, padding:'9px 16px 7px', borderBottom:'1px solid var(--overlay-4)' }}>
        {['PAIR','DIR','TIME','R:R','P&L','GRD'].map(h => (
          <span key={h} style={{ fontSize:9, fontWeight:700, letterSpacing:'0.12em', color:'var(--text-22)', textTransform:'uppercase' }}>{h}</span>
        ))}
      </div>
      <div style={{ padding:'4px 0 8px' }}>
        {MOCK_TRADES.slice(0, visible).map((t, i) => (
          <div key={i} style={{ display:'grid', gridTemplateColumns:'72px 44px 1fr 64px 64px 32px', gap:6, padding:'8px 16px', alignItems:'center', animation:'kfx-tradeIn 0.35s ease both', background: i%2===0?'var(--overlay-1)':'transparent' }}>
            <span style={{ fontSize:11, fontWeight:700, color:'#e8e0d0', letterSpacing:'0.02em' }}>{t.pair}</span>
            <span style={{ fontSize:9, fontWeight:700, padding:'2px 6px', borderRadius:4, background:t.dir==='BUY'?'rgba(46,204,138,0.12)':'rgba(224,85,85,0.12)', color:t.dir==='BUY'?'#2ECC8A':'#E05555', width:'fit-content', letterSpacing:'0.08em' }}>{t.dir}</span>
            <span style={{ fontSize:10, color:'var(--text-30)' }}>{t.time}</span>
            <span style={{ fontSize:11, fontWeight:600, color:t.win?'#2ECC8A':'#E05555' }}>{t.rr}</span>
            <span style={{ fontSize:11, fontWeight:700, color:t.win?'#2ECC8A':'#E05555' }}>{t.pnl}</span>
            <span style={{ fontSize:10, fontWeight:700, color:t.grade==='A'?'#D4A843':t.grade==='B'?'#7B9FE0':'var(--text-28)' }}>{t.grade}</span>
          </div>
        ))}
      </div>
      {visible === MOCK_TRADES.length && (
        <div style={{ margin:'0 12px 12px', padding:'9px 14px', background:'rgba(212,168,67,0.06)', borderRadius:8, border:'1px solid rgba(212,168,67,0.12)', display:'flex', justifyContent:'space-between', alignItems:'center', animation:'kfx-tradeIn 0.4s ease both' }}>
          <span style={{ fontSize:10, color:'rgba(212,168,67,0.7)', letterSpacing:'0.08em' }}>TODAY · 5 TRADES</span>
          <span style={{ fontSize:12, fontWeight:700, color:'#2ECC8A' }}>+$510 · 60% WR</span>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   AI COACH MOCKUP
───────────────────────────────────────────── */
function AiMockup() {
  const full = "Looking at your last 47 NY open trades: you're entering 4–8 minutes after the open bell when spreads are still elevated. Your win rate is 31% during 09:30–09:45 but jumps to 68% after 10:00. Wait 30 more minutes.";
  const [step, setStep] = useState(0);
  const [typed, setTyped] = useState('');
  useEffect(() => {
    const t1 = setTimeout(() => setStep(1), 800);
    const t2 = setTimeout(() => setStep(2), 1600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);
  useEffect(() => {
    if (step !== 2) return;
    let i = 0;
    const t = setInterval(() => {
      setTyped(full.slice(0, ++i));
      if (i >= full.length) clearInterval(t);
    }, 16);
    return () => clearInterval(t);
  }, [step]);
  return (
    <div style={{ background:'var(--surface3)', borderRadius:16, border:'1px solid rgba(212,168,67,0.15)', overflow:'hidden', fontFamily:"'Bricolage Grotesque',sans-serif" }}>
      <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--overlay-6)', display:'flex', alignItems:'center', gap:10 }}>
        <div style={{ width:28, height:28, borderRadius:8, background:'linear-gradient(135deg,#D4A843,#7a6030)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13 }}>🤖</div>
        <div>
          <div style={{ fontSize:12, fontWeight:700, color:'#e8e0d0' }}>KFX AI Coach</div>
          <div style={{ fontSize:10, color:'#2ECC8A', display:'flex', alignItems:'center', gap:4 }}>
            <span style={{ width:5, height:5, borderRadius:'50%', background:'#2ECC8A', display:'inline-block', animation:'kfx-pulse 2s infinite' }} />Online · Analyzing your trades
          </div>
        </div>
      </div>
      <div style={{ padding:16, display:'flex', flexDirection:'column', gap:12, minHeight:160 }}>
        {step >= 1 && (
          <div style={{ display:'flex', justifyContent:'flex-end', animation:'kfx-tradeIn 0.3s ease both' }}>
            <div style={{ maxWidth:'80%', padding:'10px 14px', borderRadius:'12px 12px 2px 12px', background:'rgba(212,168,67,0.12)', border:'1px solid rgba(212,168,67,0.2)', fontSize:12.5, color:'#e8e0d0', lineHeight:1.6 }}>Why do I keep losing during NY open?</div>
          </div>
        )}
        {step >= 2 && (
          <div style={{ display:'flex', gap:8, animation:'kfx-tradeIn 0.3s ease both' }}>
            <div style={{ width:24, height:24, borderRadius:6, background:'linear-gradient(135deg,#D4A843,#7a6030)', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, color:'#080808', marginTop:2, fontFamily:"'DM Mono',monospace" }}>AI</div>
            <div style={{ maxWidth:'88%', padding:'10px 14px', borderRadius:'2px 12px 12px 12px', background:'var(--overlay-4)', border:'1px solid var(--overlay-7)', fontSize:12.5, color:'var(--text-70)', lineHeight:1.7 }}>
              {typed}
              {typed.length < full.length && <span style={{ animation:'kfx-blink 0.7s infinite' }}>▋</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   COMPARISON TABLE
───────────────────────────────────────────── */
function ComparisonTable() {
  const rows = [
    { feat:'AI Coach powered by Claude',     kfx:true,  others:false },
    { feat:'Built for East African traders', kfx:true,  others:false },
    { feat:'MT4/MT5 CSV import',             kfx:true,  others:true  },
    { feat:'Execution grading (A/B/C)',       kfx:true,  others:false },
    { feat:'What-If trade simulator',        kfx:true,  others:false },
    { feat:'Session heatmap analytics',      kfx:true,  others:true  },
    { feat:'100% free — no credit card',     kfx:true,  others:false },
    { feat:'Trade emotion tagging',          kfx:true,  others:true  },
  ];
  return (
    <div style={{ overflowX:'auto' }}>
      <table style={{ width:'100%', minWidth:540, borderCollapse:'collapse' }}>
        <thead>
          <tr>
            <th style={{ padding:'12px 16px', textAlign:'left', fontSize:11, fontWeight:600, color:'var(--text-28)', letterSpacing:'0.1em', textTransform:'uppercase', borderBottom:'1px solid var(--overlay-6)' }}>Feature</th>
            <th style={{ padding:'12px 16px', textAlign:'center', fontSize:11, fontWeight:700, color:'#D4A843', letterSpacing:'0.1em', textTransform:'uppercase', borderBottom:'1px solid rgba(212,168,67,0.2)', background:'rgba(212,168,67,0.04)' }}>KFX Journal</th>
            <th style={{ padding:'12px 16px', textAlign:'center', fontSize:11, fontWeight:600, color:'var(--text-28)', letterSpacing:'0.1em', textTransform:'uppercase', borderBottom:'1px solid var(--overlay-6)' }}>Others</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderBottom:'1px solid var(--overlay-4)' }}>
              <td style={{ padding:'13px 16px', fontSize:13, color:'var(--text-70)' }}>{r.feat}</td>
              <td style={{ padding:'13px 16px', textAlign:'center', background:'rgba(212,168,67,0.03)' }}>
                {r.kfx ? <span style={{ color:'#2ECC8A', fontSize:16 }}>✓</span> : <span style={{ color:'var(--text-18)', fontSize:14 }}>—</span>}
              </td>
              <td style={{ padding:'13px 16px', textAlign:'center' }}>
                {r.others ? <span style={{ color:'var(--text-35)', fontSize:16 }}>✓</span> : <span style={{ color:'#E05555', fontSize:16 }}>✗</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─────────────────────────────────────────────
   TOOLS DROPDOWN MENU
───────────────────────────────────────────── */
const TOOLS_ITEMS = [
  { icon:'⚖️', label:'Position Sizing',      sub:'Risk-based position calculator',     badge:null   },
  { icon:'🧮', label:'Lot Size Calculator',   sub:'Precise lot sizing for any pair',   badge:null   },
  { icon:'📉', label:'Drawdown Recovery',     sub:'Monte Carlo recovery simulation',   badge:null   },
  { icon:'🎯', label:'Risk:Reward Calc',      sub:'RR ratio with lot size & payout',   badge:'NEW'  },
  { icon:'🏆', label:'Profit Target Planner', sub:'Reverse-engineer your monthly goal',badge:'NEW'  },
  { icon:'🏛️', label:'Prop Firm Tracker',     sub:'Challenge progress & limit alerts', badge:'NEW'  },
  { icon:'🌐', label:'Session Clock',         sub:'ICT killzones · Forex & Indices · EAT', badge:'NEW'  },
  { icon:'📅', label:'Consistency Rule',      sub:'Daily profit share vs firm limit',  badge:'NEW'  },
];
function ToolsDropdown({ onToolSelect, onGetStarted, darkMode }) {
  const dm = darkMode !== false; // default dark
  return (
    <div style={{
      position:'absolute', top:'calc(100% + 12px)', left:'50%', transform:'translateX(-50%)',
      width:520,
      background: dm ? '#0d0d0b' : '#FFFFFF',
      border: `1px solid ${dm ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.10)'}`,
      borderRadius:16, overflow:'hidden',
      boxShadow: dm
        ? '0 24px 64px rgba(0,0,0,0.75), 0 0 0 1px rgba(212,168,67,0.06)'
        : '0 12px 48px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06)',
      animation:'kfx-dropIn 0.2s cubic-bezier(0.16,1,0.3,1) both',
    }}>
      {/* Header */}
      <div style={{
        padding:'12px 16px 10px',
        borderBottom: `1px solid ${dm ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)'}`,
        display:'flex', alignItems:'center', justifyContent:'space-between',
      }}>
        <span style={{ fontSize:9, fontWeight:700, color: dm ? 'rgba(212,168,67,0.55)' : '#A67A10', letterSpacing:'0.14em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace" }}>Trading Tools</span>
        <span style={{ fontSize:9, color: dm ? 'rgba(237,232,222,0.30)' : 'rgba(26,26,22,0.45)', fontFamily:"'DM Mono',monospace", letterSpacing:'0.06em' }}>8 FREE CALCULATORS</span>
      </div>

      {/* 2-column grid */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:0, padding:'8px' }}>
        {TOOLS_ITEMS.map(item => (
          <button key={item.label} data-tool-item onClick={() => onToolSelect(item.label)} className="lp-dd-input-row" style={{
            padding:'10px 12px', background:'transparent', border:'1px solid transparent',
            borderRadius:10, display:'flex', alignItems:'flex-start', gap:10, cursor:'pointer',
            textAlign:'left', width:'100%',
          }}
          >
            <div style={{ width:32, height:32, borderRadius:8, background:'rgba(212,168,67,0.07)', border:'1px solid rgba(212,168,67,0.14)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, flexShrink:0, marginTop:1 }}>{item.icon}</div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:2 }}>
                <span style={{ fontSize:12, fontWeight:700, color: dm ? '#EDE8DE' : '#1A1A16', letterSpacing:'0.01em', fontFamily:"'Bricolage Grotesque',sans-serif" }}>{item.label}</span>
                {item.badge && (
                  <span style={{ fontSize:8, fontWeight:700, padding:'1px 5px', borderRadius:3, background:'rgba(46,204,138,0.15)', color:'#2ECC8A', letterSpacing:'0.08em', border:'1px solid rgba(46,204,138,0.22)', fontFamily:"'DM Mono',monospace" }}>{item.badge}</span>
                )}
              </div>
              <div style={{ fontSize:10.5, color: dm ? 'rgba(237,232,222,0.42)' : 'rgba(26,26,22,0.58)', lineHeight:1.4, fontFamily:"'Bricolage Grotesque',sans-serif" }}>{item.sub}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Footer CTA */}
      <div style={{
        margin:'0 8px 8px', padding:'12px 16px',
        background: dm ? 'linear-gradient(135deg,rgba(212,168,67,0.07),rgba(212,168,67,0.02))' : 'linear-gradient(135deg,rgba(166,122,16,0.08),rgba(166,122,16,0.03))',
        border: `1px solid ${dm ? 'rgba(212,168,67,0.15)' : 'rgba(166,122,16,0.22)'}`,
        borderRadius:10, display:'flex', alignItems:'center', justifyContent:'space-between', gap:12,
      }}>
        <div>
          <div style={{ fontSize:12, fontWeight:700, color: dm ? '#EDE8DE' : '#1A1A16', fontFamily:"'Bricolage Grotesque',sans-serif", marginBottom:2 }}>All tools included free</div>
          <div style={{ fontSize:10, color: dm ? 'rgba(237,232,222,0.42)' : 'rgba(26,26,22,0.58)', fontFamily:"'DM Mono',monospace", letterSpacing:'0.04em' }}>No account needed · use them right now</div>
        </div>
        <button onClick={onGetStarted} style={{ padding:'8px 16px', background:'linear-gradient(135deg,#D4A843,#8a6820)', border:'none', borderRadius:7, fontSize:10, fontWeight:700, color:'#080808', fontFamily:"'DM Mono',monospace", letterSpacing:'0.08em', textTransform:'uppercase', cursor:'pointer', whiteSpace:'nowrap', flexShrink:0, transition:'opacity 0.15s' }}
        >Get Started →</button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   TOOL PANELS
───────────────────────────────────────────── */

// All major forex pairs with pip values (per standard lot, in USD)
const ALL_PAIRS = [
  // Majors
  { pair:'EURUSD', pv:10,   cat:'Majors'    },
  { pair:'GBPUSD', pv:10,   cat:'Majors'    },
  { pair:'USDJPY', pv:9.09, cat:'Majors'    },
  { pair:'USDCHF', pv:11.2, cat:'Majors'    },
  { pair:'USDCAD', pv:7.46, cat:'Majors'    },
  { pair:'AUDUSD', pv:10,   cat:'Majors'    },
  { pair:'NZDUSD', pv:10,   cat:'Majors'    },
  // Crosses
  { pair:'EURGBP', pv:12.7, cat:'Crosses'   },
  { pair:'EURJPY', pv:9.09, cat:'Crosses'   },
  { pair:'EURCHF', pv:11.2, cat:'Crosses'   },
  { pair:'EURCAD', pv:7.46, cat:'Crosses'   },
  { pair:'EURAUD', pv:10,   cat:'Crosses'   },
  { pair:'EURNZD', pv:10,   cat:'Crosses'   },
  { pair:'GBPJPY', pv:9.09, cat:'Crosses'   },
  { pair:'GBPCHF', pv:11.2, cat:'Crosses'   },
  { pair:'GBPCAD', pv:7.46, cat:'Crosses'   },
  { pair:'GBPAUD', pv:10,   cat:'Crosses'   },
  { pair:'GBPNZD', pv:10,   cat:'Crosses'   },
  { pair:'AUDJPY', pv:9.09, cat:'Crosses'   },
  { pair:'AUDCAD', pv:7.46, cat:'Crosses'   },
  { pair:'AUDCHF', pv:11.2, cat:'Crosses'   },
  { pair:'AUDNZD', pv:10,   cat:'Crosses'   },
  { pair:'CADJPY', pv:9.09, cat:'Crosses'   },
  { pair:'CADCHF', pv:11.2, cat:'Crosses'   },
  { pair:'CHFJPY', pv:9.09, cat:'Crosses'   },
  { pair:'NZDJPY', pv:9.09, cat:'Crosses'   },
  { pair:'NZDCAD', pv:7.46, cat:'Crosses'   },
  { pair:'NZDCHF', pv:11.2, cat:'Crosses'   },
  // Exotics / Metals
  { pair:'XAUUSD', pv:10,   cat:'Metals'    },
  { pair:'XAGUSD', pv:50,   cat:'Metals'    },
  { pair:'USDZAR', pv:0.55, cat:'Exotics'   },
  { pair:'USDMXN', pv:0.52, cat:'Exotics'   },
  { pair:'USDSEK', pv:0.96, cat:'Exotics'   },
  { pair:'USDNOK', pv:0.95, cat:'Exotics'   },
  { pair:'USDTRY', pv:0.31, cat:'Exotics'   },
  { pair:'USDSGD', pv:7.4,  cat:'Exotics'   },
  { pair:'USDHKD', pv:1.28, cat:'Exotics'   },
];

const PAIR_PV = Object.fromEntries(ALL_PAIRS.map(p => [p.pair, p.pv]));

// Reusable text input — NO type="number", NO focus state (causes re-render/blocking bug)
function CalcInput({ label, value, onChange, prefix, suffix }) {
  return (
    <div className="u-flex-col u-gap-6">
      <label className="u-text-label">{label}</label>
      <div style={{ display:'flex', alignItems:'center', background:'var(--overlay-4)', border:'1px solid var(--overlay-10)', borderRadius:8, overflow:'hidden' }}>
        {prefix && <span style={{ padding:'0 10px', fontSize:12, color:'var(--text-35)', fontFamily:"'DM Mono',monospace", borderRight:'1px solid rgba(255,255,255,0.07)', whiteSpace:'nowrap' }}>{prefix}</span>}
        <input
          type="text"
          inputMode="decimal"
          value={value}
          onChange={e => {
            const v = e.target.value;
            if (/^-?\d*\.?\d*$/.test(v) || v === '' || v === '-') onChange(v);
          }}
          style={{ flex:1, background:'transparent', border:'none', outline:'none', padding:'11px 12px', fontSize:14, fontWeight:600, color:'var(--text)', fontFamily:"'DM Mono',monospace", minWidth:0 }}
        />
        {suffix && <span style={{ padding:'0 10px', fontSize:11, color:'var(--text-30)', fontFamily:"'DM Mono',monospace", borderLeft:'1px solid rgba(255,255,255,0.07)', whiteSpace:'nowrap' }}>{suffix}</span>}
      </div>
    </div>
  );
}

function PairSelect({ value, onChange }) {
  const cats = [...new Set(ALL_PAIRS.map(p => p.cat))];
  return (
    <div className="u-flex-col u-gap-6">
      <label className="u-text-label">Pair</label>
      <select value={value} onChange={e => onChange(e.target.value)} style={{
        background:'var(--overlay-6)', border:'1px solid var(--overlay-10)', borderRadius:8,
        padding:'11px 12px', fontSize:13, fontWeight:600, color:'var(--text)',
        fontFamily:"'DM Mono',monospace", outline:'none', cursor:'pointer',
        appearance:'auto',
      }}>
        {cats.map(cat => (
          <optgroup key={cat} label={`── ${cat} ──`} style={{ background:'var(--surface2)', color:'rgba(212,168,67,0.6)', fontSize:10 }}>
            {ALL_PAIRS.filter(p => p.cat === cat).map(p => (
              <option key={p.pair} value={p.pair} style={{ background:'var(--surface2)', color:'var(--text)' }}>{p.pair}</option>
            ))}
          </optgroup>
        ))}
      </select>
    </div>
  );
}

function StatRow({ label, val, accent }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 14px', background:'var(--overlay-3)', borderRadius:8, border:'1px solid var(--overlay-6)' }}>
      <span style={{ fontSize:11, color:'var(--text-45)', fontFamily:"'DM Mono',monospace" }}>{label}</span>
      <span style={{ fontSize:13, fontWeight:700, color: accent || 'var(--text)', fontFamily:"'DM Mono',monospace" }}>{val}</span>
    </div>
  );
}

function ToolPositionSizing() {
  const [acct, setAcct] = useState('1000');
  const [risk, setRisk] = useState('1');
  const [sl,   setSl]   = useState('20');
  const [pair, setPair] = useState('EURUSD');

  const pv       = PAIR_PV[pair] || 10;
  const riskAmt  = (parseFloat(acct) || 0) * ((parseFloat(risk) || 0) / 100);
  const slPips   = parseFloat(sl) || 1;
  const lots     = riskAmt / (slPips * pv);
  const validLots = isFinite(lots) && lots > 0 ? lots : 0;

  return (
    <div className="lp-tool-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:32, alignItems:'start' }}>
      <div className="u-flex-col u-gap-16">
        <CalcInput label="Account Balance" value={acct} onChange={setAcct} prefix="$" />
        <CalcInput label="Risk %" value={risk} onChange={setRisk} suffix="%" />
        <CalcInput label="Stop Loss" value={sl} onChange={setSl} suffix="pips" />
        <PairSelect value={pair} onChange={setPair} />
      </div>
      <div className="u-flex-col u-gap-12">
        <div style={{ background:'linear-gradient(135deg,rgba(212,168,67,0.12),rgba(212,168,67,0.04))', border:'1px solid rgba(212,168,67,0.35)', borderRadius:14, padding:'24px 20px', textAlign:'center' }}>
          <div style={{ fontSize:10, color:'rgba(212,168,67,0.6)', letterSpacing:'0.14em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace", marginBottom:8 }}>Position Size</div>
          <div style={{ fontSize:52, fontWeight:800, color:'var(--gold)', fontFamily:"'DM Mono',monospace", letterSpacing:'-0.04em', lineHeight:1 }}>{validLots.toFixed(2)}</div>
          <div style={{ fontSize:12, color:'rgba(212,168,67,0.5)', fontFamily:"'DM Mono',monospace", marginTop:6 }}>lots</div>
        </div>
        <StatRow label="Risk Amount"  val={`$${riskAmt.toFixed(2)}`} />
        <StatRow label="Pip Value"    val={`$${pv.toFixed(2)}/pip`} />
        <StatRow label="Mini Lots"    val={(validLots * 10).toFixed(1)} />
        <StatRow label="Micro Lots"   val={(validLots * 100).toFixed(0)} />
        <div style={{ padding:'10px 14px', background:'rgba(46,204,138,0.06)', borderRadius:8, border:'1px solid rgba(46,204,138,0.15)', fontSize:11, color:'rgba(46,204,138,0.7)', fontFamily:"'DM Mono',monospace", textAlign:'center', lineHeight:1.6 }}>
          Max loss capped at ${riskAmt.toFixed(2)} · {risk || '0'}% of account
        </div>
      </div>
    </div>
  );
}

function ToolLotSize() {
  const [balance, setBalance] = useState('10000');
  const [riskPct, setRiskPct] = useState('1');
  const [pair,    setPair]    = useState('EURUSD');
  // Two modes: 'pips' (direct pip input) or 'price' (entry/stop prices)
  const [mode,    setMode]    = useState('pips');
  const [slPips,  setSlPips]  = useState('10');
  const [entryP,  setEntryP]  = useState('1.0850');
  const [stopP,   setStopP]   = useState('1.0820');

  const pv         = PAIR_PV[pair] || 10;
  const riskDollar = (parseFloat(balance) || 0) * ((parseFloat(riskPct) || 0) / 100);

  // Pip distance: mode-driven, never mixed
  const manualPips = parseFloat(slPips) || 0;
  const pipDiff    = Math.abs((parseFloat(entryP) || 0) - (parseFloat(stopP) || 0));
  const isJPY      = pair.includes('JPY');
  const isXAU      = pair === 'XAUUSD';
  const isXAG      = pair === 'XAGUSD';
  const pricePips  = isJPY ? pipDiff * 100 : (isXAU || isXAG) ? pipDiff * 10 : pipDiff * 10000;
  const usePips    = mode === 'pips' ? manualPips : pricePips;

  const lotSize  = usePips > 0 && pv > 0 ? riskDollar / (usePips * pv) : 0;
  const validLot = isFinite(lotSize) && lotSize > 0 ? lotSize : 0;
  const units    = Math.round(validLot * 100000);

  const tabStyle = active => ({
    flex:1, padding:'8px 0', fontSize:11, fontWeight:700, cursor:'pointer',
    fontFamily:"'DM Mono',monospace", letterSpacing:'0.08em', border:'none',
    borderRadius:6, transition:'all 0.18s',
    background: active ? 'rgba(212,168,67,0.18)' : 'transparent',
    color:      active ? '#D4A843' : 'var(--text-35)',
  });

  return (
    <div className="lp-tool-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:32, alignItems:'start' }}>
      <div className="u-flex-col u-gap-14">
        <CalcInput label="Account Balance" value={balance} onChange={setBalance} prefix="$" />
        <CalcInput label="Risk %"          value={riskPct} onChange={setRiskPct} suffix="%" />
        <PairSelect value={pair} onChange={setPair} />

        {/* Mode toggle */}
        <div className="u-flex-col u-gap-6">
          <label className="u-text-label">Stop Loss Method</label>
          <div style={{ display:'flex', gap:4, padding:4, background:'var(--overlay-4)', border:'1px solid var(--overlay-8)', borderRadius:8 }}>
            <button onClick={()=>setMode('pips')}  style={tabStyle(mode==='pips')}>In Pips</button>
            <button onClick={()=>setMode('price')} style={tabStyle(mode==='price')}>By Price</button>
          </div>
        </div>

        {mode === 'pips'
          ? <CalcInput label="Stop Loss" value={slPips} onChange={setSlPips} suffix="pips" />
          : <>
              <CalcInput label="Entry Price" value={entryP} onChange={setEntryP} />
              <CalcInput label="Stop Price"  value={stopP}  onChange={setStopP} />
              {pricePips > 0 && (
                <div style={{ fontSize:11, color:'rgba(212,168,67,0.6)', fontFamily:"'DM Mono',monospace", padding:'8px 12px', background:'rgba(212,168,67,0.06)', borderRadius:6, border:'1px solid rgba(212,168,67,0.12)' }}>
                  ≈ {pricePips.toFixed(1)} pips from price difference
                </div>
              )}
            </>
        }
      </div>

      <div className="u-flex-col u-gap-12">
        <div style={{ background:'linear-gradient(135deg,rgba(46,204,138,0.1),rgba(46,204,138,0.03))', border:'1px solid rgba(46,204,138,0.25)', borderRadius:14, padding:'22px 18px', textAlign:'center' }}>
          <div style={{ fontSize:10, color:'rgba(46,204,138,0.6)', letterSpacing:'0.14em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace", marginBottom:6 }}>Lot Size</div>
          <div className="u-stat-num">{validLot.toFixed(2)}</div>
          <div style={{ fontSize:11, color:'rgba(46,204,138,0.45)', fontFamily:"'DM Mono',monospace", marginTop:4 }}>standard lots</div>
        </div>
        <StatRow label="Units"        val={units.toLocaleString()} />
        <StatRow label="Risk Amount"  val={`$${riskDollar.toFixed(2)}`} />
        <StatRow label="Pip Distance" val={usePips > 0 ? `${usePips.toFixed(1)} pips` : '—'} />
        <StatRow label="Pip Value"    val={`$${pv.toFixed(2)}/pip`} />
        <StatRow label="Mini Lots"    val={(validLot * 10).toFixed(1)} />
        <div style={{ padding:'10px 14px', background:'rgba(46,204,138,0.06)', borderRadius:8, border:'1px solid rgba(46,204,138,0.15)', fontSize:11, color:'rgba(46,204,138,0.7)', fontFamily:"'DM Mono',monospace", textAlign:'center', lineHeight:1.6 }}>
          Max loss capped at ${riskDollar.toFixed(2)} · {riskPct || '0'}% of account
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   TOOLS SECTION
───────────────────────────────────────────── */
// Drawdown field — defined at module level to avoid re-render/blocking bug
function DField({ label, value, onChange, prefix, suffix, tip }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
      <div className="u-flex-ai u-gap-5">
        <label style={{ fontSize:10, fontWeight:700, color:'rgba(212,168,67,0.6)', letterSpacing:'0.12em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace" }}>{label}</label>
        {tip && <span title={tip} style={{ width:14, height:14, borderRadius:'50%', background:'var(--overlay-8)', border:'1px solid var(--overlay-12)', fontSize:8, color:'var(--text-45)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'help', fontFamily:"'DM Mono',monospace" }}>?</span>}
      </div>
      <div style={{ display:'flex', alignItems:'center', background:'var(--overlay-4)', border:'1px solid var(--overlay-10)', borderRadius:8, overflow:'hidden' }}>
        {prefix && <span style={{ padding:'0 10px', fontSize:11, color:'var(--text-30)', fontFamily:"'DM Mono',monospace", borderRight:'1px solid rgba(255,255,255,0.07)' }}>{prefix}</span>}
        <input type="text" inputMode="decimal" value={value}
          onChange={e => { const v=e.target.value; if(/^-?\d*\.?\d*$/.test(v)||v===''||v==='-') onChange(v); }}
          style={{ flex:1, background:'transparent', border:'none', outline:'none', padding:'10px 12px', fontSize:13, fontWeight:600, color:'var(--text)', fontFamily:"'DM Mono',monospace", minWidth:0 }}
        />
        {suffix && <span style={{ padding:'0 10px', fontSize:11, color:'var(--text-30)', fontFamily:"'DM Mono',monospace", borderLeft:'1px solid rgba(255,255,255,0.07)' }}>{suffix}</span>}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   DRAWDOWN RECOVERY CALCULATOR
───────────────────────────────────────────── */
function DrawdownSection() {
  const [dd,        setDd]        = useState('20');
  const [winRate,   setWinRate]   = useState('55');
  const [tradesM,   setTradesM]   = useState('20');
  const [avgWin,    setAvgWin]    = useState('500');
  const [avgLoss,   setAvgLoss]   = useState('300');
  const [peakAcct,  setPeakAcct]  = useState('25000');
  const [dailyDD,   setDailyDD]   = useState('5');
  const [overallDD, setOverallDD] = useState('10');
  const [result,    setResult]    = useState(null);
  const simTimer  = useRef(null);
  const PRESETS = [5, 10, 20, 30, 50];

  useEffect(() => {
    clearTimeout(simTimer.current);
    simTimer.current = setTimeout(() => {
      const ddPct    = parseFloat(dd)       / 100 || 0.2;
      const wr       = parseFloat(winRate)  / 100 || 0.55;
      const tpm      = parseInt(tradesM)          || 20;
      const aw       = parseFloat(avgWin)         || 500;
      const al       = parseFloat(avgLoss)        || 300;
      const peak     = parseFloat(peakAcct)       || 25000;
      const dLim     = parseFloat(dailyDD)  / 100 || 0.05;
      const oLim     = parseFloat(overallDD)/ 100 || 0.10;
      if (ddPct <= 0 || wr <= 0 || wr >= 1 || tpm <= 0 || aw <= 0 || al <= 0 || peak <= 0) return;
      const lossAmt  = peak * ddPct;
      const start    = peak - lossAmt;
      const target   = peak;
      const gainNeeded = (peak / start - 1) * 100;
      const ev       = wr * aw - (1 - wr) * al;
      const overLim  = peak * oLim;
      const dailyLim = peak * dLim;
      const tradesToDaily   = Math.max(0, Math.floor((start - (peak - dailyLim)) / al));
      const atOverallLimit  = lossAmt >= overLim;
      const PATHS    = 500;
      const MAX_TRADES = tpm * 24;
      let recovered  = 0;
      const recTrades  = [];
      for (let p = 0; p < PATHS; p++) {
        let bal = start, rec = false;
        for (let t = 0; t < MAX_TRADES; t++) {
          const win = Math.random() < wr;
          bal += win ? aw : -al;
          if (bal <= 0) break;
          if (!rec && bal >= target) { rec = true; recovered++; recTrades.push(t + 1); break; }
        }
      }
      const recPct = (recovered / PATHS) * 100;
      recTrades.sort((a, b) => a - b);
      const medTrades = recTrades.length > 0 ? recTrades[Math.floor(recTrades.length / 2)] : null;
      const medMonths = medTrades ? (medTrades / tpm).toFixed(1) : null;
      const blowoutRisk = ((PATHS - recovered) / PATHS * 100).toFixed(0);
      setResult({ recPct: recPct.toFixed(1), medMonths, blowoutRisk, ev: ev.toFixed(0), gainNeeded: gainNeeded.toFixed(1), tradesToDaily, atOverallLimit });
    }, 300);
    return () => clearTimeout(simTimer.current);
  }, [dd, winRate, tradesM, avgWin, avgLoss, peakAcct, dailyDD, overallDD]);

  const recColor  = result ? (parseFloat(result.recPct) >= 70 ? '#2ECC8A' : parseFloat(result.recPct) >= 40 ? '#D4A843' : '#E05555') : 'var(--text-20)';
  const evColor   = result ? (parseFloat(result.ev) > 0 ? '#2ECC8A' : '#E05555') : 'var(--text-30)';

  return (
    <section id="drawdown" style={{ padding:'100px 24px', borderTop:'1px solid var(--overlay-6)', borderBottom:'1px solid var(--overlay-6)' }}>
      <div style={{ maxWidth:1100, margin:'0 auto' }}>
        <Reveal>
          <div style={{ marginBottom:52 }}>
            <div className="lp-label">Drawdown recovery calculator</div>
            <h2 className="lp-h2">How long will it take to recover?</h2>
            <p style={{ fontSize:15, color:'var(--text-45)', marginTop:14, lineHeight:1.75, maxWidth:580 }}>
              500 simulated trading paths show realistic recovery timelines, blowout risk, and the math behind why deep drawdowns are so hard to escape.
            </p>
          </div>
        </Reveal>

        <div className="lp-tool-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24, alignItems:'start' }}>
          {/* LEFT — Inputs */}
          <div className="u-card-surface">
            <div style={{ padding:'16px 24px', borderBottom:'1px solid var(--overlay-6)', background:'var(--overlay-2)', display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ width:8, height:8, borderRadius:'50%', background:'rgba(212,168,67,0.7)', boxShadow:'0 0 8px rgba(212,168,67,0.4)' }} />
              <span style={{ fontSize:10, fontWeight:700, color:'rgba(212,168,67,0.7)', letterSpacing:'0.14em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace" }}>Monte Carlo Simulation</span>
            </div>
            <div style={{ padding:'24px' }}>
              <p style={{ fontSize:10, fontWeight:700, color:'var(--text-28)', letterSpacing:'0.12em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace", marginBottom:16 }}>Your Trading Stats</p>

              {/* Drawdown % + presets */}
              <div style={{ marginBottom:16 }}>
                <DField label="Current Drawdown (%)" value={dd} onChange={setDd} suffix="%" tip="How far below your peak account value you currently are" />
                <div style={{ display:'flex', gap:6, marginTop:8, flexWrap:'wrap' }}>
                  {PRESETS.map(p => (
                    <button key={p} onClick={()=>setDd(String(p))} style={{
                      padding:'4px 12px', borderRadius:100, fontSize:10, fontWeight:700,
                      fontFamily:"'DM Mono',monospace", cursor:'pointer', border:'1px solid',
                      background: dd===String(p) ? 'rgba(212,168,67,0.15)' : 'var(--overlay-4)',
                      borderColor: dd===String(p) ? 'rgba(212,168,67,0.5)' : 'var(--overlay-10)',
                      color: dd===String(p) ? '#D4A843' : 'var(--text-38)',
                      transition:'all 0.18s',
                    }}>{p}%</button>
                  ))}
                </div>
              </div>

              <div className="lp-dd-sub-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
                <DField label="Win Rate (%)"      value={winRate}  onChange={setWinRate}  suffix="%" tip="Percentage of trades that close in profit" />
                <DField label="Trades / Month"    value={tradesM}  onChange={setTradesM}  tip="How many trades you take per month" />
                <DField label="Avg Win ($)"       value={avgWin}   onChange={setAvgWin}   prefix="$" tip="Average dollar amount you win per winning trade" />
                <DField label="Avg Loss ($)"      value={avgLoss}  onChange={setAvgLoss}  prefix="$" tip="Average dollar amount you lose per losing trade" />
              </div>
              <div style={{ marginBottom:16 }}>
                <DField label="Peak Account Size ($)" value={peakAcct} onChange={setPeakAcct} prefix="$" tip="Your account balance before the drawdown started" />
              </div>

              <p style={{ fontSize:10, fontWeight:700, color:'var(--text-28)', letterSpacing:'0.12em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace", marginBottom:12 }}>Prop Firm Limits (Optional)</p>
              <div className="lp-dd-sub-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:20 }}>
                <DField label="Daily DD Limit (%)"   value={dailyDD}   onChange={setDailyDD}   suffix="%" tip="Maximum daily drawdown allowed by your prop firm" />
                <DField label="Overall DD Limit (%)" value={overallDD} onChange={setOverallDD} suffix="%" tip="Maximum total drawdown allowed before account is failed" />
              </div>

              <div style={{ display:'flex', alignItems:'center', gap:7, padding:'9px 13px', background:'rgba(46,204,138,0.05)', border:'1px solid rgba(46,204,138,0.12)', borderRadius:8 }}>
                <span style={{ width:7, height:7, borderRadius:'50%', background:'#2ECC8A', display:'inline-block', animation:'kfx-pulse 2s infinite', flexShrink:0 }} />
                <span style={{ fontSize:11, color:'rgba(46,204,138,0.7)', fontFamily:"'DM Mono',monospace", letterSpacing:'0.06em' }}>Auto-calculating · updates as you type</span>
              </div>
            </div>
          </div>

          {/* RIGHT — Results */}
          <div className="u-flex-col u-gap-16">
            {/* Recharts recovery chart */}
            <div className="u-card-surface">
              <div style={{ padding:'14px 20px', borderBottom:'1px solid var(--overlay-6)', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
                <span style={{ fontSize:10, fontWeight:700, color:'var(--text-30)', letterSpacing:'0.12em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace" }}>Recovery Projection</span>
                <div style={{ display:'flex', gap:12, alignItems:'center' }}>
                  {[['#2ECC8A','Recovered'],['#E05555','Failed']].map(([c,l])=>(
                    <div key={l} className="u-flex-ai u-gap-5">
                      <div style={{ width:12, height:3, background:c, borderRadius:2, opacity:0.8 }} />
                      <span style={{ fontSize:9, color:'var(--text-45)', fontFamily:"'DM Mono',monospace" }}>{l}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ padding:'16px 12px 8px' }}>
                {result ? (
                  <DrawdownChart result={result} peak={parseFloat(peakAcct)||25000} dd={parseFloat(dd)||20} />
                ) : (
                  <div style={{ height:180, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:8 }}>
                    <span style={{ fontSize:24, opacity:0.2 }}>📊</span>
                    <span style={{ fontSize:11, color:'var(--text-20)', fontFamily:"'DM Mono',monospace" }}>Calculating…</span>
                  </div>
                )}
              </div>
            </div>

            {/* Stats panel */}
            <div className="u-card-surface">
              <div style={{ padding:'14px 20px', borderBottom:'1px solid var(--overlay-6)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:10, fontWeight:700, color:'var(--text-30)', letterSpacing:'0.12em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace" }}>Recovery Probability</span>
                <span style={{ fontSize:28, fontWeight:800, color:recColor, fontFamily:"'DM Mono',monospace" }}>{result ? `${result.recPct}%` : '—'}</span>
              </div>
              <div style={{ padding:'16px 20px', display:'flex', flexDirection:'column', gap:10 }}>
                {[
                  { label:'Median Recovery Time',   val: result?.medMonths ? `${result.medMonths} months` : '—',    color: result?.medMonths ? 'var(--text)' : '#E05555' },
                  { label:'Blowout Risk',            val: result ? `${result.blowoutRisk}%` : '—',                  color: result && parseInt(result.blowoutRisk) > 50 ? '#E05555' : result ? '#D4A843' : 'var(--text-30)' },
                  { label:'EV Per Trade',            val: result ? `${parseFloat(result.ev)>=0?'+':''}$${result.ev}` : '—', color: evColor },
                  { label:'Gain Needed to Recover',  val: result ? `+${result.gainNeeded}%` : '—',                 color:'var(--text-70)' },
                  { label:'Losses to Daily Limit',   val: result ? (result.tradesToDaily <= 0 ? 'At limit' : `${result.tradesToDaily} trades`) : '—', color: result && result.tradesToDaily <= 0 ? '#E05555' : '#D4A843' },
                  { label:'At Overall Limit',        val: result ? (result.atOverallLimit ? '⚠ At limit' : 'Within limit') : '—', color: result?.atOverallLimit ? '#E05555' : '#2ECC8A' },
                ].map(r => (
                  <div key={r.label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'9px 12px', background:'var(--overlay-2)', borderRadius:8, border:'1px solid var(--overlay-5)' }}>
                    <span style={{ fontSize:11, color:'var(--text-45)', fontFamily:"'DM Mono',monospace" }}>{r.label}</span>
                    <span style={{ fontSize:13, fontWeight:700, color:r.color, fontFamily:"'DM Mono',monospace" }}>{r.val}</span>
                  </div>
                ))}
              </div>

              {result && (
                <div style={{ margin:'0 20px 20px', padding:'12px 16px', background: parseFloat(result.recPct) >= 50 ? 'rgba(46,204,138,0.06)' : 'rgba(224,85,85,0.06)', borderRadius:10, border:`1px solid ${parseFloat(result.recPct) >= 50 ? 'rgba(46,204,138,0.18)' : 'rgba(224,85,85,0.18)'}`, fontSize:12, color:'var(--text-55)', lineHeight:1.65 }}>
                  A <strong style={{ color:'var(--text)' }}>{dd}% drawdown</strong> needs a <strong style={{ color:'var(--gold)' }}>+{result.gainNeeded}% gain</strong> to recover.{' '}
                  {parseFloat(result.ev) > 0
                    ? `With EV of $${result.ev}/trade, variance — not your edge — is the main risk.`
                    : `With negative EV of $${result.ev}/trade, recovery is statistically unlikely without changing your strategy.`}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ToolsSection({ activeTool, setActiveTool }) {
  const panels = {
    'Position Sizing':      { component:<ToolPositionSizing />, icon:'⚖️', desc:'Calculate your exact position size based on account risk.' },
    'Lot Size Calculator':  { component:<ToolLotSize />,        icon:'🧮', desc:'Precise lot sizing from entry, stop-loss price, and risk.' },
    'Drawdown Recovery':    { component:<DrawdownContent />,    icon:'📉', desc:'Monte Carlo simulation of your drawdown recovery odds.' },
    'Risk:Reward Calc':     { component:<ToolRiskReward />,     icon:'🎯', desc:'Instantly validate trade quality with RR ratio and payout.' },
    'Profit Target Planner':{ component:<ToolProfitTarget />,   icon:'🏆', desc:'Reverse-engineer what RR and win rate your monthly goal needs.' },
    'Prop Firm Tracker':    { component:<ToolPropFirm />,       icon:'🏛️', desc:'Track challenge progress, buffers, and per-day targets.' },
    'Session Clock':        { component:<ToolSessionClock />,   icon:'🌐', desc:'ICT killzone clock — Forex & Indices windows in EST + EAT.' },
  };
  const active = panels[activeTool] || panels['Position Sizing'];

  return (
    <section id="tools" style={{ padding:'100px 24px', background:'rgba(255,255,255,0.012)', borderTop:'1px solid var(--overlay-6)', borderBottom:'1px solid var(--overlay-6)' }}>
      <div style={{ maxWidth:1100, margin:'0 auto' }}>
        <Reveal>
          <div style={{ marginBottom:52 }}>
            <div className="lp-label">Trading tools</div>
            <h2 className="lp-h2">Built-in tools for every trade</h2>
            <p style={{ fontSize:15, color:'var(--text-45)', marginTop:14, lineHeight:1.75, maxWidth:500 }}>
              No more switching tabs. Every tool you need is right inside KFX Journal.
            </p>
          </div>
        </Reveal>

        {/* Tool tabs */}
        <div className="lp-tools-tabs">
          {TOOLS_ITEMS.map(item => (
            <button
              key={item.label}
              onClick={() => setActiveTool(item.label)}
              style={{
                display:'flex', alignItems:'center', gap:8, padding:'10px 16px',
                borderRadius:10, cursor:'pointer', transition:'all 0.22s',
                border:'1px solid',
                borderColor: activeTool===item.label ? 'rgba(212,168,67,0.45)' : 'var(--overlay-10)',
                background:  activeTool===item.label ? 'rgba(212,168,67,0.1)'  : 'var(--overlay-3)',
                whiteSpace:'nowrap',
              }}
            >
              <span style={{ fontSize:16 }}>{item.icon}</span>
              <div style={{ textAlign:'left' }}>
                <div className="u-flex-ai u-gap-7">
                  <span style={{ fontSize:12, fontWeight:700, color: activeTool===item.label ? 'var(--gold)' : 'var(--text)', fontFamily:"'Bricolage Grotesque',sans-serif", letterSpacing:'0.01em' }}>{item.label}</span>
                  {item.badge && <span style={{ fontSize:8, padding:'2px 5px', borderRadius:3, background:'rgba(46,204,138,0.12)', color:'#2ECC8A', fontFamily:"'DM Mono',monospace", border:'1px solid rgba(46,204,138,0.2)' }}>{item.badge}</span>}
                </div>
                <div style={{ fontSize:10, color:'var(--text-35)', fontFamily:"'DM Mono',monospace", marginTop:1 }}>{item.sub}</div>
              </div>
            </button>
          ))}
        </div>

        {/* Panel */}
        <Reveal key={activeTool}>
          <div style={{ background:'var(--surface4)', border:'1px solid rgba(212,168,67,0.15)', borderRadius:18, overflow:'hidden', boxShadow:'0 32px 80px rgba(0,0,0,0.5)' }}>
            {/* Panel header */}
            <div style={{ padding:'18px 28px', borderBottom:'1px solid var(--overlay-6)', display:'flex', alignItems:'center', gap:12, background:'var(--overlay-2)' }}>
              <div style={{ width:36, height:36, borderRadius:10, background:'linear-gradient(135deg,rgba(212,168,67,0.2),rgba(212,168,67,0.06))', border:'1px solid rgba(212,168,67,0.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16 }}>{active.icon}</div>
              <div>
                <div style={{ fontSize:14, fontWeight:700, color:'var(--text)' }}>{activeTool}</div>
                <div style={{ fontSize:11, color:'var(--text-38)', fontFamily:"'DM Mono',monospace", marginTop:1 }}>{active.desc}</div>
              </div>
              <div style={{ marginLeft:'auto', display:'flex', gap:5 }}>
                {['#FF5F57','#FEBC2E','#28C840'].map(c=><div key={c} style={{ width:9, height:9, borderRadius:'50%', background:c }}/>)}
              </div>
            </div>
            {/* Panel content */}
            <div className="lp-tool-panel-inner" style={{ padding:'32px 28px' }}>
              {active.component}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}



/* ─────────────────────────────────────────────
   TOOL: RISK:REWARD CALCULATOR
─────────────────────────────────────────────── */
function ToolRiskReward() {
  const [entry,  setEntry]  = useState('1.0850');
  const [stop,   setStop]   = useState('1.0820');
  const [target, setTarget] = useState('1.0910');
  const [acct,   setAcct]   = useState('10000');
  const [risk,   setRisk]   = useState('1');
  const [pair,   setPair]   = useState('EURUSD');

  const isJPY  = pair.includes('JPY');
  const isXAU  = pair === 'XAUUSD';
  const isXAG  = pair === 'XAGUSD';
  const mult   = isJPY ? 100 : (isXAU || isXAG) ? 10 : 10000;
  const pv     = PAIR_PV[pair] || 10;

  const e = parseFloat(entry)  || 0;
  const s = parseFloat(stop)   || 0;
  const t = parseFloat(target) || 0;

  const slPips  = Math.abs(e - s) * mult;
  const tpPips  = Math.abs(t - e) * mult;
  const rr      = slPips > 0 ? tpPips / slPips : 0;
  const riskAmt = (parseFloat(acct) || 0) * ((parseFloat(risk) || 0) / 100);
  const lots    = slPips > 0 && pv > 0 ? riskAmt / (slPips * pv) : 0;
  const reward  = lots * tpPips * pv;
  const validLots = isFinite(lots) && lots > 0 ? lots : 0;

  const rrColor = rr >= 2 ? '#2ECC8A' : rr >= 1 ? '#D4A843' : '#E05555';
  const rrLabel = rr >= 3 ? 'Excellent' : rr >= 2 ? 'Good' : rr >= 1 ? 'Acceptable' : 'Poor';

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:32, alignItems:'start' }} className="lp-tool-grid">
      <div className="u-flex-col u-gap-14">
        <PairSelect value={pair} onChange={setPair} />
        <CalcInput label="Entry Price"  value={entry}  onChange={setEntry} />
        <CalcInput label="Stop Loss"    value={stop}   onChange={setStop} />
        <CalcInput label="Take Profit"  value={target} onChange={setTarget} />
        <CalcInput label="Account"      value={acct}   onChange={setAcct}  prefix="$" />
        <CalcInput label="Risk %"       value={risk}   onChange={setRisk}  suffix="%" />
      </div>
      <div className="u-flex-col u-gap-12">
        {/* RR ratio hero */}
        <div style={{ background:`linear-gradient(135deg,${rrColor}18,${rrColor}06)`, border:`1px solid ${rrColor}44`, borderRadius:14, padding:'22px 18px', textAlign:'center' }}>
          <div style={{ fontSize:10, color:`${rrColor}99`, letterSpacing:'0.14em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace", marginBottom:6 }}>Risk : Reward</div>
          <div style={{ fontSize:52, fontWeight:800, color:rrColor, fontFamily:"'DM Mono',monospace", letterSpacing:'-0.04em', lineHeight:1 }}>
            1:{rr > 0 ? rr.toFixed(2) : '—'}
          </div>
          <div style={{ fontSize:11, color:`${rrColor}66`, fontFamily:"'DM Mono',monospace", marginTop:6 }}>{rr > 0 ? rrLabel : 'Enter prices'}</div>
        </div>
        <StatRow label="SL Distance"   val={slPips > 0 ? `${slPips.toFixed(1)} pips` : '—'} />
        <StatRow label="TP Distance"   val={tpPips > 0 ? `${tpPips.toFixed(1)} pips` : '—'} />
        <StatRow label="Lot Size"      val={validLots > 0 ? validLots.toFixed(2) : '—'} />
        <StatRow label="Risk Amount"   val={`$${riskAmt.toFixed(2)}`} />
        <StatRow label="Potential Reward" val={reward > 0 ? `$${reward.toFixed(2)}` : '—'} accent={reward > 0 ? '#2ECC8A' : undefined} />
        <div style={{ padding:'10px 14px', background:'rgba(212,168,67,0.06)', borderRadius:8, border:'1px solid rgba(212,168,67,0.15)', fontSize:11, color:'rgba(212,168,67,0.7)', fontFamily:"'DM Mono',monospace", textAlign:'center', lineHeight:1.6 }}>
          {rr >= 2 ? `✓ Trade qualifies — risk $${riskAmt.toFixed(2)} to win $${reward.toFixed(2)}` : rr > 0 ? `⚠ RR below 2:1 — consider widening TP` : 'Enter entry, stop and target'}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   TOOL: PROFIT TARGET CALCULATOR
─────────────────────────────────────────────── */
function ToolProfitTarget() {
  const [acct,    setAcct]    = useState('10000');
  const [monthly, setMonthly] = useState('10');
  const [perTrade,setPerTrade]= useState('1');
  const [trades,  setTrades]  = useState('20');
  const [winRate, setWinRate] = useState('55');

  const bal      = parseFloat(acct)     || 0;
  const mPct     = parseFloat(monthly)  || 0;
  const riskPct  = parseFloat(perTrade) || 0;
  const tpm      = parseFloat(trades)   || 0;
  const wr       = parseFloat(winRate)  / 100 || 0;

  const mTarget  = bal * (mPct / 100);
  const winsNeeded = tpm * wr;
  const perWin   = winsNeeded > 0 ? mTarget / winsNeeded : 0;
  const rr       = riskPct > 0 ? (perWin / (bal * riskPct / 100)) : 0;
  const daily    = mTarget / 22;

  const rrColor  = rr >= 2 ? '#2ECC8A' : rr >= 1 ? '#D4A843' : '#E05555';

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:32, alignItems:'start' }} className="lp-tool-grid">
      <div className="u-flex-col u-gap-14">
        <CalcInput label="Account Balance"    value={acct}     onChange={setAcct}     prefix="$" />
        <CalcInput label="Monthly Target %"   value={monthly}  onChange={setMonthly}  suffix="%" />
        <CalcInput label="Risk Per Trade %"   value={perTrade} onChange={setPerTrade} suffix="%" />
        <CalcInput label="Trades Per Month"   value={trades}   onChange={setTrades} />
        <CalcInput label="Win Rate %"         value={winRate}  onChange={setWinRate}  suffix="%" />
      </div>
      <div className="u-flex-col u-gap-12">
        <div style={{ background:'linear-gradient(135deg,rgba(46,204,138,0.1),rgba(46,204,138,0.03))', border:'1px solid rgba(46,204,138,0.25)', borderRadius:14, padding:'22px 18px', textAlign:'center' }}>
          <div style={{ fontSize:10, color:'rgba(46,204,138,0.6)', letterSpacing:'0.14em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace", marginBottom:6 }}>Monthly Target</div>
          <div className="u-stat-num">${mTarget.toFixed(0)}</div>
          <div style={{ fontSize:11, color:'rgba(46,204,138,0.45)', fontFamily:"'DM Mono',monospace", marginTop:4 }}>{mPct}% of ${bal.toLocaleString()}</div>
        </div>
        <StatRow label="Daily Target (22 days)"  val={`$${daily.toFixed(2)}`} />
        <StatRow label="Wins Needed / Month"     val={Math.ceil(winsNeeded).toString()} />
        <StatRow label="Required Profit/Win"     val={`$${perWin.toFixed(2)}`} />
        <StatRow label="Required RR Ratio"       val={rr > 0 ? `1:${rr.toFixed(2)}` : '—'} accent={rrColor} />
        <div style={{ padding:'10px 14px', background:'rgba(46,204,138,0.06)', borderRadius:8, border:'1px solid rgba(46,204,138,0.15)', fontSize:11, color:'rgba(46,204,138,0.7)', fontFamily:"'DM Mono',monospace", textAlign:'center', lineHeight:1.6 }}>
          Win {Math.ceil(winsNeeded)} of {tpm} trades at {rr > 0 ? `1:${rr.toFixed(1)} RR` : 'calculated RR'} to hit ${mTarget.toFixed(0)}/mo
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   TOOL: PROP FIRM CHALLENGE TRACKER
─────────────────────────────────────────────── */
function ToolPropFirm() {
  const [accountSize, setAccountSize] = useState('100000');
  const [profitTarget, setProfitTarget] = useState('10');
  const [maxDaily, setMaxDaily]   = useState('5');
  const [maxTotal, setMaxTotal]   = useState('10');
  const [currentPnl, setCurrentPnl] = useState('0');
  const [daysLeft, setDaysLeft]   = useState('30');
  const [tradesLeft, setTradesLeft] = useState('30');

  const acct   = parseFloat(accountSize) || 0;
  const tgtPct = parseFloat(profitTarget) || 0;
  const dLim   = parseFloat(maxDaily)  || 0;
  const oLim   = parseFloat(maxTotal)  || 0;
  const pnl    = parseFloat(currentPnl) || 0;
  const days   = parseInt(daysLeft)  || 1;
  const trades = parseInt(tradesLeft) || 1;

  // ── Core calculations ─────────────────────────────────────────
  const profitNeeded   = acct * (tgtPct / 100);
  const dailyBuffer    = acct * (dLim / 100);
  const totalBuffer    = acct * (oLim / 100);

  // Drawdown = how much below zero the P&L is (always >= 0)
  const drawdown       = pnl < 0 ? Math.abs(pnl) : 0;
  // How much of the total buffer is still safe to lose
  const bufferUsed     = totalBuffer > 0 ? Math.min(drawdown / totalBuffer * 100, 100) : 0;
  const bufferLeft     = Math.max(totalBuffer - drawdown, 0);
  const bufferConsumed = totalBuffer > 0 && drawdown >= totalBuffer; // account failed

  // Profit progress (only meaningful when pnl > 0)
  const profitProgress = profitNeeded > 0 ? Math.min(Math.max(pnl / profitNeeded * 100, 0), 100) : 0;

  // Still needed to hit target — always from current pnl
  const remaining      = profitNeeded - pnl;
  // Per-day and per-trade only matter if buffer isn't blown
  const perDayNeeded   = !bufferConsumed && days   > 0 ? remaining / days   : 0;
  const perTradeNeeded = !bufferConsumed && trades > 0 ? remaining / trades : 0;

  // ── Status flags ──────────────────────────────────────────────
  const passed      = pnl >= profitNeeded;
  const failed      = bufferConsumed;                          // drawdown >= total buffer
  const danger      = !failed && bufferUsed >= 80;             // 80–99% of buffer used
  const warning     = !failed && !danger && bufferUsed >= 50;  // 50–79% used
  const inDrawdown  = pnl < 0 && !failed;

  // ── Colors ────────────────────────────────────────────────────
  const profitBarColor  = passed ? '#2ECC8A' : '#D4A843';
  const drawdownColor   = failed ? '#E05555' : danger ? '#E05555' : warning ? '#F59E0B' : '#E05555';
  const bufferLeftColor = failed ? '#E05555' : danger ? '#E05555' : warning ? '#F59E0B' : 'var(--text-70)';

  // ── Status banner ─────────────────────────────────────────────
  const statusBg     = passed ? 'rgba(46,204,138,0.08)'  : failed ? 'rgba(224,85,85,0.12)'  : danger ? 'rgba(224,85,85,0.08)'  : warning ? 'rgba(245,158,11,0.08)' : 'rgba(212,168,67,0.06)';
  const statusBorder = passed ? 'rgba(46,204,138,0.2)'   : failed ? 'rgba(224,85,85,0.35)'  : danger ? 'rgba(224,85,85,0.2)'  : warning ? 'rgba(245,158,11,0.2)'  : 'rgba(212,168,67,0.15)';
  const statusColor  = passed ? 'rgba(46,204,138,0.9)'   : failed ? '#ff6b6b'               : danger ? 'rgba(224,85,85,0.9)'  : warning ? 'rgba(245,158,11,0.9)'  : 'rgba(212,168,67,0.8)';
  const statusText   = passed
    ? '🎉 Challenge target reached! Request payout.'
    : failed
    ? '💀 Account blown — drawdown limit exceeded. Challenge failed.'
    : danger
    ? `⚠ DANGER — only $${bufferLeft.toFixed(0)} buffer left (${(100-bufferUsed).toFixed(0)}%). Stop trading.`
    : warning
    ? `⚡ Caution — ${bufferUsed.toFixed(0)}% of drawdown buffer used. Reduce size.`
    : inDrawdown
    ? `📉 In drawdown — need $${remaining.toFixed(2)} to pass, $${bufferLeft.toFixed(2)} buffer remaining.`
    : `Need $${remaining.toFixed(2)} more in ${days} day${days !== 1 ? 's' : ''}.`;

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:32, alignItems:'start' }} className="lp-tool-grid">
      <div className="u-flex-col u-gap-14">
        <CalcInput label="Account Size"       value={accountSize}   onChange={setAccountSize}   prefix="$" />
        <CalcInput label="Profit Target %"    value={profitTarget}  onChange={setProfitTarget}  suffix="%" />
        <CalcInput label="Max Daily Loss %"   value={maxDaily}      onChange={setMaxDaily}       suffix="%" />
        <CalcInput label="Max Total Loss %"   value={maxTotal}      onChange={setMaxTotal}       suffix="%" />
        <CalcInput label="Current P&L"        value={currentPnl}    onChange={setCurrentPnl}    prefix="$" />
        <CalcInput label="Days Remaining"     value={daysLeft}      onChange={setDaysLeft} />
        <CalcInput label="Trades Remaining"   value={tradesLeft}    onChange={setTradesLeft} />
      </div>
      <div className="u-flex-col u-gap-12">

        {/* ── Profit progress bar ── */}
        <div style={{ background:'var(--surface4)', border:'1px solid var(--overlay-10)', borderRadius:14, padding:'18px 18px 14px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
            <span style={{ fontSize:10, color:'var(--text-45)', fontFamily:"'DM Mono',monospace", letterSpacing:'0.1em', textTransform:'uppercase' }}>Profit Progress</span>
            <span style={{ fontSize:13, fontWeight:800, color:profitBarColor, fontFamily:"'DM Mono',monospace" }}>{profitProgress.toFixed(1)}%</span>
          </div>
          <div className="u-prog-track">
            <div style={{ height:'100%', width:`${profitProgress}%`, background:`linear-gradient(90deg,${profitBarColor}88,${profitBarColor})`, borderRadius:4, transition:'width 0.5s ease' }} />
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', marginTop:6 }}>
            <span style={{ fontSize:10, color: pnl > 0 ? '#2ECC8A' : 'var(--text-30)', fontFamily:"'DM Mono',monospace" }}>
              {pnl > 0 ? `+$${pnl.toFixed(0)}` : pnl < 0 ? `-$${Math.abs(pnl).toFixed(0)}` : '$0'}
            </span>
            <span style={{ fontSize:10, color:'var(--text-30)', fontFamily:"'DM Mono',monospace" }}>${profitNeeded.toFixed(0)} target</span>
          </div>

          {/* ── Drawdown buffer bar (only shown when in drawdown or limits set) ── */}
          {(totalBuffer > 0) && (
            <div style={{ marginTop:12, paddingTop:12, borderTop:'1px solid var(--overlay-5)' }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                <span style={{ fontSize:10, color:'var(--text-45)', fontFamily:"'DM Mono',monospace", letterSpacing:'0.1em', textTransform:'uppercase' }}>Drawdown Buffer Used</span>
                <span style={{ fontSize:12, fontWeight:700, color: drawdownColor, fontFamily:"'DM Mono',monospace" }}>{bufferUsed.toFixed(1)}%</span>
              </div>
              <div className="u-prog-track">
                <div style={{ height:'100%', width:`${bufferUsed}%`, background:`linear-gradient(90deg,${drawdownColor}88,${drawdownColor})`, borderRadius:4, transition:'width 0.5s ease' }} />
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', marginTop:6 }}>
                <span style={{ fontSize:10, color: drawdownColor, fontFamily:"'DM Mono',monospace" }}>
                  {drawdown > 0 ? `-$${drawdown.toFixed(0)} used` : '$0 used'}
                </span>
                <span style={{ fontSize:10, color: bufferLeftColor, fontFamily:"'DM Mono',monospace", fontWeight: danger || failed ? 700 : 400 }}>
                  ${bufferLeft.toFixed(0)} left
                </span>
              </div>
            </div>
          )}
        </div>

        {/* ── Stats ── */}
        <StatRow label="Profit Target"        val={`$${profitNeeded.toFixed(2)}`} />
        <StatRow label="Still Needed to Pass" val={remaining > 0 ? `$${remaining.toFixed(2)}` : '✓ PASSED'} accent={remaining <= 0 ? '#2ECC8A' : undefined} />
        <StatRow label="Buffer Remaining"     val={failed ? '✗ BLOWN' : `$${bufferLeft.toFixed(2)}`} accent={bufferLeftColor} />
        <StatRow label="Per Day Needed"       val={failed ? '—' : `$${perDayNeeded.toFixed(2)}`} />
        <StatRow label="Per Trade Needed"     val={failed ? '—' : `$${perTradeNeeded.toFixed(2)}`} />
        <StatRow label="Daily Loss Limit"     val={`$${dailyBuffer.toFixed(2)}`} />
        <StatRow label="Max Total Drawdown"   val={`$${totalBuffer.toFixed(2)}`} />

        {/* ── Status banner ── */}
        <div style={{ padding:'11px 14px', background: statusBg, borderRadius:8, border:`1px solid ${statusBorder}`, fontSize:11, color: statusColor, fontFamily:"'DM Mono',monospace", textAlign:'center', lineHeight:1.7, fontWeight: failed ? 700 : 500 }}>
          {statusText}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   TOOL: TRADING SESSION CLOCK
─────────────────────────────────────────────── */
const KILLZONES_LP = {
  forex: [
    { name:'Asia',         estOpen:20,   estClose:0,    color:'#5B8FE8', icon:'🌏' },
    { name:'London',       estOpen:2,    estClose:5,    color:'#2ECC8A', icon:'🇬🇧' },
    { name:'New York AM',  estOpen:7,    estClose:10,   color:'#E05555', icon:'🗽' },
    { name:'London Close', estOpen:10,   estClose:12,   color:'#E07B54', icon:'🔔' },
  ],
  indices: [
    { name:'Asia',         estOpen:20,   estClose:0,    color:'#5B8FE8', icon:'🌏' },
    { name:'London',       estOpen:2,    estClose:5,    color:'#2ECC8A', icon:'🇬🇧' },
    { name:'New York AM',  estOpen:8.5,  estClose:11,   color:'#E05555', icon:'🗽' },
    { name:'New York PM',  estOpen:13.5, estClose:16,   color:'#B06ACF', icon:'🌆' },
  ],
};

function getEstOffsetLP(now) {
  try {
    const estDate = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    return (estDate - now) / 3600000;
  } catch { return -5; }
}

function ToolConsistencyRule() {
  const [limitPct, setLimitPct] = useState('30');
  const [method, setMethod] = useState('net'); // 'net' or 'gross'
  const [nextId, setNextId] = useState(3);
  const [days, setDays] = useState([
    { id: 1, label: 'Day 1', value: '' },
    { id: 2, label: 'Day 2', value: '' },
  ]);

  const addDay = () => {
    setDays(d => [...d, { id: nextId, label: `Day ${d.length + 1}`, value: '' }]);
    setNextId(n => n + 1);
  };
  const removeDay = (id) => setDays(d => d.length > 1 ? d.filter(x => x.id !== id) : d);
  const updateDay = (id, val) => setDays(d => d.map(x => x.id === id ? { ...x, value: val } : x));

  const limit = parseFloat(limitPct) || 0;
  const values = days.map(d => parseFloat(d.value) || 0);

  // Net = profits minus losses across all days. Gross = only profitable days summed.
  const totalNet   = values.reduce((a, b) => a + b, 0);
  const profitDays = days.map((d, i) => ({ ...d, val: values[i] })).filter(d => d.val > 0);
  const totalGross = profitDays.reduce((a, d) => a + d.val, 0);
  const highest     = profitDays.reduce((max, d) => d.val > max.val ? d : max, { label: '—', val: 0 });

  const denominator = method === 'gross' ? totalGross : totalNet;
  const denomLabel   = method === 'gross' ? 'Total gross profit' : 'Total net profit';

  const hasData = denominator > 0 && highest.val > 0;
  const highestPct = hasData ? (highest.val / denominator) * 100 : 0;
  const failed = hasData && highestPct > limit;

  // How much more profit (spread across other days) is needed for the
  // current highest day to fall back under the limit
  const denomNeededForCompliance = limit > 0 ? highest.val / (limit / 100) : 0;
  const extraNeeded = failed ? Math.max(denomNeededForCompliance - denominator, 0) : 0;

  const barColor  = failed ? '#E05555' : hasData ? '#2ECC8A' : 'var(--text-30)';
  const statusBg     = failed ? 'rgba(224,85,85,0.1)'  : hasData ? 'rgba(46,204,138,0.08)' : 'rgba(212,168,67,0.06)';
  const statusBorder = failed ? 'rgba(224,85,85,0.3)'  : hasData ? 'rgba(46,204,138,0.2)'  : 'rgba(212,168,67,0.15)';
  const statusColor  = failed ? '#ff6b6b'              : hasData ? 'rgba(46,204,138,0.9)'  : 'rgba(212,168,67,0.8)';
  const methodLabel  = method === 'gross' ? 'gross profit' : 'net profit';
  const statusText   = !hasData
    ? 'Add your daily trade results to check consistency.'
    : failed
    ? `⚠ Fails the rule — ${highest.label} is ${highestPct.toFixed(1)}% of total ${methodLabel} (limit ${limit}%).`
    : `✓ Passes — ${highest.label} is ${highestPct.toFixed(1)}% of total ${methodLabel} (limit ${limit}%).`;

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:32, alignItems:'start' }} className="lp-tool-grid">
      <div className="u-flex-col u-gap-14">
        <CalcInput label="Firm Consistency Rule" value={limitPct} onChange={setLimitPct} suffix="%" />

        {/* Compare-against method toggle */}
        <div className="u-flex-col u-gap-6">
          <label className="u-text-label">Compare Highest Day Against</label>
          <div className="u-flex u-gap-6">
            {[
              { key:'net',   label:'Net Profit',   sub:'Profits − losses' },
              { key:'gross', label:'Gross Profit',  sub:'Profitable days only' },
            ].map(m => (
              <button key={m.key} onClick={() => setMethod(m.key)} style={{
                flex:1, padding:'10px 8px', borderRadius:8, cursor:'pointer', textAlign:'left',
                background: method === m.key ? 'rgba(212,168,67,0.12)' : 'var(--overlay-3)',
                border: `1px solid ${method === m.key ? 'rgba(212,168,67,0.28)' : 'var(--overlay-8)'}`,
                transition:'all 0.15s',
              }}>
                <div style={{ fontSize:12, fontWeight:700, color: method === m.key ? '#D4A843' : 'var(--text-55)', fontFamily:"'Bricolage Grotesque',sans-serif" }}>{m.label}</div>
                <div style={{ fontSize:9.5, color: method === m.key ? 'rgba(212,168,67,0.6)' : 'var(--text-30)', fontFamily:"'DM Mono',monospace", marginTop:2 }}>{m.sub}</div>
              </button>
            ))}
          </div>
          <div style={{ fontSize:10.5, color:'var(--text-30)', lineHeight:1.6, fontFamily:"'Bricolage Grotesque',sans-serif" }}>
            Check your firm's actual rulebook — most define "total profit" as net (after losses), but some evaluate gross profit from winning days only.
          </div>
        </div>

        <div className="u-flex-col u-gap-8">
          <label className="u-text-label">Daily Results (profit/loss)</label>
          <div className="u-flex-col u-gap-8">
            {days.map(d => (
              <div key={d.id} style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:10, color:'var(--text-38)', fontFamily:"'DM Mono',monospace", width:44, flexShrink:0 }}>{d.label}</span>
                <div style={{ flex:1, display:'flex', alignItems:'center', background:'var(--overlay-4)', border:'1px solid var(--overlay-10)', borderRadius:8, overflow:'hidden' }}>
                  <span style={{ padding:'0 10px', fontSize:12, color:'var(--text-35)', fontFamily:"'DM Mono',monospace", borderRight:'1px solid rgba(255,255,255,0.07)' }}>$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={d.value}
                    placeholder="0"
                    onChange={e => { const v = e.target.value; if (/^-?\d*\.?\d*$/.test(v) || v === '' || v === '-') updateDay(d.id, v); }}
                    style={{ flex:1, background:'transparent', border:'none', outline:'none', padding:'10px 12px', fontSize:14, fontWeight:600, color:'var(--text)', fontFamily:"'DM Mono',monospace", minWidth:0 }}
                  />
                </div>
                <button onClick={() => removeDay(d.id)} disabled={days.length <= 1} style={{ width:28, height:28, borderRadius:7, background:'var(--overlay-4)', border:'1px solid var(--overlay-10)', color:'var(--text-38)', fontSize:13, cursor: days.length > 1 ? 'pointer' : 'not-allowed', opacity: days.length > 1 ? 1 : 0.4, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
              </div>
            ))}
          </div>
          <button onClick={addDay} style={{ marginTop:2, padding:'9px 14px', background:'rgba(212,168,67,0.06)', border:'1px dashed rgba(212,168,67,0.3)', borderRadius:8, color:'var(--gold)', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:"'Bricolage Grotesque',sans-serif" }}>
            + Add trading day
          </button>
        </div>
      </div>

      <div className="u-flex-col u-gap-12">
        {/* Status banner */}
        <div style={{ padding:'16px 18px', borderRadius:12, background:statusBg, border:`1px solid ${statusBorder}` }}>
          <div style={{ fontSize:13.5, fontWeight:700, color:statusColor, lineHeight:1.5 }}>{statusText}</div>
        </div>

        {/* Highest day share bar */}
        <div style={{ background:'var(--surface4)', border:'1px solid var(--overlay-10)', borderRadius:14, padding:'18px 18px 14px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
            <span style={{ fontSize:10, color:'var(--text-45)', fontFamily:"'DM Mono',monospace", letterSpacing:'0.1em', textTransform:'uppercase' }}>Highest Day Share</span>
            <span style={{ fontSize:13, fontWeight:800, color:barColor, fontFamily:"'DM Mono',monospace" }}>{highestPct.toFixed(1)}%</span>
          </div>
          <div className="u-prog-track">
            <div style={{ height:'100%', width:`${Math.min(highestPct, 100)}%`, background:`linear-gradient(90deg,${barColor}88,${barColor})`, borderRadius:4, transition:'width 0.5s ease' }} />
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', marginTop:6 }}>
            <span style={{ fontSize:10, color:'var(--text-30)', fontFamily:"'DM Mono',monospace" }}>{highest.label}: ${highest.val.toFixed(0)}</span>
            <span style={{ fontSize:10, color:'var(--text-30)', fontFamily:"'DM Mono',monospace" }}>limit {limit}%</span>
          </div>
        </div>

        {/* Summary rows */}
        <div style={{ background:'var(--surface4)', border:'1px solid var(--overlay-10)', borderRadius:14, padding:'16px 18px', display:'flex', flexDirection:'column', gap:10 }}>
          <div style={{ display:'flex', justifyContent:'space-between' }}>
            <span style={{ fontSize:12, color:'var(--text-45)' }}>{denomLabel}</span>
            <span style={{ fontSize:13, fontWeight:700, color: denominator >= 0 ? '#2ECC8A' : '#E05555', fontFamily:"'DM Mono',monospace" }}>${denominator.toFixed(2)}</span>
          </div>
          {method === 'gross' && (
            <div style={{ display:'flex', justifyContent:'space-between' }}>
              <span style={{ fontSize:12, color:'var(--text-45)' }}>Total net profit</span>
              <span style={{ fontSize:13, fontWeight:700, color: totalNet >= 0 ? '#2ECC8A' : '#E05555', fontFamily:"'DM Mono',monospace" }}>${totalNet.toFixed(2)}</span>
            </div>
          )}
          <div style={{ display:'flex', justifyContent:'space-between' }}>
            <span style={{ fontSize:12, color:'var(--text-45)' }}>Highest single day</span>
            <span style={{ fontSize:13, fontWeight:700, color:'var(--text)', fontFamily:"'DM Mono',monospace" }}>${highest.val.toFixed(2)}</span>
          </div>
          {failed && (
            <div style={{ display:'flex', justifyContent:'space-between', paddingTop:10, borderTop:'1px solid var(--overlay-5)' }}>
              <span style={{ fontSize:12, color:'var(--text-45)' }}>Extra {methodLabel} needed elsewhere</span>
              <span style={{ fontSize:13, fontWeight:700, color:'#F59E0B', fontFamily:"'DM Mono',monospace" }}>${extraNeeded.toFixed(2)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ToolSessionClock() {
  const [now, setNow] = useState(new Date());
  const [tab, setTab] = useState('forex');
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const pad  = n => String(Math.floor(n)).padStart(2, '0');
  const fmtH = h => { const hh = ((h % 24) + 24) % 24; const mm = Math.round((hh - Math.floor(hh)) * 60); return `${pad(hh)}:${String(mm).padStart(2,'0')}`; };

  // EST fractional hour (DST-aware)
  const estOffset   = getEstOffsetLP(now);
  const utcFrac     = now.getUTCHours() + now.getUTCMinutes() / 60 + now.getUTCSeconds() / 3600;
  const estFrac     = ((utcFrac + estOffset) % 24 + 24) % 24;

  // Kigali time (EAT = UTC+3)
  const eatOffset   = 3;
  const eatFrac     = ((utcFrac + eatOffset) % 24 + 24) % 24;
  const eatH        = Math.floor(eatFrac);
  const eatMin      = Math.round((eatFrac - eatH) * 60);
  const eatTime     = `${pad(eatH)}:${String(eatMin).padStart(2,'0')}:${String(now.getUTCSeconds()).padStart(2,'0')}`;

  // EST display
  const estH        = Math.floor(estFrac);
  const estMin      = Math.round((estFrac - estH) * 60);
  const estTime     = `${pad(estH)}:${String(estMin).padStart(2,'0')}:${String(now.getUTCSeconds()).padStart(2,'0')}`;

  // EST → EAT conversion for display
  const estToEat = estH => ((estH - estOffset + eatOffset) % 24 + 24) % 24;

  function isKzActive(kz) {
    const o = kz.estOpen, c = kz.estClose;
    if (o < c) return estFrac >= o && estFrac < c;
    return estFrac >= o || estFrac < c;
  }

  function minutesLeft(kz) {
    const o = kz.estOpen, c = kz.estClose;
    let closeEst = c;
    if (o >= c && estFrac >= o) closeEst = c + 24;
    return Math.max(0, (closeEst - estFrac) * 60);
  }

  const zones = KILLZONES_LP[tab].map(kz => {
    const active    = isKzActive(kz);
    const mLeft     = active ? minutesLeft(kz) : null;
    const eatOpen   = estToEat(kz.estOpen);
    const eatClose  = estToEat(kz.estClose);
    return { ...kz, active, mLeft, eatOpen, eatClose };
  });

  const activeZone = zones.find(z => z.active);

  return (
    <div className="u-flex-col u-gap-16">

      {/* Clock row */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }} className="lp-tool-grid">
        <div style={{ background:'rgba(212,168,67,0.07)', border:'1px solid rgba(212,168,67,0.2)', borderRadius:14, padding:'18px', textAlign:'center' }}>
          <div style={{ fontSize:10, color:'rgba(212,168,67,0.55)', fontFamily:"'DM Mono',monospace", letterSpacing:'0.12em', textTransform:'uppercase', marginBottom:6 }}>Kigali (EAT)</div>
          <div style={{ fontSize:32, fontWeight:800, color:'#D4A843', fontFamily:"'DM Mono',monospace", letterSpacing:'-0.02em', lineHeight:1 }}>{eatTime}</div>
          <div style={{ fontSize:10, color:'rgba(212,168,67,0.4)', fontFamily:"'DM Mono',monospace", marginTop:6 }}>{now.toLocaleDateString('en-GB',{weekday:'short',day:'numeric',month:'short'})}</div>
        </div>
        <div style={{ background:'var(--overlay-3)', border:'1px solid var(--overlay-8)', borderRadius:14, padding:'18px', textAlign:'center' }}>
          <div style={{ fontSize:10, color:'var(--text-30)', fontFamily:"'DM Mono',monospace", letterSpacing:'0.12em', textTransform:'uppercase', marginBottom:6 }}>EST / New York</div>
          <div style={{ fontSize:24, fontWeight:700, fontFamily:"'DM Mono',monospace", color:'var(--text-55)', lineHeight:1 }}>{estTime}</div>
          {activeZone ? (
            <div style={{ fontSize:10, marginTop:6, color:activeZone.color, fontFamily:"'DM Mono',monospace", fontWeight:700 }}>
              {activeZone.icon} {activeZone.name} ACTIVE
            </div>
          ) : (
            <div style={{ fontSize:10, color:'var(--text-28)', fontFamily:"'DM Mono',monospace", marginTop:6 }}>No killzone active</div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="u-flex u-gap-6">
        {[{ key:'forex', label:'Forex' }, { key:'indices', label:'Indices' }].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            flex:1, padding:'8px 0', borderRadius:8, border:'none', cursor:'pointer',
            fontSize:11, fontWeight: tab === t.key ? 700 : 500, letterSpacing:'0.06em', textTransform:'uppercase',
            background: tab === t.key ? 'rgba(212,168,67,0.12)' : 'var(--overlay-3)',
            color: tab === t.key ? '#D4A843' : 'var(--text-38)',
            border: `1px solid ${tab === t.key ? 'rgba(212,168,67,0.28)' : 'var(--overlay-8)'}`,
            fontFamily:"'DM Mono',monospace", transition:'all 0.15s',
          }}>
            {t.label} Killzones
          </button>
        ))}
      </div>

      {/* Killzone cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:10 }} className="lp-session-grid">
        {zones.map(z => (
          <div key={z.name} style={{ background: z.active ? `${z.color}10` : 'rgba(255,255,255,0.025)', border:`1px solid ${z.active ? z.color+'44' : 'var(--overlay-7)'}`, borderRadius:12, padding:'14px', position:'relative', overflow:'hidden', transition:'all 0.3s' }}>
            {z.active && <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:`linear-gradient(90deg,transparent,${z.color},transparent)` }} />}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
              <div style={{ fontSize:13, fontWeight:700, color: z.active ? 'var(--text)' : 'var(--text-45)', fontFamily:"'Bricolage Grotesque',sans-serif" }}>
                {z.icon} {z.name}
              </div>
              <div style={{ padding:'2px 8px', borderRadius:100, background: z.active ? `${z.color}22` : 'var(--overlay-5)', border:`1px solid ${z.active ? z.color+'55' : 'var(--overlay-10)'}`, fontSize:8, fontWeight:700, color: z.active ? z.color : 'var(--text-28)', fontFamily:"'DM Mono',monospace", letterSpacing:'0.1em' }}>
                {z.active ? 'ACTIVE' : 'CLOSED'}
              </div>
            </div>
            {/* EST */}
            <div style={{ fontSize:11, fontWeight:700, color: z.active ? 'var(--text-75)' : 'var(--text-35)', fontFamily:"'DM Mono',monospace" }}>
              {fmtH(z.estOpen)}–{fmtH(z.estClose)} EST
            </div>
            {/* EAT */}
            <div style={{ fontSize:10, color:'var(--text-30)', fontFamily:"'DM Mono',monospace", marginTop:2 }}>
              {fmtH(z.eatOpen)}–{fmtH(z.eatClose)} EAT
            </div>
            {z.active && z.mLeft != null && (
              <div style={{ fontSize:11, color:z.color, marginTop:6, opacity:0.85 }}>
                {Math.floor(z.mLeft)}m {Math.round((z.mLeft % 1) * 60)}s left
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Tip */}
      <div style={{ padding:'12px 16px', background:'rgba(212,168,67,0.05)', border:'1px solid rgba(212,168,67,0.15)', borderRadius:10, fontSize:12, color:'var(--text-55)', lineHeight:1.75, fontFamily:"'Bricolage Grotesque',sans-serif" }}>
        💡 <strong style={{ color:'var(--gold)' }}>Kigali tip:</strong> London killzone opens at 09:00 EAT. NY AM opens at 15:00 EAT. These are ICT's highest-probability windows — price delivers its sharpest moves right at session opens.
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   TOOL EXPLANATIONS (per-tool educational content)
───────────────────────────────────────────── */
const TOOL_EXPLANATIONS = {
  'Position Sizing': {
    what: 'Position Sizing calculates exactly how many lots to trade so that a single loss never exceeds your pre-defined risk percentage — no matter which pair or stop-loss size you use.',
    why: 'Over-leveraging is the #1 account killer. Most blown accounts are not the result of a bad strategy — they are the result of risking too much on a single trade. A disciplined 1–2% risk rule per trade means you can survive 10 consecutive losses and still have 80–90% of your account intact.',
    how: [
      { step:'1', title:'Enter your account balance', body:'Use your current live balance, not your peak or target. Be accurate.' },
      { step:'2', title:'Set your risk %', body:'Most professional traders risk 0.5–2% per trade. Start at 1% until consistently profitable.' },
      { step:'3', title:'Enter your stop-loss in pips', body:'This comes from your technical analysis — the distance from entry to where your trade idea is invalidated.' },
      { step:'4', title:'Select your pair', body:'Each pair has a different pip value per lot. EURUSD is $10/pip, USDJPY is ~$9.09/pip, etc. The calculator accounts for this automatically.' },
      { step:'5', title:'Read your lot size', body:'Trade exactly that many lots. Not more because you feel confident. Consistency in sizing is what separates professionals.' },
    ],
    tip: 'Pro tip: If your calculated lot size feels "too small," that is your psychology speaking, not your edge. Trust the math.',
  },
  'Lot Size Calculator': {
    what: 'The Lot Size Calculator is a more advanced position-sizing tool that accepts either a direct pip input or two price levels (entry and stop-loss) and computes the precise lot size for any forex pair or metal.',
    why: 'Different brokers quote prices differently and different pairs move differently. Manually converting price distances to pips and then to lot sizes introduces calculation errors under live market pressure. This tool eliminates that risk completely.',
    how: [
      { step:'1', title:'Enter account balance and risk %', body:'Same as Position Sizing — your actual balance and your chosen risk per trade.' },
      { step:'2', title:'Choose a pair', body:'Select from majors, crosses, metals, or exotics. Pip values are pre-loaded for all 35+ pairs.' },
      { step:'3', title:'Choose your stop-loss method', body:'"In Pips" — type your stop directly. "By Price" — type your entry price and stop price, the calculator converts the difference to pips automatically.' },
      { step:'4', title:'Read the result', body:'You get lot size, units, risk amount, pip distance, and pip value — all in one view.' },
    ],
    tip: 'Use "By Price" mode when trading gold (XAUUSD). Gold moves in dollar terms, not pips — entering 1.9350 and 1.9280 is much faster than manually calculating the 70-pip equivalent.',
  },
  'Risk:Reward Calc': {
    what: 'The Risk:Reward Calculator validates a trade setup by computing the exact ratio between your potential profit (distance to take profit) and your potential loss (distance to stop loss), while simultaneously calculating the correct lot size based on your account risk.',
    why: 'Most losing traders enter trades with no idea what their RR ratio is. A positive win rate means nothing if you\'re taking 1:0.8 trades. Professional traders filter every trade through an RR minimum (typically 1:2) before considering execution. This tool makes that step instant.',
    how: [
      { step:'1', title:'Select your pair', body:'Pip values are pre-loaded automatically.' },
      { step:'2', title:'Enter entry, stop, and target', body:'Type your actual price levels. The calculator converts them to pip distances instantly.' },
      { step:'3', title:'Set your account and risk %', body:'The tool calculates your lot size alongside the RR.' },
      { step:'4', title:'Read the verdict', body:'Green = RR ≥ 2 (take the trade). Amber = acceptable. Red = skip or widen TP.' },
    ],
    tip: 'A 1:2 RR means you only need a 34% win rate to break even. A 1:3 RR only needs 25%. The higher your RR minimum, the less pressure your win rate carries.',
  },
  'Profit Target Planner': {
    what: 'The Profit Target Planner reverses the usual calculation: instead of asking "what will I make?", it asks "what does my strategy need to achieve my income goal?" — giving you the required RR ratio, win count, and per-win profit needed to hit a monthly dollar target.',
    why: 'Setting a $1,000/month target without knowing the required RR and win rate is like driving without a map. Most traders set income goals emotionally. This tool grounds them in math — showing exactly what your edge must produce to reach the target you have in mind.',
    how: [
      { step:'1', title:'Enter your account balance', body:'Use your current live account size.' },
      { step:'2', title:'Set your monthly target %', body:'What % gain do you need on your account? 5%, 10%, 20%?' },
      { step:'3', title:'Enter your risk per trade %', body:'Usually 1–2%.' },
      { step:'4', title:'Input your trade frequency and win rate', body:'How many trades/month and your historical win rate.' },
      { step:'5', title:'Read the required RR', body:'If the required RR is above 3, your target may be too aggressive for your risk and frequency.' },
    ],
    tip: 'If the required RR comes back above 4:1, reduce your target or increase your win rate. Most sustainable trading businesses run at 1.5–2.5 RR with 50–60% win rates, not 10% monthly gains.',
  },
  'Prop Firm Tracker': {
    what: 'The Prop Firm Tracker helps you manage a funded account challenge by showing your profit progress, daily/total drawdown buffers, how much you need to earn per day and per trade, and flags when you are approaching limit thresholds.',
    why: 'Prop firm challenges are failed not by bad strategy, but by poor math management. Traders cross the daily loss limit because they did not track it. They over-trade the final days trying to reach the target. This tool keeps the numbers visible and the risk managed.',
    how: [
      { step:'1', title:'Set your account size and targets', body:'Enter the challenge account size, profit target %, and drawdown limits from your prop firm\'s rules.' },
      { step:'2', title:'Enter your current P&L', body:'Update this daily with your running profit or loss.' },
      { step:'3', title:'Enter days and trades remaining', body:'How many calendar days and planned trades do you have left in the challenge window?' },
      { step:'4', title:'Monitor the dashboard', body:'Per-day and per-trade targets update automatically. Red warnings appear when drawdown buffers fall below 20%.' },
    ],
    tip: 'If your per-trade target climbs above 2% of account, your challenge window is too short for your trade frequency. Slow down, not up.',
    warning: 'Always confirm your firm\'s exact drawdown calculation method — some use trailing drawdown, some use fixed. A trailing drawdown shrinks as your account grows.',
  },
  'Consistency Rule': {
    what: 'The Consistency Rule Calculator checks whether any single trading day accounts for too large a share of your total profit — the exact metric most prop firms use to approve or deny a payout, often called a "consistency score." You choose whether "total profit" is calculated as net (after losses) or gross (winning days only), matching your specific firm\'s rulebook.',
    why: 'Many funded accounts pass the profit target but fail the payout because one huge day carried the whole result. Firms want to see profit built steadily across many days, not from a single lucky session. Catching this before you request payout avoids a rejected or delayed withdrawal — and getting the comparison method right matters, since a day that passes under a gross-profit rule can fail under a net-profit rule.',
    how: [
      { step:'1', title:'Enter your firm\'s consistency limit', body:'This is stated in your firm\'s rules, e.g. "no single day may exceed 30% of total profit."' },
      { step:'2', title:'Pick your comparison method', body:'Net Profit divides your highest day by profits minus losses across all days. Gross Profit divides it by winning days only, ignoring losses. Check your firm\'s rulebook to know which one they actually use.' },
      { step:'3', title:'Add each trading day\'s result', body:'Enter your actual daily P&L — use "+ Add trading day" for as many days as you\'ve traded.' },
      { step:'4', title:'Read the verdict', body:'Green = your highest day is within the limit. Red = it exceeds the limit and would likely fail a consistency check.' },
      { step:'5', title:'Check the extra profit needed', body:'If you\'re failing, this shows how much more profit — spread across your other days — would bring your highest day back under the limit.' },
    ],
    tip: 'Build your remaining profit gradually across several sessions rather than one large trade — this naturally keeps your highest single day below most firms\' 20–40% consistency thresholds, under either method.',
    warning: 'If your firm doesn\'t clearly state which method it uses, run both — Net and Gross — and plan around whichever gives the stricter (lower) pass threshold, so you\'re covered either way.',
  },
  'Session Clock': {
    what: 'The Killzone Clock shows ICT\'s highest-probability trading windows for both Forex and Indices — displayed in EST and your local Kigali (EAT) time, with live active/closed status and a countdown for each window.',
    why: 'Price delivers its sharpest, most predictable moves during specific killzones — not the entire session. Trading outside these windows means fighting thin liquidity and choppy price action. Knowing exactly when each killzone opens and closes, in your timezone, is a fundamental edge.',
    how: [
      { step:'1', title:'Pick your market', body:'Toggle between Forex Killzones and Indices Killzones — each has different optimal windows.' },
      { step:'2', title:'Check the active window', body:'Green ACTIVE badge = a killzone is open right now. The card shows how many minutes remain.' },
      { step:'3', title:'Use EAT times for planning', body:'Every killzone shows both EST and Kigali (EAT) times so you always know your local entry window.' },
    ],
    tip: 'London killzone (09:00–12:00 EAT) and NY AM killzone (15:00–18:00 EAT for Forex, 15:30–18:00 for Indices) are your two highest-probability windows each day. Plan your screen time around these.',
  },
  'Drawdown Recovery': {
    what: 'Drawdown Recovery runs a Monte Carlo simulation — 500 randomised trade sequences — to estimate your statistical probability of recovering from a drawdown, given your actual win rate, average win, average loss, and trade frequency.',
    why: `After a drawdown, the math becomes brutally asymmetric: a 20% drawdown requires a 25% gain to recover. A 50% drawdown requires a 100% gain. Most traders do not intuitively grasp this — they assume recovering 20% of their account requires 20% gains. It does not. This tool makes the real numbers visible so you trade with clear eyes, not false hope or unnecessary panic.`,
    how: [
      { step:'1', title:'Enter your current drawdown %', body:'How far below your peak balance are you right now? Use the quick presets or enter a custom value.' },
      { step:'2', title:'Input your trading statistics', body:'Win rate, trades per month, average win size, average loss size. Use your actual journal data — not your best week.' },
      { step:'3', title:'Set your peak account balance', body:'The balance you had at the top before the drawdown began.' },
      { step:'4', title:'Optionally set prop firm limits', body:'If you are trading a funded account, enter the daily and overall drawdown limits. The calculator warns you if you are already close to or at those thresholds.' },
      { step:'5', title:'Run the simulation', body:'500 random trade sequences are generated matching your stats. The chart shows green paths (recovered) and red paths (blew up). The summary gives you your recovery probability, median time to recover, and risk of total account loss.' },
    ],
    tip: 'If your Expected Value (EV) per trade is negative — meaning avg_loss × loss_rate > avg_win × win_rate — no position sizing will save you. Fix your strategy first. The tool will tell you this directly in the result summary.',
    warning: 'A high recovery probability does not mean you will recover quickly. Median recovery time of 3–6 months is common even with a positive edge and a 20% drawdown. Patience and consistency are the variables you control.',
  },
};

/* ─────────────────────────────────────────────
   TOOL PAGE (single tool, opened from navbar dropdown)
───────────────────────────────────────────── */
function ToolPage({ toolLabel, onBack, onNavigate }) {
  const PANELS = {
    'Position Sizing':      { component:<ToolPositionSizing />, icon:'⚖️', desc:'Calculate your exact position size based on account risk.' },
    'Lot Size Calculator':  { component:<ToolLotSize />,        icon:'🧮', desc:'Precise lot sizing from entry, stop-loss price, and risk.' },
    'Drawdown Recovery':    { component:<DrawdownContent />,    icon:'📉', desc:'Monte Carlo simulation of your drawdown recovery odds.' },
    'Risk:Reward Calc':     { component:<ToolRiskReward />,     icon:'🎯', desc:'Instantly validate trade quality with RR ratio and payout.' },
    'Profit Target Planner':{ component:<ToolProfitTarget />,   icon:'🏆', desc:'Reverse-engineer what RR and win rate your monthly goal needs.' },
    'Prop Firm Tracker':    { component:<ToolPropFirm />,       icon:'🏛️', desc:'Track challenge progress, buffers, and per-day targets.' },
    'Consistency Rule':     { component:<ToolConsistencyRule />,icon:'📅', desc:'Check your highest trading day against your firm\'s consistency limit.' },
    'Session Clock':        { component:<ToolSessionClock />,   icon:'🌐', desc:'ICT killzone clock — Forex & Indices windows in EST + EAT.' },
  };
  const panel = PANELS[toolLabel] || PANELS['Position Sizing'];
  const label = PANELS[toolLabel] ? toolLabel : 'Position Sizing';
  const expl  = TOOL_EXPLANATIONS[label];

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', paddingTop:80 }}>
      {/* Page header */}
      <div style={{ borderBottom:'1px solid var(--overlay-7)', padding:'28px 24px 24px' }}>
        <div style={{ maxWidth:1100, margin:'0 auto' }}>
          <button onClick={onBack} style={{ display:'inline-flex', alignItems:'center', gap:8, background:'none', border:'none', color:'var(--text-38)', cursor:'pointer', fontSize:11, fontFamily:"'DM Mono',monospace", marginBottom:18, padding:0, letterSpacing:'0.08em', transition:'color 0.2s' }}
          >
            ← Back to home
          </button>
          <div style={{ display:'flex', alignItems:'center', gap:14 }}>
            <div style={{ width:48, height:48, borderRadius:13, background:'linear-gradient(135deg,rgba(212,168,67,0.18),rgba(212,168,67,0.05))', border:'1px solid rgba(212,168,67,0.22)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>{panel.icon}</div>
            <div>
              <div className="lp-label" style={{ marginBottom:4 }}>Trading tool</div>
              <h1 style={{ fontSize:'clamp(24px,3.5vw,40px)', fontWeight:800, color:'var(--text)', margin:0, fontFamily:"'Bricolage Grotesque',sans-serif", letterSpacing:'-0.03em', lineHeight:1.1 }}>{label}</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="lp-toolpage-content" style={{ maxWidth:1100, margin:'0 auto', padding:'40px 24px 80px' }}>

        {/* Calculator panel */}
        <div style={{ background:'var(--surface4)', border:'1px solid rgba(212,168,67,0.15)', borderRadius:18, overflow:'hidden', boxShadow:'0 32px 80px rgba(0,0,0,0.5)', marginBottom:56 }}>
          <div style={{ padding:'18px 28px', borderBottom:'1px solid var(--overlay-6)', display:'flex', alignItems:'center', gap:12, background:'var(--overlay-2)' }}>
            <div style={{ width:34, height:34, borderRadius:10, background:'linear-gradient(135deg,rgba(212,168,67,0.2),rgba(212,168,67,0.06))', border:'1px solid rgba(212,168,67,0.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:15 }}>{panel.icon}</div>
            <div>
              <div style={{ fontSize:14, fontWeight:700, color:'var(--text)' }}>{label}</div>
              <div style={{ fontSize:11, color:'var(--text-38)', fontFamily:"'DM Mono',monospace", marginTop:1 }}>{panel.desc}</div>
            </div>
            <div style={{ marginLeft:'auto', display:'flex', gap:5 }}>
              {['#FF5F57','#FEBC2E','#28C840'].map(c=><div key={c} style={{ width:9, height:9, borderRadius:'50%', background:c }}/>)}
            </div>
          </div>
          <div className="lp-tool-panel-inner" style={{ padding:'32px 28px' }}>{panel.component}</div>
        </div>

        {/* Explanation section */}
        {expl && (
          <div style={{ display:'flex', flexDirection:'column', gap:48 }}>

            {/* What is it */}
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:'rgba(212,168,67,0.6)', letterSpacing:'0.14em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace", marginBottom:14 }}>What is this tool?</div>
              <p style={{ fontSize:16, color:'var(--text-70)', lineHeight:1.9, maxWidth:760, margin:0, fontFamily:"'Bricolage Grotesque',sans-serif", fontWeight:400 }}>{expl.what}</p>
            </div>

            {/* Why it matters */}
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:'rgba(212,168,67,0.6)', letterSpacing:'0.14em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace", marginBottom:14 }}>Why it matters</div>
              <div style={{ padding:'22px 28px', background:'rgba(212,168,67,0.04)', border:'1px solid rgba(212,168,67,0.14)', borderLeft:'3px solid rgba(212,168,67,0.55)', borderRadius:'0 12px 12px 0', maxWidth:760 }}>
                <p style={{ fontSize:15.5, color:'var(--text-70)', lineHeight:1.9, margin:0, fontFamily:"'Bricolage Grotesque',sans-serif", fontWeight:400 }}>{expl.why}</p>
              </div>
            </div>

            {/* How to use */}
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:'rgba(212,168,67,0.6)', letterSpacing:'0.14em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace", marginBottom:18 }}>How to use it</div>
              <div style={{ display:'flex', flexDirection:'column', gap:12, maxWidth:760 }}>
                {expl.how.map(s => (
                  <div key={s.step} style={{ display:'flex', gap:18, alignItems:'flex-start', padding:'18px 22px', background:'var(--overlay-2)', border:'1px solid var(--overlay-7)', borderRadius:12 }}>
                    <div style={{ width:30, height:30, borderRadius:9, background:'linear-gradient(135deg,rgba(212,168,67,0.2),rgba(212,168,67,0.06))', border:'1px solid rgba(212,168,67,0.22)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:800, color:'var(--gold)', fontFamily:"'DM Mono',monospace", flexShrink:0, marginTop:1 }}>{s.step}</div>
                    <div>
                      <div style={{ fontSize:15, fontWeight:700, color:'var(--text)', marginBottom:6, fontFamily:"'Bricolage Grotesque',sans-serif" }}>{s.title}</div>
                      <div style={{ fontSize:14, color:'var(--text-55)', lineHeight:1.75, fontFamily:"'Bricolage Grotesque',sans-serif", fontWeight:400 }}>{s.body}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tip */}
            <div style={{ display:'flex', gap:16, padding:'20px 24px', background:'rgba(46,204,138,0.05)', border:'1px solid rgba(46,204,138,0.16)', borderRadius:12, maxWidth:760 }}>
              <span style={{ fontSize:20, flexShrink:0, marginTop:2 }}>💡</span>
              <p style={{ fontSize:14.5, color:'rgba(46,204,138,0.85)', lineHeight:1.8, margin:0, fontFamily:"'Bricolage Grotesque',sans-serif", fontWeight:400 }}>{expl.tip}</p>
            </div>

            {/* Warning (optional) */}
            {expl.warning && (
              <div style={{ display:'flex', gap:16, padding:'20px 24px', background:'rgba(224,85,85,0.05)', border:'1px solid rgba(224,85,85,0.16)', borderRadius:12, maxWidth:760 }}>
                <span style={{ fontSize:20, flexShrink:0, marginTop:2 }}>⚠️</span>
                <p style={{ fontSize:14.5, color:'rgba(224,85,85,0.8)', lineHeight:1.8, margin:0, fontFamily:"'Bricolage Grotesque',sans-serif", fontWeight:400 }}>{expl.warning}</p>
              </div>
            )}

            {/* Other tools */}
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--text-28)', letterSpacing:'0.14em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace", marginBottom:16 }}>Other tools</div>
              <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
                {TOOLS_ITEMS.filter(t => t.label !== label).map(t => (
                  <button key={t.label} onClick={() => { window.scrollTo({ top:0, behavior:'smooth' }); onNavigate(t.label); }}
                    style={{ display:'flex', alignItems:'center', gap:12, padding:'13px 20px', borderRadius:11, cursor:'pointer', background:'var(--overlay-3)', border:'1px solid var(--overlay-8)', transition:'all 0.2s' }}
                  >
                    <span style={{ fontSize:18 }}>{t.icon}</span>
                    <div>
                      <div style={{ fontSize:13.5, fontWeight:700, color:'var(--text)', fontFamily:"'Bricolage Grotesque',sans-serif" }}>{t.label}</div>
                      <div style={{ fontSize:11.5, color:'var(--text-38)', fontFamily:"'DM Mono',monospace", marginTop:2 }}>{t.sub}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}

// Drawdown tool as standalone content (without the section wrapper)
function DrawdownContent() {
  const [dd,        setDd]        = useState('20');
  const [winRate,   setWinRate]   = useState('55');
  const [tradesM,   setTradesM]   = useState('20');
  const [avgWin,    setAvgWin]    = useState('500');
  const [avgLoss,   setAvgLoss]   = useState('300');
  const [peakAcct,  setPeakAcct]  = useState('25000');
  const [dailyDD,   setDailyDD]   = useState('5');
  const [overallDD, setOverallDD] = useState('10');
  const [result,    setResult]    = useState(null);
  const simTimer  = useRef(null);
  const PRESETS = [5, 10, 20, 30, 50];

  // Auto-run simulation whenever any input changes, debounced 300ms
  useEffect(() => {
    clearTimeout(simTimer.current);
    simTimer.current = setTimeout(() => {
      const ddPct    = parseFloat(dd)       / 100 || 0.2;
      const wr       = parseFloat(winRate)  / 100 || 0.55;
      const tpm      = parseInt(tradesM)          || 20;
      const aw       = parseFloat(avgWin)         || 500;
      const al       = parseFloat(avgLoss)        || 300;
      const peak     = parseFloat(peakAcct)       || 25000;
      const dLim     = parseFloat(dailyDD)  / 100 || 0.05;
      const oLim     = parseFloat(overallDD)/ 100 || 0.10;

      if (ddPct <= 0 || wr <= 0 || wr >= 1 || tpm <= 0 || aw <= 0 || al <= 0 || peak <= 0) return;

      const lossAmt       = peak * ddPct;
      const start         = peak - lossAmt;
      const target        = peak;
      const gainNeeded    = (peak / start - 1) * 100;
      const ev            = wr * aw - (1 - wr) * al;
      const overLim       = peak * oLim;
      const dailyLim      = peak * dLim;
      const tradesToDaily = Math.max(0, Math.floor((start - (peak - dailyLim)) / al));
      const atOverallLimit = lossAmt >= overLim;

      const PATHS      = 500;
      const MAX_TRADES = tpm * 24;
      let recovered    = 0;
      const recTrades  = [];

      for (let p = 0; p < PATHS; p++) {
        let bal = start, rec = false;
        for (let t = 0; t < MAX_TRADES; t++) {
          const win = Math.random() < wr;
          bal += win ? aw : -al;
          if (bal <= 0) break;
          if (!rec && bal >= target) { rec = true; recovered++; recTrades.push(t + 1); break; }
        }
      }

      const recPct      = (recovered / PATHS) * 100;
      recTrades.sort((a, b) => a - b);
      const medTrades   = recTrades.length > 0 ? recTrades[Math.floor(recTrades.length / 2)] : null;
      const medMonths   = medTrades ? (medTrades / tpm).toFixed(1) : null;
      const blowoutRisk = ((PATHS - recovered) / PATHS * 100).toFixed(0);

      setResult({ recPct: recPct.toFixed(1), medMonths, blowoutRisk, ev: ev.toFixed(0), gainNeeded: gainNeeded.toFixed(1), tradesToDaily, atOverallLimit });
    }, 300);
    return () => clearTimeout(simTimer.current);
  }, [dd, winRate, tradesM, avgWin, avgLoss, peakAcct, dailyDD, overallDD]);

  const recColor = result ? (parseFloat(result.recPct)>=70?'#2ECC8A':parseFloat(result.recPct)>=40?'#D4A843':'#E05555') : 'var(--text-20)';
  const evColor  = result ? (parseFloat(result.ev)>0?'#2ECC8A':'#E05555') : 'var(--text-30)';

  return (
    <div className="lp-tool-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24, alignItems:'start' }}>
      {/* Left inputs */}
      <div className="u-flex-col u-gap-14">
        <p style={{ fontSize:11, fontWeight:700, color:'var(--text-35)', letterSpacing:'0.1em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace", margin:0 }}>Your Trading Stats</p>
        <div>
          <DField label="Current Drawdown (%)" value={dd} onChange={setDd} suffix="%" tip="How far below your peak account value you currently are" />
          <div style={{ display:'flex', gap:6, marginTop:8, flexWrap:'wrap' }}>
            {PRESETS.map(p => (
              <button key={p} onClick={()=>setDd(String(p))} style={{
                padding:'5px 13px', borderRadius:100, fontSize:11, fontWeight:700,
                fontFamily:"'DM Mono',monospace", cursor:'pointer', border:'1px solid',
                background: dd===String(p)?'rgba(212,168,67,0.15)':'var(--overlay-4)',
                borderColor: dd===String(p)?'rgba(212,168,67,0.5)':'var(--overlay-10)',
                color: dd===String(p)?'#D4A843':'var(--text-45)', transition:'all 0.18s',
              }}>{p}%</button>
            ))}
          </div>
        </div>
        <div className="lp-dd-sub-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <DField label="Win Rate (%)"   value={winRate}  onChange={setWinRate}  suffix="%" tip="% of trades closing in profit" />
          <DField label="Trades/Month"   value={tradesM}  onChange={setTradesM}  tip="Trades per month" />
          <DField label="Avg Win ($)"    value={avgWin}   onChange={setAvgWin}   prefix="$" tip="Avg $ per winning trade" />
          <DField label="Avg Loss ($)"   value={avgLoss}  onChange={setAvgLoss}  prefix="$" tip="Avg $ per losing trade" />
        </div>
        <DField label="Peak Account ($)" value={peakAcct} onChange={setPeakAcct} prefix="$" tip="Balance before drawdown" />
        <p style={{ fontSize:11, fontWeight:700, color:'var(--text-28)', letterSpacing:'0.1em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace", margin:'4px 0 0' }}>Prop Firm Limits <span style={{ fontWeight:400, opacity:0.6 }}>(optional)</span></p>
        <div className="lp-dd-sub-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <DField label="Daily DD (%)"   value={dailyDD}   onChange={setDailyDD}   suffix="%" tip="Max daily drawdown by prop firm" />
          <DField label="Overall DD (%)" value={overallDD} onChange={setOverallDD} suffix="%" tip="Max total drawdown before fail" />
        </div>
        {/* Live indicator */}
        <div style={{ display:'flex', alignItems:'center', gap:7, padding:'8px 12px', background:'rgba(46,204,138,0.05)', border:'1px solid rgba(46,204,138,0.12)', borderRadius:8 }}>
          <span style={{ width:7, height:7, borderRadius:'50%', background:'#2ECC8A', display:'inline-block', animation:'kfx-pulse 2s infinite', flexShrink:0 }} />
          <span style={{ fontSize:11, color:'rgba(46,204,138,0.7)', fontFamily:"'DM Mono',monospace", letterSpacing:'0.06em' }}>Auto-calculating · updates as you type</span>
        </div>
      </div>

      {/* Right results */}
      <div className="u-flex-col u-gap-14">
        <div className="u-card-sm">
          <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--overlay-5)', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
            <span style={{ fontSize:10, fontWeight:700, color:'var(--text-30)', letterSpacing:'0.1em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace" }}>Recovery Projection</span>
            <div style={{ display:'flex', gap:12 }}>
              {[['#2ECC8A','Recovered'],['#E05555','Failed']].map(([c,l])=>(
                <div key={l} className="u-flex-ai u-gap-5">
                  <div style={{ width:12, height:3, background:c, borderRadius:2, opacity:0.8 }} />
                  <span style={{ fontSize:10, color:'var(--text-45)', fontFamily:"'DM Mono',monospace" }}>{l}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ padding:'12px 8px 4px' }}>
            {result ? (
              <DrawdownChart result={result} peak={parseFloat(peakAcct)||25000} dd={parseFloat(dd)||20} />
            ) : (
              <div style={{ height:170, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:8 }}>
                <span style={{ fontSize:24, opacity:0.2 }}>📊</span>
                <span style={{ fontSize:11, color:'var(--text-20)', fontFamily:"'DM Mono',monospace" }}>Calculating…</span>
              </div>
            )}
          </div>
        </div>

        <div className="u-card-sm">
          <div style={{ padding:'14px 18px', borderBottom:'1px solid var(--overlay-5)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontSize:11, fontWeight:600, color:'var(--text-45)', fontFamily:"'DM Mono',monospace", letterSpacing:'0.06em' }}>Recovery Probability</span>
            <span style={{ fontSize:28, fontWeight:800, color:recColor, fontFamily:"'DM Mono',monospace", letterSpacing:'-0.03em' }}>{result?`${result.recPct}%`:'—'}</span>
          </div>
          <div style={{ padding:'14px 18px', display:'flex', flexDirection:'column', gap:9 }}>
            {[
              { label:'Median Recovery Time', val: result?.medMonths?`${result.medMonths} months`:'—', color: result?.medMonths?'var(--text)':'#E05555' },
              { label:'Blowout Risk',          val: result?`${result.blowoutRisk}%`:'—', color: result&&parseInt(result.blowoutRisk)>50?'#E05555':result?'#D4A843':'var(--text-30)' },
              { label:'EV Per Trade',          val: result?`${parseFloat(result.ev)>=0?'+':''}$${result.ev}`:'—', color: evColor },
              { label:'Gain Needed',           val: result?`+${result.gainNeeded}%`:'—', color:'var(--text-70)' },
              { label:'Losses to Daily Limit', val: result?(result.tradesToDaily<=0?'At limit':`${result.tradesToDaily} trades`):'—', color: result&&result.tradesToDaily<=0?'#E05555':'#D4A843' },
              { label:'At Overall Limit',      val: result?(result.atOverallLimit?'⚠ At limit':'Within limit'):'—', color: result?.atOverallLimit?'#E05555':'#2ECC8A' },
            ].map(r=>(
              <div key={r.label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'9px 12px', background:'var(--overlay-2)', borderRadius:8, border:'1px solid var(--overlay-5)' }}>
                <span style={{ fontSize:12, color:'var(--footer-link)', fontFamily:"'DM Mono',monospace" }}>{r.label}</span>
                <span style={{ fontSize:13, fontWeight:700, color:r.color, fontFamily:"'DM Mono',monospace" }}>{r.val}</span>
              </div>
            ))}
          </div>
          {result && (
            <div style={{ margin:'0 18px 18px', padding:'12px 16px', background:parseFloat(result.recPct)>=50?'rgba(46,204,138,0.06)':'rgba(224,85,85,0.06)', borderRadius:9, border:`1px solid ${parseFloat(result.recPct)>=50?'rgba(46,204,138,0.18)':'rgba(224,85,85,0.18)'}`, fontSize:12.5, color:'var(--text-55)', lineHeight:1.7 }}>
              A <strong style={{ color:'var(--text)' }}>{dd}% drawdown</strong> needs a <strong style={{ color:'var(--gold)' }}>+{result.gainNeeded}%</strong> gain to recover.{' '}
              {parseFloat(result.ev)>0 ? `EV of $${result.ev}/trade is positive — variance is your main risk.` : `Negative EV of $${result.ev}/trade — fix your strategy before focusing on recovery.`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   PRICING CARDS
───────────────────────────────────────────── */
function PricingSection({ onGetStarted, onGoToPricing }) {
  // onGoToPricing — navigates to the in-app /pricing page (with full waitlist capture).
  // For logged-out visitors this is handled by App: it opens Auth then redirects to pricing.
  const handleComingSoon = () => {
    if (typeof onGoToPricing === 'function') onGoToPricing();
    else onGetStarted(); // fallback: just open auth
  };
  const plans = [
    {
      name: 'Free',
      price: '$0',
      period: 'forever',
      tag: 'Everything Unlocked',
      tagColor: 'rgba(46,204,138,0.15)',
      tagText: '#2ECC8A',
      desc: 'All features unlocked during early access. No credit card, no trial, no limits.',
      highlight: false,
      active: true,
      cta: 'Start Free — No Card',
      ctaColor: '#2ECC8A',
      features: [
        { text:'Unlimited trade logging' },
        { text:'Full analytics dashboard' },
        { text:'MT4/MT5 CSV import' },
        { text:'Execution grading (A/B/C)' },
        { text:'Trading notebook & debrief' },
        { text:'Rule adherence tracker' },
        { text:'What-If trade simulator' },
        { text:'Session heatmap & equity curve' },
        { text:'Prop firm tracker' },
        { text:'Drawdown & position sizing tools' },
        { note: 'All pro features included now' },
        { text:'AI coaching report', note:'1 per month' },
      ],
    },
    {
      name: 'Pro',
      price: '$4.99',
      period: 'per month',
      tag: 'Coming Soon',
      tagColor: 'rgba(212,168,67,0.1)',
      tagText: '#D4A843',
      desc: 'Unlimited AI coaching, advanced analytics, and priority support.',
      highlight: true,
      active: false,
      cta: 'Not Available Yet',
      ctaColor: '#D4A843',
      features: [
        { text:'Everything in Free' },
        { text:'Unlimited AI coaching reports' },
        { text:'AI pattern detection alerts' },
        { text:'Weekly AI performance summary' },
        { text:'Advanced session breakdowns' },
        { text:'Export reports (PDF)' },
        { text:'Priority email support' },
        { text:'Early access to new features' },
      ],
    },
    {
      name: 'Lifetime',
      price: '$49.99',
      period: 'one-time',
      tag: 'Coming Soon',
      tagColor: 'rgba(91,143,232,0.1)',
      tagText: '#5B8FE8',
      desc: 'Pay once, own it forever. All Pro features included, no recurring fees.',
      highlight: false,
      active: false,
      cta: 'Not Available Yet',
      ctaColor: '#5B8FE8',
      features: [
        { text:'Everything in Pro' },
        { text:'Lifetime access — pay once' },
        { text:'All future updates included' },
        { text:'Unlimited AI coaching reports' },
        { text:'PDF export forever' },
        { text:'Priority support forever' },
        { text:'Founding member badge' },
      ],
    },
  ];

  return (
    <section id="pricing" style={{ padding:'112px 24px', background:'rgba(255,255,255,0.012)', borderTop:'1px solid var(--overlay-6)', borderBottom:'1px solid var(--overlay-6)' }}>
      <div style={{ maxWidth:1100, margin:'0 auto' }}>
        <Reveal>
          <div className="u-section-hd">
            <div className="lp-label">Pricing</div>
            <h2 className="lp-h2">Everything free during early access</h2>
            <p style={{ fontSize:16, color:'var(--text-45)', maxWidth:520, margin:'18px auto 0', lineHeight:1.8 }}>
              We've unlocked all features while we grow. Pro and Lifetime plans are coming — early users will get preferential pricing when they launch.
            </p>
          </div>
        </Reveal>

        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:20, alignItems:'start' }} className="lp-pricing-grid">
          {plans.map((plan, i) => (
            <Reveal key={plan.name} delay={i * 90}>
              <div className={`lp-pricing-card${plan.active?' active':''}`} style={{
                borderRadius:18,
                border: plan.active
                  ? '1px solid rgba(46,204,138,0.4)'
                  : plan.highlight
                    ? '1px solid rgba(212,168,67,0.2)'
                    : '1px solid var(--overlay-6)',
                background: plan.active
                  ? 'linear-gradient(160deg,rgba(46,204,138,0.06) 0%,rgba(46,204,138,0.02) 100%)'
                  : plan.highlight
                    ? 'linear-gradient(160deg,rgba(212,168,67,0.05) 0%,rgba(212,168,67,0.01) 100%)'
                    : 'var(--overlay-2)',
                padding:'32px 28px',
                position:'relative',
                overflow:'hidden',
                opacity: plan.active ? 1 : 0.82,
              }}
              >
                {/* Top glow line */}
                {plan.active && <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:'linear-gradient(90deg,transparent,#2ECC8A,transparent)', borderRadius:'18px 18px 0 0' }} />}
                {plan.highlight && !plan.active && <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:'linear-gradient(90deg,transparent,rgba(212,168,67,0.4),transparent)', borderRadius:'18px 18px 0 0' }} />}

                {/* Tag badge */}
                <div style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'4px 10px', borderRadius:100, background:plan.tagColor, border:`1px solid ${plan.tagText}33`, marginBottom:20 }}>
                  {plan.active && <span style={{ width:6, height:6, borderRadius:'50%', background:'#2ECC8A', animation:'kfx-pulse 2s infinite', boxShadow:'0 0 6px #2ECC8A' }} />}
                  {!plan.active && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={plan.tagText} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>}
                  <span style={{ fontSize:10, fontWeight:700, color:plan.tagText, letterSpacing:'0.1em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace" }}>{plan.tag}</span>
                </div>

                <div style={{ marginBottom:6 }}>
                  <span style={{ fontSize:13, fontWeight:600, color: plan.active ? 'var(--text)' : 'var(--text-45)', fontFamily:"'Bricolage Grotesque',sans-serif" }}>{plan.name}</span>
                </div>

                <div style={{ display:'flex', alignItems:'baseline', gap:6, marginBottom:8 }}>
                  <span style={{ fontSize:48, fontWeight:800, letterSpacing:'-0.04em', color: plan.active ? 'var(--text)' : 'var(--text-30)', fontFamily:"'Bricolage Grotesque',sans-serif", lineHeight:1 }}>{plan.price}</span>
                  <span style={{ fontSize:12, color:'var(--text-25)', fontFamily:"'DM Mono',monospace" }}>/{plan.period}</span>
                </div>

                <p style={{ fontSize:13, color:'var(--muted)', lineHeight:1.65, marginBottom:24, minHeight:44 }}>{plan.desc}</p>

                {/* CTA button */}
                {plan.active ? (
                  <button onClick={onGetStarted} style={{
                    width:'100%', padding:'13px', borderRadius:10, marginBottom:24,
                    background:'linear-gradient(135deg,#2ECC8A,#1a9e6a)',
                    color:'#080808', fontSize:12, fontWeight:700, letterSpacing:'0.08em',
                    textTransform:'uppercase', cursor:'pointer', fontFamily:"'DM Mono',monospace",
                    border:'none', transition:'all 0.2s',
                    boxShadow:'0 4px 24px rgba(46,204,138,0.25)',
                  }}
                  >{plan.cta}</button>
                ) : (
                  <button
                    onClick={handleComingSoon}
                    style={{
                      width:'100%', padding:'13px', borderRadius:10, marginBottom:24,
                      background:'var(--overlay-3)', border:`1px solid ${plan.ctaColor}35`,
                      color:plan.ctaColor, fontSize:12, fontWeight:700, letterSpacing:'0.08em',
                      textTransform:'uppercase', fontFamily:"'DM Mono',monospace",
                      cursor:'pointer', transition:'all 0.2s',
                      display:'flex', alignItems:'center', justifyContent:'center', gap:8,
                    }}
                  >
                    🔔 Notify Me When This Launches
                  </button>
                )}

                {/* Feature list */}
                <div style={{ borderTop:'1px solid var(--overlay-6)', paddingTop:20, display:'flex', flexDirection:'column', gap:9 }}>
                  {plan.features.map((f, fi) => (
                    <div key={fi} style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={plan.active ? '#2ECC8A' : 'var(--text-20)'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0, marginTop:2 }} aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
                      <div>
                        <span style={{ fontSize:12.5, color: plan.active ? 'var(--text-70)' : 'var(--text-30)', lineHeight:1.4 }}>{f.text}</span>
                        {f.note && <span style={{ fontSize:10, color:'rgba(212,168,67,0.6)', fontFamily:"'DM Mono',monospace", marginLeft:6 }}>({f.note})</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Early access explanation */}
        <Reveal delay={220}>
          <div style={{ marginTop:32, padding:'20px 28px', borderRadius:12, background:'rgba(46,204,138,0.04)', border:'1px solid rgba(46,204,138,0.15)', display:'flex', alignItems:'flex-start', gap:14, flexWrap:'wrap' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2ECC8A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0, marginTop:1 }} aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            <p style={{ fontSize:13, color:'var(--muted)', lineHeight:1.75, flex:1 }}>
              <strong style={{ color:'#2ECC8A', fontWeight:700 }}>Why is everything free right now?</strong> KFX is in early access. We're looking for 20–50 serious traders to use the platform, tell us what's broken, and help us build what matters. In return, early users lock in preferential pricing when Pro and Lifetime launch. <strong style={{ color:'var(--text-60)', fontWeight:600 }}>Your feedback shapes the product.</strong>
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   MAIN LANDING PAGE
───────────────────────────────────────────── */
// Import logo so Vite bundles it correctly
import kfxLogo          from '../images/kfxlogo.png';
import kfxLogoLight      from '../images/kfxlogolight.png';
import imgFundedNext     from '../images/FundedNext.png';
import imgXM             from '../images/XM-logo.jpg';
import imgJustMarkets    from '../images/justmarkets-logo.jpg';
import imgTradingView    from '../images/TradingView_logo.png';
import imgDashboard      from '../images/Dashboard.png';
import imgAnalytics      from '../images/Whatifsim.png';
import imgAiCoach        from '../images/Aicoach.png';

export default function LandingPage({ onGetStarted, onNavigate, onGoToPricing }) {
  useFonts(); // non-blocking font injection
  const [scrolled,    setScrolled]    = useState(false);
  const [activeFaq,   setActiveFaq]   = useState(null);
  const [mobileMenu,  setMobileMenu]  = useState(false);
  const [toolsOpen,   setToolsOpen]   = useState(false);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const [heroVisible, setHeroVisible] = useState(false);
  useEffect(() => { const _t = setTimeout(() => setHeroVisible(true), 80); return () => clearTimeout(_t); }, []);
  const [logoErr,     setLogoErr]     = useState(false);
  const [activeTab,    setActiveTab]    = useState('aicoach');
  const [activeTool,   setActiveTool]   = useState('Position Sizing');
  const [selectedTool, setSelectedTool] = useState(null); // null = home, string = tool page
  const [activeSection, setActiveSection] = useState('');
  const [darkMode,     setDarkMode]     = useState(() => {
    try { return localStorage.getItem('kfx-theme') !== 'light'; } catch { return true; }
  });
  const toggleTheme = () => setDarkMode(prev => {
    const next = !prev;
    try { localStorage.setItem('kfx-theme', next ? 'dark' : 'light'); } catch {}
    return next;
  });
  const toolsRef  = useRef(null);
  const subnavRef = useRef(null);

  // Auto-scroll active subnav pill into view
  useEffect(() => {
    if (!subnavRef.current) return;
    const active = subnavRef.current.querySelector('.lp-subnav-btn.active');
    if (active) active.scrollIntoView({ behavior:'smooth', block:'nearest', inline:'center' });
  }, [selectedTool]);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 48);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); };
  }, []);

  // Scroll-spy: highlight active nav section
  useEffect(() => {
    const ids = ['features','tools','pricing','testimonials'];
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.id); });
    }, { rootMargin:'-40% 0px -55% 0px' });
    ids.forEach(id => { const el = document.getElementById(id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);

  // Close tools dropdown on outside click or Escape key
  useEffect(() => {
    const onMouse = e => {
      if (toolsRef.current && !toolsRef.current.contains(e.target)) setToolsOpen(false);
    };
    const onKey = e => {
      if (e.key === 'Escape') {
        setToolsOpen(false);
        setMobileMenu(false);
      }
      // Arrow-key nav inside tools dropdown
      if (toolsOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        e.preventDefault();
        const items = toolsRef.current?.querySelectorAll('[data-tool-item]');
        if (!items?.length) return;
        const focused = document.activeElement;
        const idx = Array.from(items).indexOf(focused);
        const next = e.key === 'ArrowDown'
          ? (idx + 1) % items.length
          : (idx - 1 + items.length) % items.length;
        items[next]?.focus();
      }
    };
    document.addEventListener('mousedown', onMouse);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouse);
      document.removeEventListener('keydown', onKey);
    };
  }, [toolsOpen]);

  const scrollTo = id => {
    document.getElementById(id)?.scrollIntoView({ behavior:'smooth' });
    setMobileMenu(false);
    setToolsOpen(false);
  };

  const handleToolSelect = toolLabel => {
    setSelectedTool(toolLabel);
    setToolsOpen(false);
    setMobileMenu(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const FEATURES = [
    { icon:'journal',    title:'Full Trade Journal',      desc:'Log every entry with emotion, session, strategy and execution grade. Nothing missed — everything searchable and filterable.' },
    { icon:'analytics',  title:'Deep Analytics',          desc:'Win rate, average RR, drawdown curve, session heatmap and streak analysis — auto-calculated the moment you save a trade.' },
    { icon:'ai',         title:'AI Coach (Claude)',        desc:'The AI reads YOUR actual trade history and tells you exactly what to fix — your worst session, your best setup, your costly habits.' },
    { icon:'simulator',  title:'What-If Simulator',       desc:'Replay your month with perfect discipline. See the exact dollar difference between what you did and what your rules say.' },
    { icon:'grade',      title:'Execution Grading',       desc:'Grade every entry A/B/C. Track rule adherence over time. The single habit that separates funded traders from everyone else.' },
    { icon:'discipline', title:'Discipline Tracking',    desc:'Build consistency through graded entries and rule-adherence scoring. See exactly how following your plan correlates with your P&L.' },
  ];

  const TESTIMONIALS = [
    { name:'Jean Claude M.', role:'Forex Trader · Kigali',        avatar:'JC', text:'KFX Journal showed me I was revenge trading every Tuesday afternoon. Fixed that one habit and my whole month turned green.' },
    { name:'Amina K.',        role:'Price Action Trader · Nairobi', avatar:'AK', text:"The AI coach told me my London session trades are 3× better than my NY session. I'd been trading NY for 18 months thinking it was my best. Total game changer." },
    { name:'Patrick N.',      role:'Prop Firm Trader · Kampala',   avatar:'PN', text:"Passed my FTMO challenge on the third attempt. The execution grading kept me disciplined on every single entry. I directly attribute it to KFX Journal." },
    { name:'Diane U.',        role:'Swing Trader · Dar es Salaam',        avatar:'DU', text:'Finally a journaling tool that does not feel like homework. The analytics dashboard shows me everything in one view. I use it every single day.' },
    { name:'Regis Parfait.',        role:'Gold Trader · Kigali',        avatar:'RP', text:'The what-if simulator showed me I was leaving $800/month on the table by closing winners too early. That alone was worth everything.' },
    { name:'RUKUNDO Jackson.',       role:'Forex Student · KFX',          avatar:'RJ', text:'As a student at KFX this has been essential. I can see my progress week by week and the AI points me to exactly which mistakes I keep repeating.' },
  ];

  const FAQS = [
    { q:'Is KFX Journal really 100% free?',       a:"Yes. Core journaling, full analytics dashboard, AI coaching (1 report/month), MT4/MT5 import — all free forever. No credit card. Upgrade to Pro for unlimited AI reports." },
    { q:'How is the AI coach different from ChatGPT?', a:"Powered by Claude (Anthropic) and reads YOUR actual trade history. It doesn't give generic advice — it tells you YOUR worst session, YOUR most common mistake, YOUR best setup." },
    { q:'Can I import my existing MetaTrader history?', a:'Yes. Export your MT4 or MT5 history as a CSV file and import it in one click. We automatically map pair, direction, entry, exit, and PnL.' },
    { q:'Is my trading data private and secure?',  a:'Your trades are visible only to you. Data is encrypted in transit and at rest. We never sell, share, or use your data.' },
    { q:'Does it work for markets other than forex?', a:'Built with forex in mind but works for any instrument — gold (XAUUSD), indices, crypto, commodities.' },
    { q:'What is the execution grade?',            a:"You grade your own entry A, B, or C. A = perfect execution, followed all rules. B = minor deviation. C = broke rules. Over time you'll see how grade correlates with profitability." },
    { q:'I already use another journal. Why switch?', a:'Most popular journaling tools charge $30+/month and have no AI coaching. KFX is a trading intelligence platform — it reads your data, identifies your patterns, and tells you what to fix. Free to start, Pro is $4.99/mo.' },
  ];

  const NAV_ITEMS = [
    { label:'Analytics',   id:'features',     dropdown:false },
    { label:'Tools',       id:'tools',         dropdown:true  },
    { label:'Pricing',     id:'pricing',       dropdown:false },
    { label:'About',       page:'about',       dropdown:false },
  ];

  return (
    <>
      <a href="#main-content" className="lp-skip-link">Skip to main content</a>
      <style>{`
        /* ── Skip-to-content (keyboard / screen reader) ── */
        .lp-skip-link {
          position:absolute; top:-999px; left:16px; z-index:9999;
          padding:10px 20px; background:var(--gold); color:#000;
          font-weight:700; font-size:14px; border-radius:0 0 8px 8px;
          text-decoration:none; font-family:'Bricolage Grotesque',sans-serif;
          transition:top 0.15s;
        }
        .lp-skip-link:focus { top:0; }

        .lp-root *, .lp-root *::before, .lp-root *::after { box-sizing:border-box; margin:0; padding:0; }

        .lp-root {
          /* Core palette */
          --bg:        #070707;
          --surface:   #0f0f0d;
          --surface2:  #161613;
          --surface3:  rgba(14,14,12,0.95);
          --surface4:  rgba(14,14,12,0.9);
          /* Borders */
          --border:    rgba(212,168,67,0.1);
          --border2:   rgba(255,255,255,0.06);
          --border3:   rgba(255,255,255,0.08);
          --border4:   rgba(255,255,255,0.04);
          --border5:   rgba(255,255,255,0.05);
          /* Brand */
          --gold:      #D4A843;
          --gold-lt:   #F0C855;
          --gold-dk:   #8a6820;
          --green:     #2ECC8A;
          --red:       #E05555;
          /* Text hierarchy */
          --text:      #EDE8DE;
          --text-70:   rgba(237,232,222,0.70);
          --text-55:   rgba(237,232,222,0.55);
          --text-45:   rgba(237,232,222,0.45);
          --text-42:   rgba(237,232,222,0.42);
          --text-38:   rgba(237,232,222,0.38);
          --text-35:   rgba(237,232,222,0.35);
          --text-30:   rgba(237,232,222,0.30);
          --text-28:   rgba(237,232,222,0.28);
          --text-25:   rgba(237,232,222,0.25);
          --text-22:   rgba(237,232,222,0.22);
          --text-20:   rgba(237,232,222,0.20);
          --text-18:   rgba(237,232,222,0.18);
          /* Surface overlays */
          --overlay-1: rgba(255,255,255,0.01);
          --overlay-2: rgba(255,255,255,0.02);
          --overlay-3: rgba(255,255,255,0.03);
          --overlay-4: rgba(255,255,255,0.04);
          --overlay-5: rgba(255,255,255,0.05);
          --overlay-6: rgba(255,255,255,0.06);
          --overlay-7: rgba(255,255,255,0.07);
          --overlay-8: rgba(255,255,255,0.08);
          --overlay-10: rgba(255,255,255,0.10);
          --overlay-12: rgba(255,255,255,0.12);
          /* Nav */
          --nav-bg-scrolled:     rgba(7,7,7,0.94);
          --nav-bg:              rgba(7,7,7,0.60);
          --nav-border-scrolled: rgba(212,168,67,0.12);
          --nav-border:          rgba(255,255,255,0.04);
          /* Footer */
          --footer-bg:   #050505;
          --footer-link: rgba(237,232,222,0.50);
          /* Fonts */
          --mono:      'DM Mono', monospace;
          --sans:      'Bricolage Grotesque', sans-serif;
          --r:         8px;
          /* Muted aliases */
          --muted:     rgba(237,232,222,0.55);
          --muted2:    rgba(237,232,222,0.25);

          min-height:100vh;
          background:var(--bg);
          color:var(--text);
          font-family:var(--sans);
          overflow-x:hidden;
          -webkit-font-smoothing:antialiased;
          transition: background 0.3s ease, color 0.3s ease;
        }

        /* ══════════════════════════════════════════
           LIGHT MODE — PREMIUM IVORY DESIGN SYSTEM
           Stripe · Linear · Bloomberg Luxury
        ══════════════════════════════════════════ */
        .lp-root.lp-light {
          /* ── Backgrounds ── */
          --bg:        #FAF8F4;
          --surface:   #F3EFE7;
          --surface2:  #EDE8DC;
          --surface3:  rgba(250,248,244,0.97);
          --surface4:  rgba(243,239,231,0.94);

          /* ── Borders ── */
          --border:    #E7DFCF;
          --border2:   rgba(180,140,40,0.14);
          --border3:   rgba(0,0,0,0.10);
          --border4:   rgba(231,223,207,0.80);
          --border5:   rgba(0,0,0,0.06);

          /* ── Brand gold — same richness as dark mode, not brighter ── */
          --gold:      #C9971A;
          --gold-lt:   #D4A843;
          --gold-dk:   #8B6512;

          /* ── Semantic ── */
          --green:     #00A86B;
          --red:       #C0392B;

          /* ── Text — high contrast on ivory ── */
          --text:      #111111;
          --text-70:   #2D2D2D;
          --text-55:   #4B5563;
          --text-45:   #4B5563;
          --text-42:   #6B7280;
          --text-38:   #6B7280;
          --text-35:   #6B7280;
          --text-30:   #9CA3AF;
          --text-28:   #9CA3AF;
          --text-25:   #9CA3AF;
          --text-22:   #9CA3AF;
          --text-20:   #D1D5DB;
          --text-18:   #D1D5DB;

          /* ── Surface overlays (dark on light) ── */
          --overlay-1:  rgba(0,0,0,0.01);
          --overlay-2:  rgba(0,0,0,0.02);
          --overlay-3:  rgba(0,0,0,0.03);
          --overlay-4:  rgba(0,0,0,0.04);
          --overlay-5:  rgba(0,0,0,0.05);
          --overlay-6:  rgba(0,0,0,0.06);
          --overlay-7:  rgba(0,0,0,0.08);
          --overlay-8:  rgba(0,0,0,0.10);
          --overlay-10: rgba(0,0,0,0.12);
          --overlay-12: rgba(0,0,0,0.14);

          /* ── Nav ── */
          --nav-bg-scrolled:     rgba(250,248,244,0.98);
          --nav-bg:              rgba(250,248,244,0.88);
          --nav-border-scrolled: #E7DFCF;
          --nav-border:          rgba(231,223,207,0.60);

          /* ── Footer ── */
          --footer-bg:   #EDE8DC;
          --footer-link: #4B5563;

          /* ── Aliases ── */
          --muted:     #4B5563;
          --muted2:    #6B7280;

          min-height: 100vh;
          background: var(--bg);
          color: var(--text);
        }
          --gold:      #A67A10;
          --gold-lt:   #C49020;
          background: var(--bg);
          color: var(--text);
        }
        /* ══════════════════════════════════════════
           LIGHT MODE — PREMIUM COMPONENT OVERRIDES
           Stripe · Linear · Bloomberg Luxury Ivory
        ══════════════════════════════════════════ */

        /* Base */
        .lp-root.lp-light { color: #111111; }
        .lp-root.lp-light button { color: inherit; }

        /* ── Hero ── */
        .lp-root.lp-light .lp-hero-grid { opacity: 0.28 !important; }
        .lp-root.lp-light .lp-hero-radial { background: radial-gradient(ellipse 80% 55% at 50% 0%, rgba(201,151,26,0.06) 0%, transparent 70%) !important; }
        .lp-root.lp-light .lp-scanline { display: none !important; }

        /* ── Ticker ── */
        .lp-root.lp-light .lp-ticker-wrap::before { background: linear-gradient(to right, #FAF8F4, transparent); }
        .lp-root.lp-light .lp-ticker-wrap::after  { background: linear-gradient(to left,  #FAF8F4, transparent); }
        .lp-root.lp-light .lp-ticker-item { color: #6B7280; border-right-color: #E7DFCF; }

        /* ── Section labels ── */
        .lp-root.lp-light .lp-label { color: #C9971A; }
        .lp-root.lp-light .lp-label::before { background: #C9971A; }

        /* ── Nav ── */
        .lp-root.lp-light .lp-nav-link { color: #4B5563; }
        .lp-root.lp-light .lp-nav-link:hover,
        .lp-root.lp-light .lp-nav-link.open { color: #111111; }

        /* ── Dropdown ── */
        .lp-root.lp-light .lp-tools-dropdown { background: #FAF8F4 !important; border-color: #E7DFCF !important; box-shadow: 0 16px 48px rgba(0,0,0,0.09), 0 0 0 1px #E7DFCF !important; }
        .lp-root.lp-light .lp-tools-dropdown-header { border-bottom-color: #E7DFCF !important; }
        .lp-root.lp-light .lp-tools-dropdown-item-label { color: #111111 !important; }
        .lp-root.lp-light .lp-tools-dropdown-item:hover { background: rgba(0,0,0,0.04) !important; border-color: #E7DFCF !important; }
        .lp-root.lp-light .lp-tools-dropdown-footer { background: linear-gradient(135deg, rgba(201,151,26,0.06), rgba(201,151,26,0.02)) !important; border-color: rgba(201,151,26,0.20) !important; }

        /* ── Cards ── */
        .lp-root.lp-light .lp-feat-card { background: #F3EFE7; border-color: #E7DFCF; box-shadow: 0 1px 4px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03); }
        .lp-root.lp-light .lp-feat-card:hover { border-color: #C9971A; box-shadow: 0 4px 24px rgba(139,101,18,0.12); transform: translateY(-3px); }
        .lp-root.lp-light .lp-step-card { background: #F3EFE7 !important; border-color: #E7DFCF !important; box-shadow: 0 1px 4px rgba(0,0,0,0.04) !important; }
        .lp-root.lp-light .lp-step-card:hover { border-color: #C9971A !important; box-shadow: 0 4px 20px rgba(139,101,18,0.10) !important; }
        .lp-root.lp-light .lp-testi-card { background: #F3EFE7; border-color: #E7DFCF; box-shadow: 0 1px 4px rgba(0,0,0,0.04); }
        .lp-root.lp-light .lp-testi-card:hover { border-color: #C9971A; box-shadow: 0 4px 20px rgba(139,101,18,0.10); }
        .lp-root.lp-light .lp-partner-card { background: #F3EFE7 !important; border-color: #E7DFCF !important; box-shadow: 0 1px 4px rgba(0,0,0,0.04) !important; }

        /* ── FAQ ── */
        .lp-root.lp-light .lp-faq-item { border-color: #E7DFCF; background: #F3EFE7; }
        .lp-root.lp-light .lp-faq-btn:hover { background: rgba(0,0,0,0.025); }
        .lp-root.lp-light .lp-faq-answer { color: #4B5563; }

        /* ── Comparison table ── */
        .lp-root.lp-light .lp-compare-wrap { background: #F3EFE7; border-color: #E7DFCF !important; box-shadow: 0 1px 8px rgba(0,0,0,0.05); }
        .lp-root.lp-light .lp-compare-wrap thead tr { background: rgba(0,0,0,0.025) !important; }
        .lp-root.lp-light .lp-compare-wrap tr { border-bottom-color: #E7DFCF !important; }

        /* ── Pricing ── */
        .lp-root.lp-light .lp-pricing-card { background: #F3EFE7 !important; border-color: #E7DFCF !important; box-shadow: 0 1px 4px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03) !important; }
        .lp-root.lp-light .lp-pricing-card-highlight { background: linear-gradient(160deg, rgba(201,151,26,0.07), #F3EFE7) !important; border-color: rgba(201,151,26,0.45) !important; box-shadow: 0 8px 40px rgba(139,101,18,0.14) !important; }
        .lp-root.lp-light .lp-pricing-feat-divider { border-top-color: #E7DFCF !important; }
        .lp-root.lp-light .lp-pricing-ghost-btn { background: rgba(0,0,0,0.04) !important; border-color: #E7DFCF !important; color: #2D2D2D !important; }

        /* ── Stats ── */
        .lp-root.lp-light .lp-stats-cell-even { background: rgba(0,0,0,0.025) !important; }
        .lp-root.lp-light .lp-stats-cell-odd  { background: rgba(0,0,0,0.015) !important; }

        /* ── Section alternates ── */
        .lp-root.lp-light section[style*="rgba(255,255,255,0.012)"],
        .lp-root.lp-light section[style*="rgba(255,255,255,0.015)"] { background: #F3EFE7 !important; border-color: #E7DFCF !important; }

        /* ── Forms ── */
        .lp-root.lp-light input, .lp-root.lp-light select { background: #FAF8F4 !important; border-color: #E7DFCF !important; color: #111111 !important; }

        /* ── Tool tabs ── */
        .lp-root.lp-light .lp-tool-tab-inactive { border-color: #E7DFCF !important; background: #F3EFE7 !important; color: #4B5563 !important; }
        .lp-root.lp-light .lp-tool-tab-inactive:hover { border-color: #C9971A !important; background: rgba(201,151,26,0.05) !important; color: #C9971A !important; }

        /* ── Mockup chrome ── */
        .lp-root.lp-light [data-mockup] { background: #F3EFE7 !important; border-color: #E7DFCF !important; }
        .lp-root.lp-light .lp-browser-chrome { background: #EDE8DC !important; border-color: #E7DFCF !important; }
        .lp-root.lp-light .lp-browser-bar { background: rgba(0,0,0,0.04) !important; border-color: #E7DFCF !important; }

        /* ── Footer ── */
        .lp-root.lp-light footer { background: #EDE8DC !important; border-top-color: #E7DFCF !important; }
        .lp-root.lp-light .lp-footer-cta-strip { border-bottom-color: #E7DFCF !important; }
        .lp-root.lp-light .lp-footer-bottom { border-top-color: #E7DFCF !important; }
        .lp-root.lp-light .lp-footer-loc-box { background: rgba(201,151,26,0.07) !important; border-color: rgba(201,151,26,0.22) !important; }
        .lp-root.lp-light .lp-footer-social-btn { background: rgba(0,0,0,0.05) !important; border-color: #E7DFCF !important; color: #6B7280 !important; }
        .lp-root.lp-light .lp-footer-social-btn:hover { background: rgba(201,151,26,0.08) !important; border-color: #C9971A !important; color: #C9971A !important; }
        .lp-root.lp-light .lp-footer-col-heading { color: #C9971A !important; }
        .lp-root.lp-light .lp-footer-brand-sub { color: #C9971A !important; opacity: 0.9; }

        /* ── Mobile menu ── */
        .lp-root.lp-light .lp-mobile-menu {
          background: #FAF8F4 !important;
        }

        /* ── Dark/Light toggle button ── */
        .lp-theme-toggle {
          display: flex; align-items: center; justify-content: center;
          width: 36px; height: 36px;
          border-radius: 9px;
          border: 1px solid rgba(255,255,255,0.12);
          background: rgba(255,255,255,0.05);
          cursor: pointer;
          font-size: 16px;
          transition: all 0.22s;
          flex-shrink: 0;
        }
        .lp-theme-toggle:hover {
          border-color: rgba(212,168,67,0.45);
          background: rgba(212,168,67,0.09);
          transform: rotate(12deg) scale(1.08);
        }
        .lp-root.lp-light .lp-theme-toggle {
          border-color: rgba(0,0,0,0.14);
          background: rgba(0,0,0,0.04);
        }
        .lp-root.lp-light .lp-theme-toggle:hover {
          border-color: rgba(180,140,40,0.5);
          background: rgba(180,140,40,0.1);
        }

        /* ── Keyframes ── */
        @keyframes kfx-fadeUp  { from{opacity:0;transform:translateY(28px);} to{opacity:1;transform:translateY(0);} }
        @keyframes kfx-fadeIn  { from{opacity:0;} to{opacity:1;} }
        @keyframes kfx-shimmer { 0%{background-position:200% center;} 100%{background-position:-200% center;} }
        @keyframes kfx-float   { 0%,100%{transform:translateY(0);} 50%{transform:translateY(-8px);} }
        @keyframes kfx-pulse   { 0%,100%{opacity:1;} 50%{opacity:0.3;} }
        @keyframes kfx-glow    { 0%,100%{box-shadow:0 0 20px rgba(212,168,67,0.3);} 50%{box-shadow:0 0 40px rgba(212,168,67,0.6);} }
        @keyframes kfx-tradeIn { from{opacity:0;transform:translateX(-8px);} to{opacity:1;transform:translateX(0);} }
        @keyframes kfx-blink   { 0%,100%{opacity:1;} 50%{opacity:0;} }
        @keyframes kfx-ticker  { from{transform:translateX(0);} to{transform:translateX(-50%);} }
        @keyframes kfx-scanline{ from{transform:translateY(-100%);} to{transform:translateY(200vh);} }
        @keyframes kfx-dropIn  { from{opacity:0;transform:translateY(-8px) translateX(-50%) scale(0.97);} to{opacity:1;transform:translateY(0) translateX(-50%) scale(1);} }
        @keyframes kfx-spin    { to{transform:rotate(360deg);} }

        /* ── Shared classes ── */
        .lp-section  { padding:96px 24px; }
        .lp-container{ max-width:1160px; margin:0 auto; padding:0 4px; }
        @media(max-width:760px) { .lp-container { padding:0 8px; } }
        .lp-label {
          display:inline-flex; align-items:center; gap:8px;
          font-family:var(--mono); font-size:10px; font-weight:500;
          letter-spacing:0.18em; text-transform:uppercase;
          color:rgba(212,168,67,0.7); margin-bottom:20px;
        }
        .lp-label::before{ content:''; width:20px; height:1px; background:rgba(212,168,67,0.4); }
        .lp-h2 {
          font-size:clamp(34px,4.5vw,56px); font-weight:800;
          letter-spacing:-0.025em; line-height:1.05; color:var(--text);
        }

        /* ── Buttons ── */
        .lp-btn-primary {
          display:inline-flex; align-items:center; gap:10px;
          padding:15px 32px; border:none; border-radius:var(--r);
          background:linear-gradient(135deg,var(--gold) 0%,var(--gold-dk) 100%);
          color:#080808; font-family:var(--mono); font-size:12px;
          font-weight:700; letter-spacing:0.08em; text-transform:uppercase;
          cursor:pointer; transition:all 0.25s;
          box-shadow:0 4px 24px rgba(212,168,67,0.3), 0 0 0 1px rgba(212,168,67,0.2);
        }
        .lp-btn-primary:hover { transform:translateY(-2px); box-shadow:0 12px 48px rgba(212,168,67,0.45), 0 0 0 1px rgba(212,168,67,0.35); background:linear-gradient(135deg,var(--gold-lt) 0%,var(--gold) 100%); }
        .lp-root.lp-light .lp-btn-primary {
          background:linear-gradient(135deg,#C9971A 0%,#8B6512 100%);
          color:#FAF8F4;
          box-shadow:0 4px 20px rgba(139,101,18,0.28), 0 1px 0 rgba(255,255,255,0.15) inset;
        }
        .lp-root.lp-light .lp-btn-primary:hover {
          background:linear-gradient(135deg,#B8870F 0%,#7A5510 100%);
          box-shadow:0 8px 32px rgba(139,101,18,0.40), 0 1px 0 rgba(255,255,255,0.10) inset;
        }
        .lp-btn-ghost {
          display:inline-flex; align-items:center; gap:8px;
          padding:15px 28px; border:1px solid rgba(255,255,255,0.12); border-radius:var(--r);
          background:transparent; color:var(--text); font-family:var(--mono);
          font-size:12px; font-weight:600; letter-spacing:0.08em;
          text-transform:uppercase; cursor:pointer; transition:all 0.25s;
        }
        .lp-btn-ghost:hover { border-color:rgba(212,168,67,0.4); color:var(--gold); background:rgba(212,168,67,0.05); }
        .lp-root.lp-light .lp-btn-ghost {
          border-color: #E7DFCF;
          color: #2D2D2D;
          background: transparent;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
        }
        .lp-root.lp-light .lp-btn-ghost:hover {
          border-color: #C9971A;
          color: #C9971A;
          background: rgba(201,151,26,0.05);
          box-shadow: 0 2px 8px rgba(139,101,18,0.12);
        }

        .lp-nav-link {
          background:none; border:none; cursor:pointer;
          font-family:var(--sans); font-size:13.5px; font-weight:500;
          color:var(--muted); padding:4px 0; transition:color 0.2s; position:relative;
        }
        .lp-nav-link:hover { color:var(--text); }
        .lp-nav-link.has-dropdown { display:flex; align-items:center; gap:4px; }
        .lp-nav-link.has-dropdown.open { color:var(--text); }
        .lp-nav-link-active {
          color:var(--text) !important;
        }
        .lp-nav-link-active::after {
          content:''; position:absolute; bottom:-6px; left:0; right:0;
          height:2px; background:var(--gold); border-radius:2px;
          opacity:0.8;
        }

        /* ── Sub-nav breadcrumb ── */
        .lp-subnav-scroll {
          display:flex; align-items:center; gap:2px;
          overflow-x:auto; scroll-behavior:smooth;
          scrollbar-width:none; -ms-overflow-style:none; -webkit-overflow-scrolling:touch;
        }
        .lp-subnav-scroll::-webkit-scrollbar { display:none; }
        .lp-subnav-btn {
          padding:5px 10px; border-radius:7px; cursor:pointer;
          display:flex; align-items:center; gap:5px; flex-shrink:0;
          white-space:nowrap; font-family:'Bricolage Grotesque',sans-serif;
          background:transparent; border:1px solid transparent;
          transition:background 0.18s, border-color 0.18s;
        }
        .lp-subnav-btn:hover {
          background: rgba(212,168,67,0.09);
          border-color: rgba(212,168,67,0.25);
        }
        .lp-subnav-btn.active {
          background: rgba(212,168,67,0.12);
          border-color: rgba(212,168,67,0.32);
        }
        .lp-subnav-label {
          font-size:11.5px; font-weight:500; color:var(--muted);
          transition:color 0.18s;
          pointer-events:none;
        }
        .lp-subnav-btn:hover .lp-subnav-label,
        .lp-subnav-btn.active .lp-subnav-label { color:var(--gold); }
        .lp-subnav-btn.active .lp-subnav-label { font-weight:700; }

        /* light mode subnav */
        .lp-root.lp-light .lp-subnav-btn:hover { background:rgba(201,151,26,0.08); border-color:rgba(201,151,26,0.30); }
        .lp-root.lp-light .lp-subnav-btn.active { background:rgba(201,151,26,0.12); border-color:rgba(201,151,26,0.40); }
        .lp-root.lp-light .lp-subnav-label { color:#4B5563; }
        .lp-root.lp-light .lp-subnav-btn:hover .lp-subnav-label,
        .lp-root.lp-light .lp-subnav-btn.active .lp-subnav-label { color:#C9971A; }

        /* ── Feature card ── */
        .lp-feat-card {
          padding:32px 28px; border-radius:10px;
          background:rgba(255,255,255,0.02);
          border:1px solid rgba(255,255,255,0.07);
          transition:all 0.3s; cursor:default; position:relative; overflow:hidden;
        }
        .lp-feat-card::before {
          content:''; position:absolute; inset:0;
          background:linear-gradient(135deg,rgba(212,168,67,0.04),transparent 60%);
          opacity:0; transition:opacity 0.3s;
        }
        .lp-feat-card:hover { border-color:rgba(212,168,67,0.3); transform:translateY(-4px); box-shadow:0 24px 64px rgba(0,0,0,0.5); }
        .lp-feat-card:hover::before { opacity:1; }

        /* ── Testimonial ── */
        .lp-testi-card {
          padding:28px; border-radius:10px;
          background:rgba(255,255,255,0.025);
          border:1px solid rgba(255,255,255,0.07);
          transition:border-color 0.3s;
          display:flex; flex-direction:column; gap:16px;
        }
        .lp-testi-card:hover { border-color:rgba(212,168,67,0.25); }

        /* ── Grids ── */
        .lp-grid-3 { display:grid; grid-template-columns:repeat(3,1fr); gap:20px; }
        .lp-grid-2 { display:grid; grid-template-columns:repeat(2,1fr); gap:28px; }

        /* ── Ticker ── */
        .lp-ticker-wrap { overflow:hidden; position:relative; flex:1; }
        .lp-ticker-wrap::before,.lp-ticker-wrap::after { content:''; position:absolute; top:0; bottom:0; width:80px; z-index:2; }
        .lp-ticker-wrap::before { left:0; background:linear-gradient(to right,var(--bg),transparent); }
        .lp-ticker-wrap::after  { right:0; background:linear-gradient(to left,var(--bg),transparent); }
        .lp-ticker { display:flex; gap:0; animation:kfx-ticker 28s linear infinite; white-space:nowrap; }
        .lp-ticker-item { display:inline-flex; align-items:center; gap:12px; padding:0 32px; font-family:var(--mono); font-size:12px; letter-spacing:0.06em; color:var(--muted2); border-right:1px solid rgba(255,255,255,0.06); flex-shrink:0; }
        .lp-ticker-item .pair { color:rgba(212,168,67,0.6); font-weight:600; }
        .lp-ticker-item .buy  { color:var(--green); }
        .lp-ticker-item .sell { color:var(--red); }

        /* ── Scanline ── */
        .lp-scanline { position:absolute; top:0; left:0; right:0; height:120px; background:linear-gradient(to bottom,transparent,rgba(212,168,67,0.025),transparent); animation:kfx-scanline 8s linear infinite; pointer-events:none; }

        /* ── FAQ ── */
        .lp-faq-item { border-radius:8px; border:1px solid; overflow:hidden; transition:border-color 0.25s; }
        .lp-faq-btn  { width:100%; padding:18px 22px; background:transparent; border:none; cursor:pointer; display:flex; align-items:center; justify-content:space-between; gap:16px; text-align:left; transition:background 0.2s; }
        .lp-faq-btn:hover { background:rgba(255,255,255,0.02); }
        .lp-faq-answer { padding:0 22px 18px; font-size:13.5px; color:var(--muted); line-height:1.8; }

        /* ── CSS hover replacements (replaces all onMouseEnter/Leave JS) ── */
        .lp-dd-input-row { background:transparent; border-color:transparent; transition:background 0.15s,border-color 0.15s; }
        .lp-dd-input-row:hover { background:var(--overlay-4); border-color:var(--overlay-8); }
        .lp-calc-btn:hover { opacity:0.85; }
        .lp-tool-tab { background:var(--overlay-3); border:1px solid var(--overlay-10); transition:border-color 0.15s,background 0.15s; }
        .lp-tool-tab:hover:not(.active) { border-color:var(--overlay-12); background:var(--overlay-5); }
        .lp-tool-tab.active { border-color:var(--gold); background:rgba(212,168,67,0.08); }
        .lp-back-link:hover { color:var(--text) !important; }
        .lp-broker-card { transition:border-color 0.2s,background 0.2s; }
        .lp-broker-card:hover { border-color:rgba(212,168,67,0.32) !important; background:rgba(212,168,67,0.06) !important; }
        .lp-pricing-cta-primary:hover { opacity:0.88; transform:translateY(-1px); }
        .lp-pricing-cta-secondary:hover { transform:translateY(-1px); }
        .lp-pricing-card { transition:transform 0.2s,box-shadow 0.2s,opacity 0.2s; }
        .lp-pricing-card:not(.active):hover { transform:translateY(-2px); opacity:1 !important; }
        .lp-pricing-card.active:hover { transform:translateY(-4px); box-shadow:0 24px 64px rgba(46,204,138,0.12); }
        .lp-nav-signin:hover { color:var(--text) !important; background:var(--overlay-5) !important; }
        .lp-mobile-tool-btn:hover { background:var(--overlay-4); }
        .lp-pain-card { transition:transform 0.2s,border-color 0.2s,box-shadow 0.2s; }
        .lp-pain-card:hover { transform:translateY(-4px); }
        .lp-for-card { transition:transform 0.25s,box-shadow 0.25s; }
        .lp-for-card:hover { transform:translateY(-5px); }
        .lp-step-card { transition:transform 0.2s,border-color 0.2s; }
        .lp-step-card:hover { transform:translateY(-4px); border-color:rgba(212,168,67,0.25) !important; }
        .lp-social-btn:hover { background:rgba(212,168,67,0.1) !important; border-color:rgba(212,168,67,0.35) !important; color:var(--gold) !important; }
        .lp-footer-link:hover { color:var(--text) !important; }
        .lp-testi-card { transition:transform 0.2s,border-color 0.2s; }
        .lp-testi-card:hover { transform:translateY(-3px); }

        /* ── TOOLS SECTION MOBILE ── */
        .lp-tools-tabs {
          display: flex;
          gap: 10px;
          margin-bottom: 32px;
          flex-wrap: wrap;
        }
        @media(max-width:760px) {
          .lp-tools-tabs {
            flex-wrap: nowrap !important;
            overflow-x: auto !important;
            padding-bottom: 8px !important;
            margin: 0 -20px 24px !important;
            padding-left: 20px !important;
            padding-right: 20px !important;
            gap: 8px !important;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
          }
          .lp-tools-tabs::-webkit-scrollbar { display: none; }
          .lp-tools-tabs button {
            flex-shrink: 0 !important;
            padding: 10px 14px !important;
          }
          .lp-tools-tabs button span:last-child { display: none; }
          /* ALL 2-col tool grids stack to single column */
          .lp-tool-grid {
            grid-template-columns: 1fr !important;
            gap: 20px !important;
          }
          /* Drawdown inner sub-grids (win rate / avg win etc) also stack */
          .lp-dd-sub-grid {
            grid-template-columns: 1fr 1fr !important;
          }
          /* Panel content padding */
          .lp-tool-panel-inner { padding: 20px 16px !important; }
          /* ToolPage outer content area */
          .lp-toolpage-content { padding: 24px 16px 48px !important; }
          /* Session clock stays 2-col at this breakpoint */
          .lp-session-grid { grid-template-columns: 1fr 1fr !important; }
        }
        @media(max-width:480px) {
          .lp-tools-tabs { margin: 0 -16px 20px !important; padding-left: 16px !important; }
          /* Session clock goes single col at small phones */
          .lp-session-grid { grid-template-columns: 1fr !important; }
          /* Drawdown sub-grids go single col too */
          .lp-dd-sub-grid { grid-template-columns: 1fr !important; }
        }


        .lp-pricing-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:20px; align-items:start; }

        /* ── Partners grid ── */

        /* —— Partners —— */
        .lp-partners-section { padding:72px 24px; }
        .lp-partners-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:20px; }
        @media(max-width:900px) {
          .lp-partners-grid { grid-template-columns:repeat(2,1fr); gap:14px; }
        }
        @media(max-width:480px) {
          .lp-partners-section { padding:56px 16px; }
          .lp-partners-grid { grid-template-columns:repeat(2,1fr); gap:12px; }
        }

        /* ═══════════════════════════
           RESPONSIVE
        ═══════════════════════════ */
        @media(max-width:1024px) {
          .lp-grid-3 { grid-template-columns:repeat(2,1fr); }
          .lp-pricing-grid { grid-template-columns:repeat(2,1fr) !important; }
        }
        @media(max-width:760px) {
          .lp-section { padding:56px 20px; }
          .lp-grid-3  { grid-template-columns:1fr; }
          .lp-grid-2  { grid-template-columns:1fr; }
          .lp-pricing-grid { grid-template-columns:1fr !important; }
          .lp-hero-btns { flex-direction:column; align-items:stretch; gap:10px !important; margin-top:40px !important; }
          .lp-hero-btns button { justify-content:center; width:100%; padding:16px 24px !important; font-size:13px !important; }
          .lp-hero-mockup { display:none !important; }
          .lp-tab-switcher { flex-wrap:nowrap !important; justify-content:flex-start !important; gap:8px !important; overflow-x:auto !important; padding:0 20px 6px !important; margin:0 -20px 16px !important; }
          .lp-tab-switcher::-webkit-scrollbar { display:none; }
          .lp-nav-desktop { display:none !important; }
          .lp-nav-signin  { display:none !important; }
          .lp-nav-cta     { display:none !important; }
          .lp-hamburger   { display:flex !important; }
          .lp-stats-grid  { grid-template-columns:repeat(2,1fr) !important; }
          .lp-hero-grid   { grid-template-columns:1fr !important; }
          .lp-hero-mockups{ display:none !important; }
          .lp-instrument-strip { display:none !important; }
          .lp-steps-grid  { grid-template-columns:1fr !important; }
          .lp-compare-grid{ grid-template-columns:1fr !important; }
        }
        @media(max-width:480px) {
          .lp-stats-grid { grid-template-columns:1fr 1fr !important; }
          .lp-stats-grid > div { padding:16px 12px !important; }
          .lp-stats-grid > div:nth-child(2n) { border-right:none !important; }
          .lp-stats-grid > div:nth-child(3),
          .lp-stats-grid > div:nth-child(4) { border-bottom:none !important; }
          .lp-section { padding:48px 16px; }
          nav { padding:0 16px !important; }
          .lp-hero-container { padding-top:64px !important; padding-bottom:40px !important; }
          .lp-hero-top-pad { padding-top:88px !important; }
        }

        /* ═══════════════════════════════════════════
           MOBILE POLISH — fills every gap
        ═══════════════════════════════════════════ */

        /* Hero badge wraps gracefully */
        @media(max-width:480px) {
          .lp-hero-badge span:last-child { display:none; }
        }

        /* Hero CTA row stacks */
        @media(max-width:760px) {
          .lp-hero-cta-row {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 10px !important;
          }
          .lp-hero-cta-row button { width: 100% !important; justify-content: center !important; }
        }

        /* Browser mockup tabs — compact on mobile */
        @media(max-width:480px) {
          .lp-tab-switcher button { padding: 8px 12px !important; }
          .lp-tab-switcher button span:last-child { display: none !important; }
        }

        /* Feature grid full-width on mobile */
        @media(max-width:760px) {
          .lp-feat-card { padding: 24px 20px !important; }
        }

        /* Footer grid: 2-col on tablet, 1-col on mobile */
        @media(max-width:900px) {
          .lp-footer-grid { grid-template-columns: 1fr 1fr !important; gap:40px !important; }
          .lp-footer-brand-col { grid-column: 1 / -1 !important; }
        }
        @media(max-width:600px) {
          .lp-footer-grid { grid-template-columns: 1fr 1fr !important; gap:32px !important; }
          .lp-footer-cta-band { flex-direction: column !important; align-items: flex-start !important; gap: 16px !important; }
          .lp-footer-bottom > div > div { flex-direction: column !important; align-items: flex-start !important; gap: 10px !important; }
          .lp-footer-bottom .lp-container > div { flex-direction: column !important; align-items: flex-start !important; }
        }
        @media(max-width:420px) {
          .lp-footer-grid { grid-template-columns: 1fr !important; }
        }

        /* Pricing: single card per row on very narrow screens */
        @media(max-width:420px) {
          .lp-pricing-grid { gap: 14px !important; }
        }

        /* Compare table scrollable */
        @media(max-width:760px) {
          .lp-compare-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
          .lp-compare-grid { min-width: 480px; }
        }

        /* Tool-page header on mobile */
        @media(max-width:760px) {
          .lp-toolpage-header { padding: 72px 16px 20px !important; }
        }

        /* Testimonial cards full-width */
        @media(max-width:480px) {
          .lp-testi-card { padding: 20px 16px !important; }
        }

        /* FAQ padding */
        @media(max-width:480px) {
          .lp-faq-btn { padding: 16px 16px !important; }
          .lp-faq-answer { padding: 0 16px 14px !important; }
        }

        /* Section headings on mobile */
        @media(max-width:760px) {
          .lp-h2 { font-size: clamp(28px,7vw,44px) !important; }
        }

        /* Theme toggle visible always */
        .lp-theme-toggle {
          background: var(--overlay-4);
          border: 1px solid var(--overlay-8);
          border-radius: 8px;
          width: 34px; height: 34px;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; font-size: 14px; flex-shrink: 0;
          transition: background 0.2s, border-color 0.2s;
        }
        .lp-theme-toggle:hover { background: var(--overlay-8); }

        /* Mobile: social proof row wraps well */
        @media(max-width:400px) {
          .lp-hero-social { flex-direction: column !important; align-items: center !important; gap: 8px !important; }
        }

        /* Subnav in mobile tool view */
        @media(max-width:760px) {
          .lp-subnav-scroll { max-width: calc(100vw - 120px); }
        }

        /* Trade mockup table: hide on tiny screens */
        @media(max-width:380px) {
          .lp-trade-mockup-header { display: none !important; }
        }

        /* Instrument strip never overflows */
        @media(max-width:760px) {
          .lp-instrument-strip { display: none !important; }
        }

        /* Stats mini bar in footer */
        @media(max-width:420px) {
          .lp-footer-stats { gap: 16px !important; }
        }

        /* ── FOOTER GRID ── */
        .lp-footer-grid {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 2fr;
          gap: 48px;
          align-items: start;
        }
        .lp-footer-brand-col { grid-column: span 1; }

        @media(max-width:900px) {
          .lp-footer-grid { grid-template-columns: 1fr 1fr; gap:40px; }
          .lp-footer-brand-col { grid-column: span 2; }
        }
        @media(max-width:520px) {
          .lp-footer-grid { grid-template-columns: 1fr; gap:32px; }
          .lp-footer-brand-col { grid-column: span 1; }
        }

        /* ── UTILITY CLASSES (replace repeated inline styles) ── */

        /* Flex helpers */
        .u-flex       { display: flex; }
        .u-flex-col   { display: flex; flex-direction: column; }
        .u-flex-center{ display: flex; align-items: center; justify-content: center; }
        .u-flex-between { display: flex; align-items: center; justify-content: space-between; }
        .u-flex-ai    { display: flex; align-items: center; }
        .u-gap-5  { gap: 5px; }
        .u-gap-6  { gap: 6px; }
        .u-gap-7  { gap: 7px; }
        .u-gap-8  { gap: 8px; }
        .u-gap-10 { gap: 10px; }
        .u-gap-12 { gap: 12px; }
        .u-gap-13 { gap: 13px; }
        .u-gap-14 { gap: 14px; }
        .u-gap-16 { gap: 16px; }
        .u-gap-20 { gap: 20px; }
        .u-gap-24 { gap: 24px; }
        .u-gap-32 { gap: 32px; }

        /* Text helpers */
        .u-text-center { text-align: center; }
        .u-text-label  { font-size:10px; font-weight:700; letter-spacing:0.14em; text-transform:uppercase; font-family:'DM Mono',monospace; color:rgba(212,168,67,0.6); }
        .u-text-label-green { font-size:10px; font-weight:700; letter-spacing:0.14em; text-transform:uppercase; font-family:'DM Mono',monospace; color:rgba(46,204,138,0.6); }
        .u-text-mono   { font-family: 'DM Mono', monospace; }
        .u-text-muted  { font-size:13px; color:var(--muted); line-height:1.75; }
        .u-text-muted-lg { font-size:15px; color:var(--muted); max-width:480px; margin:16px auto 0; line-height:1.75; }
        .u-text-cite   { color:var(--text); font-style:normal; font-weight:600; }
        .u-text-gold   { color:var(--gold); }

        /* Section header block */
        .u-section-hd       { text-align:center; margin-bottom:64px; }
        .u-section-hd-56    { text-align:center; margin-bottom:56px; }
        .u-section-hd-52    { text-align:center; margin-bottom:52px; }

        /* Card shells — most common shapes */
        .u-card-surface { background:var(--surface4); border:1px solid var(--overlay-8); border-radius:18px; overflow:hidden; }
        .u-card-sm      { background:var(--overlay-2); border:1px solid var(--overlay-6); border-radius:12px; overflow:hidden; }
        .u-card-pad     { padding:32px 28px; }

        /* Stat/metric number */
        .u-stat-num { font-size:48px; font-weight:800; color:#2ECC8A; font-family:'DM Mono',monospace; letter-spacing:-0.04em; line-height:1; }

        /* Progress bar track */
        .u-prog-track { height:8px; border-radius:4px; background:var(--overlay-7); overflow:hidden; }

        /* Dot bullet */
        .u-dot { width:7px; height:7px; border-radius:50%; background:#2ECC8A; display:inline-block; animation:kfx-pulse 2s infinite; box-shadow:0 0 6px #2ECC8A; }

        /* Section intro subtitle */
        .u-section-sub { font-size:15px; color:var(--muted); max-width:560px; margin:12px auto 0; line-height:1.75; text-align:center; }

        /* Focus-visible ring (keyboard nav) */
        :focus-visible {
          outline: 2px solid var(--gold);
          outline-offset: 3px;
          border-radius: 4px;
        }
        button:focus-visible, a:focus-visible { outline: 2px solid var(--gold); outline-offset: 3px; }

        /* ── WCAG AA contrast fixes ── */
        /* --text-30 and below on #070707 bg fail AA at body size.          */
        /* Bump the floor so smallest used muted text is at least 4.5:1.    */
        /* On dark bg (#070707), rgba(237,232,222,0.45) ≈ 4.6:1 — AA pass. */
        .lp-root {
          --text-30: rgba(237,232,222,0.45);  /* was 0.30 — fail → 0.45 pass */
          --text-28: rgba(237,232,222,0.45);  /* was 0.28 */
          --text-25: rgba(237,232,222,0.40);  /* was 0.25 — decorative only  */
          --text-22: rgba(237,232,222,0.38);  /* was 0.22 — decorative only  */
          --text-20: rgba(237,232,222,0.35);  /* was 0.20 — decorative only  */
          --text-18: rgba(237,232,222,0.30);  /* was 0.18 — decorative only  */
        }
        /* Footer link token: bump from 0.50 → 0.60 (AA at body size) */
        .lp-root { --footer-link: rgba(237,232,222,0.60); }
        /* Muted body copy token: bump from 0.55 → 0.65 */
        .lp-root { --muted: rgba(237,232,222,0.65); }

      `}</style>

      <div className={`lp-root${darkMode ? '' : ' lp-light'}`}>

        {/* ══ TICKER BAR ══ */}
        <div style={{ height:36, background:'rgba(212,168,67,0.04)', borderBottom:'1px solid rgba(212,168,67,0.08)', display:'flex', alignItems:'center', overflow:'hidden' }}>
          <div className="lp-ticker-wrap">
            <div className="lp-ticker">
              {[
                {pair:'XAUUSD',dir:'BUY', rr:'+3.2R',pnl:'+$320'},
                {pair:'EURUSD',dir:'SELL',rr:'+1.8R',pnl:'+$180'},
                {pair:'GBPJPY',dir:'BUY', rr:'+2.7R',pnl:'+$270'},
                {pair:'USDJPY',dir:'SELL',rr:'-1.0R',pnl:'-$100'},
                {pair:'USDCHF',dir:'BUY', rr:'+1.5R',pnl:'+$150'},
                {pair:'XAUUSD',dir:'BUY', rr:'+3.2R',pnl:'+$320'},
                {pair:'EURUSD',dir:'SELL',rr:'+1.8R',pnl:'+$180'},
                {pair:'GBPJPY',dir:'BUY', rr:'+2.7R',pnl:'+$270'},
                {pair:'USDJPY',dir:'SELL',rr:'-1.0R',pnl:'-$100'},
                {pair:'USDCHF',dir:'BUY', rr:'+1.5R',pnl:'+$150'},
              ].map((t,i) => (
                <span key={i} className="lp-ticker-item">
                  <span className="pair">{t.pair}</span>
                  <span className={t.dir==='BUY'?'buy':'sell'}>{t.dir}</span>
                  <span style={{color:t.pnl.startsWith('+')?'var(--green)':'var(--red)'}}>{t.rr}</span>
                  <span style={{color:t.pnl.startsWith('+')?'var(--green)':'var(--red)'}}>{t.pnl}</span>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* ══ NAVBAR ══ */}
        <nav aria-label="Main navigation" style={{
          position:'sticky', top:0, zIndex:100,
          height:60, display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'0 32px',
          background: scrolled ? 'var(--nav-bg-scrolled)' : 'var(--nav-bg)',
          backdropFilter:'blur(24px)',
          borderBottom:`1px solid ${scrolled?'var(--nav-border-scrolled)':'var(--nav-border)'}`,
          transition:'all 0.3s',
        }}>
          {/* Logo — always clickable back to home */}
          <button onClick={() => { setSelectedTool(null); window.scrollTo({ top:0, behavior:'smooth' }); }} style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0, background:'none', border:'none', cursor:'pointer', padding:0 }}>
            {!logoErr
              ? <img src={darkMode ? kfxLogo : kfxLogoLight} alt="KFX" style={{ width:32, height:32, objectFit:'contain', borderRadius:8, animation:'kfx-glow 4s ease infinite' }} onError={() => setLogoErr(true)} />
              : <div style={{ width:32, height:32, borderRadius:8, background:'linear-gradient(135deg,#D4A843,#7a6020)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:900, color:'#080808', fontFamily:"'DM Mono',monospace", animation:'kfx-glow 4s ease infinite', flexShrink:0 }}>KFX</div>
            }
            <div>
              <span style={{ fontSize:15, fontWeight:700, color:'var(--text)', letterSpacing:'-0.01em', display:'block' }}>KFX Journal</span>
              <span style={{ fontSize:8, color:'var(--text-30)', fontFamily:"'DM Mono',monospace", letterSpacing:'0.1em', textTransform:'uppercase' }}>Trading Intelligence</span>
            </div>
          </button>

          {/* Desktop nav */}
          {!selectedTool ? (
          <div className="lp-nav-desktop u-flex-ai u-gap-8">
            {NAV_ITEMS.map(item => item.dropdown ? (
              <div key={item.label} ref={toolsRef} style={{ position:'relative' }}
              >
                <button
                  className={`lp-nav-link has-dropdown${toolsOpen?' open':''}`}
                  aria-haspopup="true"
                  aria-expanded={toolsOpen}
                  aria-controls="lp-tools-dropdown"
                  onClick={() => setToolsOpen(v => !v)}
                >
                  {item.label}
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition:'transform 0.2s', transform:toolsOpen?'rotate(180deg)':'none', opacity:0.6 }} aria-hidden="true">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
                {toolsOpen && <>
                  {/* Invisible bridge fills the gap between button and dropdown */}
                  <div style={{ position:'absolute', top:'100%', left:'-40%', width:'180%', height:14 }} />
                  <ToolsDropdown onToolSelect={handleToolSelect} onGetStarted={onGetStarted} darkMode={darkMode} />
                </>}
              </div>
            ) : (
              <button key={item.label} className={`lp-nav-link${activeSection===item.id?' lp-nav-link-active':''}`} onClick={() => item.page ? onNavigate(item.page) : scrollTo(item.id)} style={{ padding:'4px 14px', color: activeSection===item.id ? 'var(--text)' : undefined }}>{item.label}</button>
            ))}
          </div>
          ) : (
          /* Tool page: breadcrumb + subnav */
          <div className="lp-nav-desktop" style={{ display:'flex', alignItems:'center', gap:0, overflow:'hidden', flex:1, minWidth:0 }}>
            <span style={{ color:'var(--text-42)', fontSize:12, padding:'0 8px 0 2px', flexShrink:0 }}>/</span>
            <div ref={subnavRef} className="lp-subnav-scroll">
              {TOOLS_ITEMS.map(t => (
                <button
                  key={t.label}
                  className={`lp-subnav-btn${selectedTool === t.label ? ' active' : ''}`}
                  onClick={() => { setSelectedTool(t.label); window.scrollTo({ top:0, behavior:'smooth' }); }}
                >
                  <span style={{ fontSize:13, pointerEvents:'none' }}>{t.icon}</span>
                  <span className="lp-subnav-label">{t.label}</span>
                  {t.badge && <span style={{ fontSize:7, padding:'1px 4px', borderRadius:3, background:'rgba(46,204,138,0.15)', color:'#2ECC8A', fontFamily:"'DM Mono',monospace", pointerEvents:'none' }}>{t.badge}</span>}
                </button>
              ))}
            </div>
          </div>
          )}

          {/* CTA + hamburger */}
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            {/* Theme toggle */}
            <button
              className="lp-theme-toggle"
              onClick={toggleTheme}
              title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              aria-label={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {darkMode ? '☀️' : '🌙'}
            </button>
            {/* Sign In — visually separated from nav links with a left divider */}
            <div style={{ width:1, height:18, background:'var(--overlay-10)', flexShrink:0 }} className="lp-nav-signin" />
            <button className="lp-nav-signin" onClick={onGetStarted} style={{ background:'none', border:'none', cursor:'pointer', fontSize:12, fontWeight:500, color:'var(--footer-link)', fontFamily:"'Bricolage Grotesque',sans-serif", padding:'6px 10px', borderRadius:6, letterSpacing:'0.01em' }}
            >Sign In</button>
            <button className="lp-btn-primary lp-nav-cta" onClick={onGetStarted} style={{ padding:'9px 20px', fontSize:11 }}>
              Start Free
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </button>
            <button
              className="lp-hamburger"
              onClick={() => setMobileMenu(v => !v)}
              aria-label={mobileMenu ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenu}
              aria-controls="lp-mobile-menu"
              style={{ display:'none', background:'var(--overlay-5)', border:'1px solid var(--overlay-10)', borderRadius:8, width:36, height:36, alignItems:'center', justifyContent:'center', cursor:'pointer', color:'var(--text)', flexShrink:0, fontSize:18 }}
            >
              {mobileMenu ? '✕' : '☰'}
            </button>
          </div>
        </nav>

        {/* ══ MOBILE MENU ══ */}
        {mobileMenu && (
          <div id="lp-mobile-menu" role="dialog" aria-modal="true" aria-label="Navigation menu" className="lp-mobile-menu" style={{ position:'fixed', inset:0, zIndex:200, background:'var(--bg)', backdropFilter:'blur(24px)', display:'flex', flexDirection:'column', padding:'20px 24px', animation:'kfx-fadeIn 0.2s ease both', overflowY:'auto' }}>
            
            {/* Header */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:32, paddingBottom:16, borderBottom:'1px solid var(--overlay-7)' }}>
              <div className="u-flex-ai u-gap-8">
                <img src={darkMode ? kfxLogo : kfxLogoLight} alt="KFX" style={{ width:28, height:28, borderRadius:7, objectFit:'contain' }} onError={e=>{e.target.style.display='none';}} />
                <span style={{ fontSize:15, fontWeight:700, color:'var(--text)' }}>KFX Journal</span>
              </div>
              <div className="u-flex-ai u-gap-8">
                <button
                  className="lp-theme-toggle"
                  onClick={toggleTheme}
                  title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                  aria-label={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                >
                  {darkMode ? '☀️' : '🌙'}
                </button>
                <button onClick={() => setMobileMenu(false)} aria-label="Close menu" style={{ background:'var(--overlay-6)', border:'1px solid var(--overlay-10)', borderRadius:8, width:36, height:36, color:'var(--text)', fontSize:18, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
              </div>
            </div>

            {/* Nav items */}
            <div style={{ display:'flex', flexDirection:'column', flex:1 }}>
              {/* Analytics */}
              <button onClick={() => scrollTo('features')} style={{ background:'none', border:'none', textAlign:'left', fontSize:20, fontWeight:700, color:'var(--text)', cursor:'pointer', padding:'12px 0', borderBottom:'1px solid var(--overlay-5)' }}>Analytics</button>

              {/* Tools — click to expand */}
              <div style={{ borderBottom:'1px solid var(--overlay-5)' }}>
                <button
                  onClick={() => setMobileToolsOpen(v => !v)}
                  aria-expanded={mobileToolsOpen}
                  aria-controls="lp-mobile-tools-list"
                  style={{ background:'none', border:'none', textAlign:'left', fontSize:20, fontWeight:700, color: mobileToolsOpen ? 'var(--gold)' : 'var(--text)', cursor:'pointer', padding:'12px 0', width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between' }}
                >
                  Tools
                  <span style={{ fontSize:14, color:'var(--text-45)', transition:'transform 0.25s', display:'inline-block', transform: mobileToolsOpen ? 'rotate(180deg)' : 'none' }}>▾</span>
                </button>
                {mobileToolsOpen && (
                  <div id="lp-mobile-tools-list" style={{ display:'flex', flexDirection:'column', gap:0, paddingLeft:0, paddingBottom:12, animation:'kfx-fadeIn 0.2s ease both' }}>
                    {TOOLS_ITEMS.map(t => (
                      <button key={t.label} onClick={() => handleToolSelect(t.label)} className="lp-mobile-tool-btn" style={{ background:'none', border:'none', textAlign:'left', fontSize:14, color:'var(--text-55)', cursor:'pointer', padding:'11px 12px', display:'flex', alignItems:'center', gap:12, fontFamily:"'Bricolage Grotesque',sans-serif", borderRadius:8 }}
                      >
                        <div style={{ width:32, height:32, borderRadius:8, background:'rgba(212,168,67,0.07)', border:'1px solid rgba(212,168,67,0.14)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:15, flexShrink:0 }}>{t.icon}</div>
                        <div>
                          <div className="u-flex-ai u-gap-7">
                            <span>{t.label}</span>
                            {t.badge && <span style={{ fontSize:8, padding:'1px 5px', borderRadius:4, background:'rgba(46,204,138,0.15)', color:'#2ECC8A', fontFamily:"'DM Mono',monospace" }}>{t.badge}</span>}
                          </div>
                          <div style={{ fontSize:11, color:'var(--text-30)', marginTop:1, fontFamily:"'DM Mono',monospace", letterSpacing:'0.02em' }}>{t.sub}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Pricing, About, FAQ */}
              {[['Pricing','pricing',false],['About','about',true],['FAQ','faq',false]].map(([label,id,isPage]) => (
                <button key={label} onClick={() => { setMobileMenu(false); isPage ? onNavigate(id) : scrollTo(id); }} style={{ background:'none', border:'none', textAlign:'left', fontSize:20, fontWeight:700, color:'var(--text)', cursor:'pointer', padding:'12px 0', borderBottom:'1px solid var(--overlay-5)' }}>{label}</button>
              ))}
            </div>

            {/* CTAs */}
            <div style={{ display:'flex', flexDirection:'column', gap:12, paddingTop:24 }}>
              <button className="lp-btn-ghost" onClick={onGetStarted} style={{ justifyContent:'center', width:'100%' }}>Sign In</button>
              <button className="lp-btn-primary" onClick={onGetStarted} style={{ justifyContent:'center', width:'100%' }}>Start Free →</button>
            </div>
          </div>
        )}

        {/* ══ TOOL PAGE (single tool) or LANDING CONTENT ══ */}
        {selectedTool
          ? <ToolPage toolLabel={selectedTool} onBack={() => { setSelectedTool(null); window.scrollTo({ top:0, behavior:'smooth' }); }} onNavigate={label => { setSelectedTool(label); window.scrollTo({ top:0, behavior:'smooth' }); }} />
          : <main id="main-content" aria-label="Landing page content">

        {/* ══ HERO ══ */}
        <section style={{ position:'relative', overflow:'hidden', paddingBottom:0 }}>

          {/* Background */}
          <div style={{ position:'absolute', inset:0, pointerEvents:'none' }}>
            <div className="lp-hero-grid" style={{ position:'absolute', inset:0, backgroundImage:'linear-gradient(rgba(212,168,67,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(212,168,67,0.025) 1px,transparent 1px)', backgroundSize:'64px 64px' }} />
            <div className="lp-hero-radial" style={{ position:'absolute', top:0, left:'50%', transform:'translateX(-50%)', width:'100%', height:'70%', background:'radial-gradient(ellipse 80% 55% at 50% 0%,rgba(212,168,67,0.10) 0%,transparent 70%)' }} />
            <div className="lp-scanline" />
          </div>

          <div className="lp-container lp-hero-top-pad" style={{ position:'relative', zIndex:1, padding:'100px 24px 0', width:'100%', textAlign:'center' }}>

            {/* Badge */}
            <div style={{ display:'inline-flex', alignItems:'center', gap:8, background:'rgba(46,204,138,0.08)', border:'1px solid rgba(46,204,138,0.2)', borderRadius:6, padding:'6px 16px', marginBottom:32, opacity:heroVisible?1:0, transition:'opacity 0.6s 0.1s', fontFamily:"'DM Mono',monospace" }}>
              <span style={{ width:6, height:6, borderRadius:'50%', background:'var(--green)', animation:'kfx-pulse 2s infinite', boxShadow:'0 0 8px var(--green)', flexShrink:0 }} />
              <span style={{ fontSize:10, color:'rgba(46,204,138,0.9)', fontWeight:500, letterSpacing:'0.12em', textTransform:'uppercase' }}>Free to start · No credit card · Used by FTMO challengers</span>
            </div>

            {/* Headline — 3 lines */}
            <h1 style={{
              fontWeight:900, lineHeight:0.95,
              letterSpacing:'-0.05em',
              opacity:heroVisible?1:0,
              transition:'opacity 0.7s 0.15s, transform 0.7s 0.15s',
              transform:heroVisible?'translateY(0)':'translateY(20px)',
              maxWidth:860, margin:'0 auto 28px',
            }}>
              <span style={{ color:'var(--text)', display:'block', fontSize:'clamp(48px,8.5vw,104px)' }}>Stop guessing</span>
              <span style={{ color:'var(--text)', display:'block', fontSize:'clamp(48px,8.5vw,104px)' }}>why you're losing.</span>
              <span className="lp-hero-gold-line" style={{
                display:'block',
                fontSize:'clamp(48px,8.5vw,104px)',
                background:'linear-gradient(135deg,var(--gold-lt) 0%,var(--gold) 50%,var(--gold-dk) 100%)',
                backgroundSize:'200% auto',
                WebkitBackgroundClip:'text',
                WebkitTextFillColor:'transparent',
                animation:'kfx-shimmer 4s linear infinite',
              }}>
                Start knowing.
              </span>
            </h1>

            {/* Sub */}
            <p style={{
              fontSize:'clamp(16px,1.8vw,18px)', lineHeight:1.8, color:'var(--muted)',
              maxWidth:580, margin:'0 auto 0',
              opacity:heroVisible?1:0,
              transition:'opacity 0.7s 0.25s',
              letterSpacing:'0.005em',
              fontWeight:400,
            }}>
              KFX reads your actual trade history and tells you what's costing you money —
              your worst session, your most broken rule, your hidden edge.
              <span style={{ color:'var(--text)', fontWeight:500 }}> The only platform with a personal AI coach built on Claude.</span>
            </p>

            {/* CTAs */}
            <div className="lp-hero-btns lp-hero-cta-row" style={{ display:'flex', gap:10, justifyContent:'center', marginTop:48, marginBottom:14, opacity:heroVisible?1:0, transition:'opacity 0.7s 0.35s' }}>
              <button className="lp-btn-primary" onClick={onGetStarted} style={{ fontSize:14, padding:'17px 44px' }}>
                Start Free
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
              </button>
              <button className="lp-btn-ghost" onClick={() => scrollTo('howitworks')} style={{ fontSize:14, padding:'17px 32px' }}>See how it works</button>
            </div>
            <div style={{ fontSize:11, color:'var(--text-35)', fontFamily:"'DM Mono',monospace", letterSpacing:'0.08em', marginBottom:30, opacity:heroVisible?1:0, transition:'opacity 0.7s 0.4s' }}>
              No credit card · Free forever
            </div>

            {/* Social proof */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:12, opacity:heroVisible?1:0, transition:'opacity 0.7s 0.45s', flexWrap:'wrap', rowGap:8, marginBottom:40 }}>
              <div style={{ display:'flex', alignItems:'center' }}>
                {['JC','AK','PN','DU','MO'].map((av, idx) => (
                  <div key={av} style={{ width:28, height:28, borderRadius:'50%', background:`hsl(${idx*42+30},50%,28%)`, border:'2px solid var(--bg)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:8, fontWeight:700, color:'#c4a05a', marginLeft:idx>0?-9:0, fontFamily:"'DM Mono',monospace" }}>{av}</div>
                ))}
              </div>
              <div style={{ display:'flex', gap:2 }}>
                {[...Array(5)].map((_,i)=><span key={i} style={{ color:'#D4A843', fontSize:11 }}>★</span>)}
              </div>
              <span style={{ fontSize:11, color:'var(--muted2)', fontFamily:"'DM Mono',monospace" }}>12,000+ trades analyzed</span>
              <span style={{ fontSize:10, color:'rgba(46,204,138,0.65)', fontFamily:"'DM Mono',monospace", letterSpacing:'0.06em' }}>✓ FREE FOREVER · NO CARD</span>
            </div>

            {/* Stats bar */}
            <div className="lp-stats-grid" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', borderRadius:'10px 10px 0 0', overflow:'hidden', border:'1px solid var(--overlay-6)', borderBottom:'none', opacity:heroVisible?1:0, transition:'opacity 0.7s 0.5s' }}>
              {[
                {n:12000, s:'+', label:'Trades Analyzed'},
                {n:97,    s:'%', label:'Rule Accuracy↑'},
                {n:100,   s:'%', label:'Free Forever'},
                {n:4.9,   s:'/5',label:'Trader Rating', dec:1, link:'https://producthunt.com'},
              ].map(({n,s,label,dec,link},i) => (
                <div key={label} style={{ padding:'18px 16px', background: i%2===0?'var(--overlay-2)':'var(--overlay-1)', borderRight:i<3?'1px solid var(--overlay-6)':'none', borderBottom:'1px solid var(--overlay-6)', textAlign:'center' }}>
                  <div style={{ fontSize:'clamp(18px,2.5vw,30px)', fontWeight:800, color:'var(--gold)', letterSpacing:'-0.035em', lineHeight:1, fontFamily:"'DM Mono',monospace" }}>
                    {link
                      ? <a href={link} target="_blank" rel="noopener noreferrer" style={{ color:'var(--gold)', textDecoration:'none' }}><Counter target={n} suffix={s} decimals={dec||0} /></a>
                      : <Counter target={n} suffix={s} decimals={dec||0} />
                    }
                  </div>
                  <div style={{ fontSize:9, color:'var(--muted2)', textTransform:'uppercase', letterSpacing:'0.09em', fontFamily:"'DM Mono',monospace", marginTop:5, fontWeight:500 }}>{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* App mockup with tab switcher */}
          <div style={{ position:'relative', zIndex:1, marginTop:0, padding:'0 16px', opacity:heroVisible?1:0, transition:'opacity 0.9s 0.65s, transform 0.9s 0.65s', transform:heroVisible?'translateY(0)':'translateY(32px)' }}>
            <div style={{ maxWidth:1100, margin:'0 auto', position:'relative' }}>

              {/* Glow */}
              <div style={{ position:'absolute', top:'10%', left:'50%', transform:'translateX(-50%)', width:'80%', height:'60%', background:'radial-gradient(ellipse,rgba(212,168,67,0.08) 0%,transparent 70%)', pointerEvents:'none', zIndex:0 }} />
              <br />
              {/* Tab switcher pills */}
              <div className="lp-tab-switcher" style={{ display:'flex', justifyContent:'center', gap:8, marginBottom:16, position:'relative', zIndex:2, overflowX:'auto', paddingBottom:4, WebkitOverflowScrolling:'touch', scrollbarWidth:'none', msOverflowStyle:'none' }}>
                {[
                  { id:'dashboard', label:'Dashboard',  desc:'Trade calendar & KFX score',      svg:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg> },
                  { id:'Whatifsimulator', label:'What-If Simulator',   desc:'Deep performance breakdown',      svg:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg> },
                  { id:'aicoach',   label:'AI Coach',    desc:'Personalized coaching reports',   svg:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" strokeWidth="1.5"/></svg> },
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    style={{
                      padding:'10px 20px', borderRadius:8, border:'1px solid',
                      borderColor: activeTab===tab.id ? 'rgba(212,168,67,0.5)' : 'var(--overlay-10)',
                      background: activeTab===tab.id ? 'rgba(212,168,67,0.12)' : 'var(--overlay-3)',
                      color: activeTab===tab.id ? 'var(--gold)' : 'var(--muted)',
                      fontSize:12, fontWeight:600, cursor:'pointer',
                      fontFamily:"'Bricolage Grotesque',sans-serif",
                      transition:'all 0.2s',
                      display:'flex', flexDirection:'column', alignItems:'center', gap:3,
                      flexShrink:0, minWidth:0, whiteSpace:'nowrap',
                    }}
                  >
                    <span style={{ display:'flex', alignItems:'center', gap:6, fontSize:12 }}>{tab.svg}{tab.label}</span>
                    <span style={{ fontSize:9, fontFamily:"'DM Mono',monospace", letterSpacing:'0.06em', opacity:0.65, whiteSpace:'nowrap' }}>{tab.desc}</span>
                  </button>
                ))}
              </div>

              {/* Browser chrome */}
              <div style={{ borderRadius:'16px 16px 0 0', border:'1px solid rgba(212,168,67,0.2)', borderBottom:'none', background:'rgba(18,18,16,0.95)', overflow:'hidden', position:'relative', zIndex:1, boxShadow:'0 -8px 64px rgba(0,0,0,0.6), 0 0 0 1px rgba(212,168,67,0.08)' }}>
                {/* Browser bar */}
                <div style={{ padding:'10px 16px', background:'var(--overlay-3)', borderBottom:'1px solid var(--overlay-6)', display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ display:'flex', gap:5 }}>
                    {['#FF5F57','#FEBC2E','#28C840'].map(col => <div key={col} style={{ width:10, height:10, borderRadius:'50%', background:col }} />)}
                  </div>
                  <div style={{ flex:1, background:'var(--overlay-5)', borderRadius:6, padding:'4px 12px', display:'flex', alignItems:'center', gap:8, maxWidth:360, margin:'0 auto' }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--overlay-10)" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    <span style={{ fontSize:10, color:'var(--text-30)', fontFamily:"'DM Mono',monospace", letterSpacing:'0.04em' }}>
                      app.kfxjournal.com/{activeTab==='dashboard'?'dashboard':activeTab==='Whatifsimulator'?'what-if':'ai-coach'}
                    </span>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:6, marginLeft:'auto' }}>
                    <div style={{ width:6, height:6, borderRadius:'50%', background:'var(--green)', boxShadow:'0 0 6px var(--green)', animation:'kfx-pulse 2s infinite' }} />
                    <span style={{ fontSize:9, color:'var(--green)', fontFamily:"'DM Mono',monospace", letterSpacing:'0.08em' }}>LIVE</span>
                  </div>
                </div>

                {/* Screenshot — switches with fade */}
                <div style={{ position:'relative', overflow:'hidden' }}>
                  <img
                    key={activeTab}
                    src={activeTab==='dashboard' ? imgDashboard : activeTab==='Whatifsimulator' ? imgAnalytics : imgAiCoach}
                    alt={`KFX Journal ${activeTab}`}
                    decoding="async"
                    style={{ width:'100%', display:'block', maxHeight:540, objectFit:'cover', objectPosition:'top', animation:'kfx-fadeIn 0.35s ease both' }}
                  />
                  {/* Bottom fade */}
                  <div style={{ position:'absolute', bottom:0, left:0, right:0, height:140, background:'linear-gradient(to bottom,transparent,var(--bg))', pointerEvents:'none' }} />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ══ WHAT YOU'LL SEE INSIDE ══ */}
        <LazySection>
        <section style={{ padding:'0', background:'rgba(212,168,67,0.02)', borderTop:'1px solid rgba(212,168,67,0.08)', borderBottom:'1px solid rgba(212,168,67,0.08)', marginTop:0 }}>
          <div className="lp-container">
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', borderLeft:'1px solid var(--overlay-6)' }}>
              {[
                { label:'Worst trading day', value:'Tuesday 3pm', sub:'You lose 68% of weeks here', color:'#E05555', icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#E05555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg> },
                { label:'Most profitable setup', value:'London BoS', sub:'Avg +2.4R across 31 trades', color:'#2ECC8A', icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2ECC8A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg> },
                { label:'Rule you break most', value:'No revenge entry', sub:'Costs you $340/month avg', color:'#E09B35', icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#E09B35" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg> },
                { label:'Your actual win rate', value:'61% (not 48%)', sub:'When you follow your rules', color:'#5B8FE8', icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5B8FE8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg> },
              ].map((item, i) => (
                <div key={item.label} style={{ padding:'28px 24px', borderRight:'1px solid var(--overlay-6)', borderBottom:'none' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
                    {item.icon}
                    <span style={{ fontSize:10, fontFamily:"'DM Mono',monospace", color:'var(--muted2)', letterSpacing:'0.08em', textTransform:'uppercase' }}>{item.label}</span>
                  </div>
                  <div style={{ fontSize:20, fontWeight:800, color:item.color, fontFamily:"'DM Mono',monospace", letterSpacing:'-0.03em', marginBottom:6 }}>{item.value}</div>
                  <div style={{ fontSize:12, color:'var(--muted)', lineHeight:1.5 }}>{item.sub}</div>
                </div>
              ))}
            </div>
            <div style={{ textAlign:'center', padding:'14px 0', borderTop:'1px solid var(--overlay-5)' }}>
              <span style={{ fontSize:11, color:'var(--text-30)', fontFamily:"'DM Mono',monospace", letterSpacing:'0.08em' }}>↑ REAL INSIGHTS FROM YOUR TRADE HISTORY · GENERATED IN SECONDS AFTER IMPORT</span>
            </div>
          </div>
        </section>
        </LazySection>

        {/* ══ PAIN POINTS ══ */}
        <LazySection>
        <section style={{ padding:'80px 0 96px' }}>
          <div className="lp-container">
            <Reveal>
              <div className="u-section-hd-52">
                <div className="lp-label">Sound familiar?</div>
                <h2 className="lp-h2" >Every losing trader says the same things</h2>
                <p style={{ fontSize:15, color:'var(--muted)', maxWidth:480, margin:'14px auto 0', lineHeight:1.75 }}>
                  These aren't excuses. They're symptoms of trading without data. KFX turns each one into a specific, fixable insight.
                </p>
              </div>
            </Reveal>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16 }} className="lp-grid-3">
              {[
                {
                  pain: '"I was doing fine, then I gave it all back in one week."',
                  insight: 'Your analytics will show you the exact session, pair, and emotional state that triggers your blowup weeks. It\'s always the same pattern.',
                  icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#E05555" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
                  color: '#E05555',
                },
                {
                  pain: '"I know my strategy works — I just can\'t seem to follow it consistently."',
                  insight: 'Execution grading scores every entry A/B/C. Within a month you\'ll see exactly which rule you break most and what it\'s costing you in dollars.',
                  icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#E09B35" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
                  color: '#E09B35',
                },
                {
                  pain: '"I passed a prop firm challenge demo — but I keep failing the real funded account."',
                  insight: 'The AI identifies the psychological shift between demo and funded. Usually it\'s position sizing or session timing. It will tell you specifically.',
                  icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5B8FE8" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>,
                  color: '#5B8FE8',
                },
                {
                  pain: '"I have no idea which of my setups actually makes money long-term."',
                  insight: 'Deep analytics breaks down P&L by setup, session, pair, and day of week. Your actual edge — not what you think it is — becomes visible in days.',
                  icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2ECC8A" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
                  color: '#2ECC8A',
                },
                {
                  pain: '"I revenge trade. I know I do it. I just can\'t stop."',
                  insight: 'The AI flags revenge trading patterns automatically — it sees the trade sequence, the timing, the loss that preceded it. Awareness is the first fix.',
                  icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#D4A843" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>,
                  color: '#D4A843',
                },
                {
                  pain: '"I journal for a week, then I stop. Nothing actually changes."',
                  insight: 'KFX auto-generates your analytics whether you write notes or not. Import your MT4/MT5 history and the insights are instant — no discipline required to start.',
                  icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9B8FE8" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
                  color: '#9B8FE8',
                },
              ].map((item, i) => (
                <Reveal key={i} delay={i * 60}>
                  <div style={{
                    padding:'28px 24px', borderRadius:14,
                    background:'var(--overlay-2)',
                    border:`1px solid ${item.color}22`,
                    height:'100%', display:'flex', flexDirection:'column', gap:16,
                    transition:'transform 0.3s, border-color 0.3s, box-shadow 0.3s',
                  }}
                  >
                    <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
                      <div style={{ width:36, height:36, borderRadius:8, background:`${item.color}11`, border:`1px solid ${item.color}33`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        {item.icon}
                      </div>
                      <p style={{ fontSize:13.5, color:'var(--text-70)', lineHeight:1.7, fontStyle:'italic', fontWeight:500 }}>{item.pain}</p>
                    </div>
                    <div style={{ paddingTop:12, borderTop:`1px solid ${item.color}18` }}>
                      <div style={{ fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:'0.1em', color:item.color, marginBottom:8, textTransform:'uppercase', fontWeight:600 }}>What KFX shows you</div>
                      <p className="u-text-muted">{item.insight}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
        </LazySection>

        <div className="lp-section-divider" />

        {/* ══ WHO IS THIS FOR ══ */}
        <LazySection>
        <section style={{ padding:'96px 0', background:'rgba(255,255,255,0.012)' }}>
          <div className="lp-container">
            <Reveal>
              <div className="u-section-hd">
                <div className="lp-label">Find yourself</div>
                <h2 className="lp-h2">Built for every stage of the trading journey</h2>
                <p className="u-text-muted-lg">
                  Whether you placed your first trade last month or you're chasing a funded account — KFX gives you exactly what you need right now.
                </p>
              </div>
            </Reveal>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:20 }} className="lp-grid-3">
              {[
                {
                  tag: 'New Trader',
                  tagColor: '#2ECC8A',
                  headline: 'Start with clarity, not confusion',
                  desc: "You're still figuring out what works. Most beginners spend 2 years losing money before they realise they have no idea which setups are actually profitable. KFX shows you in weeks, not years.",
                  bullets: [
                    'Understand your P&L at a glance — no spreadsheets',
                    'Learn what a real edge looks like with your own data',
                    'AI coach explains your results in plain language',
                    'Free forever — start with zero risk',
                  ],
                  cta: 'Start for free',
                  icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2ECC8A" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>,
                },
                {
                  tag: 'Active Trader',
                  tagColor: '#D4A843',
                  headline: 'Stop repeating the same mistakes',
                  desc: "You have a strategy. You have experience. But something keeps costing you — a session, a pair, an emotional pattern you can't pin down. KFX finds it for you and names it specifically.",
                  bullets: [
                    'Pattern detection across hundreds of trades',
                    'Session heatmap reveals your peak and worst hours',
                    'What-If simulator shows the cost of every rule break',
                    'Weekly AI performance summary sent automatically',
                  ],
                  cta: 'Find your leaks',
                  icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#D4A843" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" strokeWidth="1.5"/></svg>,
                  highlight: true,
                },
                {
                  tag: 'Prop Firm Challenger',
                  tagColor: '#5B8FE8',
                  headline: 'Pass your challenge. Keep your account.',
                  desc: "The difference between failing and passing an FTMO challenge is almost never strategy — it's discipline under pressure. KFX tracks your rule adherence, drawdown, and emotional patterns in real time.",
                  bullets: [
                    'Prop firm tracker: drawdown, daily limits, targets',
                    'Execution grade on every entry — A/B/C scoring',
                    'AI identifies your funded-vs-demo performance gap',
                    'What-If: see your month with perfect rule adherence',
                  ],
                  cta: 'Track your challenge',
                  icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#5B8FE8" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2z"/></svg>,
                },
              ].map((card, i) => (
                <Reveal key={card.tag} delay={i * 80}>
                  <div style={{
                    padding:'32px 28px', borderRadius:16, height:'100%', display:'flex', flexDirection:'column', gap:20,
                    background: card.highlight ? `linear-gradient(160deg, ${card.tagColor}0D 0%, ${card.tagColor}05 100%)` : 'var(--overlay-2)',
                    border: card.highlight ? `1px solid ${card.tagColor}50` : `1px solid var(--overlay-7)`,
                    position:'relative', overflow:'hidden',
                    transition:'transform 0.3s, box-shadow 0.3s',
                  }}
                  >
                    {card.highlight && <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:`linear-gradient(90deg,transparent,${card.tagColor},transparent)` }} />}
                    <div>
                      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
                        <div style={{ width:40, height:40, borderRadius:10, background:`${card.tagColor}12`, border:`1px solid ${card.tagColor}30`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                          {card.icon}
                        </div>
                        <span style={{ fontSize:10, fontWeight:700, color:card.tagColor, letterSpacing:'0.12em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace" }}>{card.tag}</span>
                      </div>
                      <h3 style={{ fontSize:18, fontWeight:800, color:'var(--text)', letterSpacing:'-0.02em', lineHeight:1.25, marginBottom:12 }}>{card.headline}</h3>
                      <p style={{ fontSize:13.5, color:'var(--muted)', lineHeight:1.8 }}>{card.desc}</p>
                    </div>
                    <div className="u-flex-col u-gap-10">
                      {card.bullets.map(b => (
                        <div key={b} style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={card.tagColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0, marginTop:3 }} aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
                          <span style={{ fontSize:13, color:'var(--text-70)', lineHeight:1.6 }}>{b}</span>
                        </div>
                      ))}
                    </div>
                    <button onClick={onGetStarted} className="lp-for-cta" style={{ marginTop:'auto', padding:'13px 20px', borderRadius:10, background:`${card.tagColor}14`, border:`1px solid ${card.tagColor}35`, color:card.tagColor, fontFamily:"'DM Mono',monospace", fontSize:11, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', cursor:'pointer' }}
                    >{card.cta} →</button>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
        </LazySection>

        <div className="lp-section-divider" />

        {/* ══ HOW IT WORKS ══ */}
        <LazySection>
        <section id="howitworks" className="lp-section">
          <div className="lp-container">
            <Reveal>
              <div style={{ textAlign:'center', marginBottom:72 }}>
                <div className="lp-label">Simple process</div>
                <h2 className="lp-h2">From trade to insight in seconds</h2>
              </div>
            </Reveal>
            <div className="lp-steps-grid" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:20 }}>
              {[
                {n:'01', title:'Create your account',   desc:'Sign up in 30 seconds. No credit card, no trial — free forever.',            iconPath:<path d="M5 12l5 5L20 7" stroke="var(--gold)" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>},
                {n:'02', title:'Log your trades',        desc:'Add trades manually or import your full MT4/MT5 history with one click.',    iconPath:<><line x1="12" y1="5" x2="12" y2="19" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round"/><line x1="5" y1="12" x2="19" y2="12" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round"/></>},
                {n:'03', title:'Review your analytics',  desc:"See your win rate, session heatmap, equity curve, and rule adherence score.", iconPath:<><line x1="18" y1="20" x2="18" y2="10" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round"/><line x1="12" y1="20" x2="12" y2="4" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round"/><line x1="6" y1="20" x2="6" y2="14" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round"/></>},
                {n:'04', title:'Talk to your AI coach',  desc:"Ask the AI anything. It reads your history, not a script.",                   iconPath:<><circle cx="12" cy="12" r="3" stroke="var(--gold)" fill="none" strokeWidth="2"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" stroke="var(--gold)" strokeWidth="1.75" strokeLinecap="round"/></>},
              ].map(({n,title,desc,iconPath},i) => (
                <Reveal key={n} delay={i*80}>
                  <div className="lp-step-card" style={{ position:'relative', padding:'28px 24px', borderRadius:16, background:'var(--overlay-2)', border:'1px solid var(--overlay-7)', height:'100%' }}
                  >
                    <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:'linear-gradient(90deg,var(--gold),transparent)', borderRadius:'16px 16px 0 0', opacity:0.6 }} />
                    <div style={{ fontSize:10, fontFamily:"'DM Mono',monospace", fontWeight:600, color:'rgba(212,168,67,0.5)', letterSpacing:'0.15em', marginBottom:16 }}>{n}</div>
                    <div style={{ width:36, height:36, borderRadius:8, background:'rgba(212,168,67,0.08)', border:'1px solid rgba(212,168,67,0.15)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:16 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">{iconPath}</svg>
                    </div>
                    <div style={{ fontSize:15, fontWeight:700, color:'var(--text)', marginBottom:10, letterSpacing:'-0.01em' }}>{title}</div>
                    <div className="u-text-muted">{desc}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
        </LazySection>

        {/* ══ AI COACH ══ */}
        <LazySection>
        <section style={{ padding:'100px 0', background:'rgba(255,255,255,0.015)', borderTop:'1px solid var(--border2)', borderBottom:'1px solid var(--border2)' }}>
          <div className="lp-container">
            <div className="lp-grid-2" style={{ alignItems:'center', gap:72 }}>
              <Reveal>
                <div>
                  <div className="lp-label">Powered by Claude AI</div>
                  <h2 className="lp-h2" style={{ marginBottom:24 }}>An AI coach that actually knows your trades</h2>
                  <p style={{ fontSize:16, color:'var(--muted)', lineHeight:1.8, marginBottom:28 }}>
                    Other tools give you averages. KFX gives you answers about <em className="u-text-cite">you specifically</em>. The AI reads your actual trade history and tells you <em className="u-text-cite">your</em> worst session, <em className="u-text-cite">your</em> most costly habit, <em className="u-text-cite">your</em> highest-probability setup.
                  </p>
                  <div className="u-flex-col u-gap-14">
                    {[
                      'Identifies your worst time-of-day to trade',
                      'Flags emotional patterns like revenge trading',
                      'Tells you which setups are actually profitable',
                      'Gives specific, data-backed recommendations',
                    ].map(p => (
                      <div key={p} style={{ display:'flex', gap:12, alignItems:'flex-start' }}>
                        <span style={{ color:'var(--green)', fontSize:15, flexShrink:0, marginTop:1 }}>✓</span>
                        <span style={{ fontSize:14, color:'var(--text-70)', lineHeight:1.6 }}>{p}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
              <Reveal delay={120}>
                <AiMockup />
              </Reveal>
            </div>
          </div>
        </section>
        </LazySection>

        <div className="lp-section-divider" />

        {/* ══ FEATURES ══ */}
        <LazySection>
        <section id="features" className="lp-section">
          <div className="lp-container">
            <Reveal>
              <div style={{ marginBottom:64 }}>
                <div className="lp-label">Everything you need</div>
                <h2 className="lp-h2" style={{ maxWidth:600 }}>Your trading data, finally working for you</h2>
              </div>
            </Reveal>
            <div className="lp-grid-3" style={{ gap:24 }}>
              {FEATURES.map((f,i) => (
                <Reveal key={f.title} delay={i*60}>
                  <div className="lp-feat-card">
                    <div style={{ width:44, height:44, borderRadius:10, background:'rgba(212,168,67,0.08)', border:'1px solid rgba(212,168,67,0.15)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:16, flexShrink:0 }}>
                      <FeatureIcon name={f.icon} />
                    </div>
                    <div style={{ fontSize:14.5, fontWeight:700, color:'var(--text)', marginBottom:10, letterSpacing:'-0.01em' }}>{f.title}</div>
                    <div className="u-text-muted">{f.desc}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
        </LazySection>

        {/* ══ PRICING ══ */}
        <LazySection>
        <PricingSection onGetStarted={onGetStarted} onGoToPricing={onGoToPricing} />
        </LazySection>

        {/* ══ COMPARISON ══ */}
        <LazySection>
        <section id="compare" style={{ padding:'100px 0' }}>
          <div className="lp-container" style={{ maxWidth:800 }}>
            <Reveal>
              <div className="u-section-hd-56">
                <div className="lp-label">How we compare</div>
                <h2 className="lp-h2">Why traders choose KFX Journal</h2>
                <p style={{ fontSize:15, color:'var(--muted)', marginTop:16, lineHeight:1.7 }}>AI coaching that reads your actual data — free to start, at a fraction of the cost.</p>
              </div>
            </Reveal>
            <Reveal delay={80}>
              <div className="lp-compare-wrap" style={{ borderRadius:16, border:'1px solid rgba(212,168,67,0.15)', overflow:'hidden', overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
                <ComparisonTable />
              </div>
              <p style={{ textAlign:'center', fontSize:12, color:'var(--muted2)', marginTop:18, fontFamily:"'DM Mono',monospace" }}>
                Compared against Tradervue, TraderSync, Edgewonk, and TradeZella
              </p>
            </Reveal>
          </div>
        </section>
        </LazySection>

        <div className="lp-section-divider" />

        {/* ══ TESTIMONIALS ══ */}
        <LazySection>
        <section id="testimonials" style={{ padding:'100px 0', background:'rgba(255,255,255,0.015)', borderTop:'1px solid var(--border2)', borderBottom:'1px solid var(--border2)' }}>
          <div className="lp-container">
            <Reveal>
              <div className="u-section-hd">
                <div className="lp-label">Trader stories</div>
                <h2 className="lp-h2">From traders who found their edge</h2>
              </div>
            </Reveal>
            <div className="lp-grid-3" style={{ gap:20 }}>
              {TESTIMONIALS.map((t,i) => (
                <Reveal key={t.name} delay={i*70}>
                  <div className="lp-testi-card">
                    <div style={{ fontSize:32, color:'rgba(212,168,67,0.2)', lineHeight:1, marginBottom:-8, fontFamily:"Georgia,serif" }}>"</div>
                    <div style={{ display:'flex', gap:3 }}>
                      {[...Array(5)].map((_,j)=><span key={j} style={{ color:'#D4A843', fontSize:12 }}>★</span>)}
                    </div>
                    <p style={{ fontSize:13.5, color:'var(--text-55)', lineHeight:1.8, flex:1 }}>"{t.text}"</p>
                    <div style={{ display:'flex', alignItems:'center', gap:10, paddingTop:4, borderTop:'1px solid var(--overlay-5)' }}>
                      <div style={{ width:42, height:42, borderRadius:'50%', background:`linear-gradient(135deg,hsl(${i*42+30},50%,30%),hsl(${i*42+60},40%,20%))`, border:'1.5px solid rgba(212,168,67,0.25)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:800, color:'var(--gold)', fontFamily:"'DM Mono',monospace", flexShrink:0, boxShadow:'0 4px 12px rgba(0,0,0,0.3)' }}>{t.avatar}</div>
                      <div>
                        <div style={{ fontSize:13, fontWeight:700, color:'var(--text)' }}>{t.name}</div>
                        <div style={{ fontSize:10, color:'var(--muted2)', fontFamily:"'DM Mono',monospace" }}>{t.role}</div>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            {/* ── Built by a trader ── */}
            <Reveal delay={200}>
              <div style={{ marginTop:72, padding:'48px', borderRadius:20, background:'linear-gradient(135deg,rgba(212,168,67,0.06) 0%,rgba(212,168,67,0.02) 100%)', border:'1px solid rgba(212,168,67,0.18)', position:'relative', overflow:'hidden' }}>
                <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:'linear-gradient(90deg,transparent,rgba(212,168,67,0.5),transparent)' }} />
                <div style={{ position:'absolute', top:0, right:0, width:200, height:200, background:'radial-gradient(circle,rgba(212,168,67,0.05) 0%,transparent 70%)', pointerEvents:'none' }} />
                <div style={{ display:'flex', gap:32, alignItems:'center', flexWrap:'wrap' }}>
                  <div style={{ width:72, height:72, borderRadius:'50%', background:'linear-gradient(135deg,#8a6820,#D4A843)', border:'2px solid rgba(212,168,67,0.4)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, fontWeight:900, color:'#080808', fontFamily:"'DM Mono',monospace", flexShrink:0, boxShadow:'0 8px 24px rgba(212,168,67,0.2)' }}>Prino</div>
                  <div style={{ flex:1, minWidth:260 }}>
                    <div style={{ fontSize:10, fontFamily:"'DM Mono',monospace", color:'rgba(212,168,67,0.7)', letterSpacing:'0.12em', textTransform:'uppercase', marginBottom:12 }}>A note from the founder</div>
                    <p style={{ fontSize:17, color:'var(--text-80)', lineHeight:1.8, marginBottom:16, fontWeight:500 }}>
                      "I built KFX because I kept losing money for reasons I couldn't articulate. I had the setups. I had the strategy. What I didn't have was a mirror — something that could show me <em className="u-text-gold u-text-cite" style={{ fontStyle:'normal' }}>my</em> patterns, <em className="u-text-gold u-text-cite" style={{ fontStyle:'normal' }}>my</em> habits, <em className="u-text-gold u-text-cite" style={{ fontStyle:'normal' }}>my</em> actual edge. This is that mirror."
                    </p>
                    <div style={{ display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
                      <div>
                        <div style={{ fontSize:14, fontWeight:700, color:'var(--text)' }}>Prince Honore. known as Prino</div>
                        <div style={{ fontSize:11, color:'var(--muted2)', fontFamily:"'DM Mono',monospace", marginTop:2 }}>Founder · KFX Journal · Forex Trader</div>
                      </div>
                      <div style={{ height:32, width:1, background:'var(--overlay-10)' }} />
                      <div style={{ fontSize:12, color:'var(--muted)', fontStyle:'italic', maxWidth:280 }}>
                        Built this after blowing too many funded accounts I shouldn't have lost.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
        </LazySection>

        <div className="lp-section-divider" />

        {/* ══ DATA SECURITY ══ */}
        <LazySection>
        <section style={{ padding:'80px 0', background:'rgba(46,204,138,0.02)', borderTop:'1px solid rgba(46,204,138,0.08)', borderBottom:'1px solid rgba(46,204,138,0.08)' }}>
          <div className="lp-container" style={{ maxWidth:880 }}>
            <Reveal>
              <div className="u-section-hd-52">
                <div className="lp-label" style={{ color:'rgba(46,204,138,0.7)' }}>Privacy & Security</div>
                <h2 className="lp-h2" style={{ fontSize:'clamp(28px,3.5vw,44px)' }}>Your data is yours. Always.</h2>
                <p className="u-text-muted-lg">
                  Your trade history is deeply personal financial data. Here's exactly how we handle it.
                </p>
              </div>
            </Reveal>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:20 }} className="lp-grid-3">
              {[
                { title:'End-to-end encryption', body:'All data is encrypted in transit (TLS 1.3) and at rest (AES-256). Your trades are never transmitted in plaintext.' },
                { title:'AI never trains on your data', body:'The AI coach reads your data to generate your report and does not store or use it for model training. Your trades are not used to train any AI.' },
                { title:'Full data ownership', body:'Export everything or delete your account and all associated data at any time. No questions, no delays, complete removal within 24 hours.' },
              ].map((item, i) => (
                <Reveal key={item.title} delay={i*80}>
                  <div style={{ padding:'28px 24px', borderRadius:14, background:'var(--overlay-2)', border:'1px solid rgba(46,204,138,0.12)', height:'100%' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2ECC8A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
                      <div style={{ fontSize:14, fontWeight:700, color:'var(--text)', letterSpacing:'-0.01em' }}>{item.title}</div>
                    </div>
                    <div className="u-text-muted">{item.body}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
        </LazySection>

        <div className="lp-section-divider" />

        {/* ══ FAQ ══ */}
        <LazySection>
        <section id="faq" className="lp-section">
          <div className="lp-container" style={{ maxWidth:740 }}>
            <Reveal>
              <div className="u-section-hd-56">
                <div className="lp-label">Got questions?</div>
                <h2 className="lp-h2">Everything you need to know</h2>
              </div>
            </Reveal>
            <div className="u-flex-col u-gap-10">
              {FAQS.map(({q,a},i) => (
                <Reveal key={q} delay={i*50}>
                  <div className="lp-faq-item" style={{ borderColor:activeFaq===i?'rgba(212,168,67,0.28)':'var(--overlay-7)' }}>
                    <button
                      className="lp-faq-btn"
                      onClick={() => setActiveFaq(activeFaq===i?null:i)}
                      aria-expanded={activeFaq===i}
                      aria-controls={`faq-answer-${i}`}
                      id={`faq-btn-${i}`}
                      style={{ background:activeFaq===i?'rgba(212,168,67,0.04)':'transparent' }}
                    >
                      <span style={{ fontSize:14, fontWeight:600, color:activeFaq===i?'var(--gold)':'var(--text)', transition:'color 0.2s', lineHeight:1.5 }}>{q}</span>
                      <span aria-hidden="true" style={{ fontSize:20, color:activeFaq===i?'var(--gold)':'var(--text-25)', transition:'transform 0.25s, color 0.2s', transform:activeFaq===i?'rotate(45deg)':'none', flexShrink:0 }}>+</span>
                    </button>
                    {activeFaq===i && (
                      <div
                        id={`faq-answer-${i}`}
                        role="region"
                        aria-labelledby={`faq-btn-${i}`}
                        className="lp-faq-answer"
                        style={{ animation:'kfx-fadeIn 0.2s ease both' }}
                      >{a}</div>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
        </LazySection>

        {/* ══ FINAL CTA ══ */}
        <LazySection>
        <section style={{ padding:'112px 0' }}>
          <div className="lp-container">
            <Reveal>
              <div style={{ position:'relative', borderRadius:24, padding:'clamp(48px,6vw,88px) clamp(28px,5vw,80px)', textAlign:'center', overflow:'hidden', background:'linear-gradient(135deg,rgba(212,168,67,0.07) 0%,rgba(212,168,67,0.02) 100%)', border:'1px solid rgba(212,168,67,0.2)' }}>
                <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:'70%', height:'160%', background:'radial-gradient(ellipse,rgba(212,168,67,0.06) 0%,transparent 70%)', pointerEvents:'none' }} />
                <div style={{ position:'absolute', top:0, left:0, width:80, height:80, borderTop:'1px solid rgba(212,168,67,0.3)', borderLeft:'1px solid rgba(212,168,67,0.3)', borderRadius:'24px 0 0 0', pointerEvents:'none' }} />
                <div style={{ position:'absolute', bottom:0, right:0, width:80, height:80, borderBottom:'1px solid rgba(212,168,67,0.3)', borderRight:'1px solid rgba(212,168,67,0.3)', borderRadius:'0 0 24px 0', pointerEvents:'none' }} />
                <div style={{ position:'relative', zIndex:1 }}>
                  <img
                    src={darkMode ? kfxLogo : kfxLogoLight}
                    alt="KFX"
                    style={{ width:56, height:56, borderRadius:14, objectFit:'contain', display:'block', margin:'0 auto 20px', animation:'kfx-float 3s ease-in-out infinite', boxShadow:'0 8px 32px rgba(212,168,67,0.35)' }}
                    onError={e=>{e.target.style.display='none';}}
                  />
                  <h2 style={{ fontSize:'clamp(28px,3.5vw,52px)', fontWeight:800, letterSpacing:'-0.025em', color:'var(--text)', marginBottom:16, lineHeight:1.1 }}>
                    Your blind spot is costing you money.<br />
                    <span style={{ background:'linear-gradient(135deg,var(--gold-lt),var(--gold))', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>Find it today. Fix it this week.</span>
                  </h2>
                  <p style={{ fontSize:16, color:'var(--muted)', maxWidth:500, margin:'0 auto 16px', lineHeight:1.8 }}>
                    Every trader on this page has the same question: <em style={{ color:'var(--text-70)', fontStyle:'normal' }}>"Why am I not profitable yet?"</em><br />
                    KFX answers it — with your actual data, not generic advice.
                  </p>
                  {/* Three micro-CTAs for each segment */}
                  <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap', marginBottom:36, marginTop:32 }}>
                    {[
                      { label:'New trader — start free', color:'#2ECC8A' },
                      { label:'Find my leaks', color:'#D4A843' },
                      { label:'Track my prop challenge', color:'#5B8FE8' },
                    ].map(({label, color}) => (
                      <button key={label} onClick={onGetStarted} className="lp-social-btn" style={{ padding:'11px 22px', borderRadius:9, background:`${color}12`, border:`1px solid ${color}35`, color:color, fontFamily:"'DM Mono',monospace", fontSize:10, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', cursor:'pointer' }}
                      >{label} →</button>
                    ))}
                  </div>
                  <button className="lp-btn-primary" onClick={onGetStarted} style={{ fontSize:13, padding:'17px 44px' }}>
                    Start Free — No Card Required
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                  </button>
                  <p style={{ marginTop:18, fontSize:11, color:'var(--muted2)', fontFamily:"'DM Mono',monospace", letterSpacing:'0.08em' }}>FREE FOREVER · NO TRIAL · NO CONTRACTS · CANCEL ANYTIME</p>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
        </LazySection>

        {/* ══ FOOTER ══ */}
        <footer style={{ position:'relative', background:'var(--footer-bg)', borderTop:'1px solid var(--border)', overflow:'hidden' }}>

          {/* Gold glow top edge */}
          <div style={{ position:'absolute', top:0, left:0, right:0, height:1, background:'linear-gradient(90deg,transparent 0%,rgba(212,168,67,0.5) 30%,rgba(212,168,67,0.8) 50%,rgba(212,168,67,0.5) 70%,transparent 100%)', pointerEvents:'none' }} />
          {/* Subtle radial glow */}
          <div style={{ position:'absolute', top:0, left:'50%', transform:'translateX(-50%)', width:'60%', height:300, background:'radial-gradient(ellipse at top,rgba(212,168,67,0.04) 0%,transparent 70%)', pointerEvents:'none' }} />

          {/* ── CTA strip ── */}
          <div className="lp-footer-cta-strip" style={{ borderBottom:'1px solid var(--overlay-5)', padding:'64px 24px' }}>
            <div className="lp-container">
              <div className="lp-footer-cta-band" style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:32, flexWrap:'wrap' }}>
                <div style={{ maxWidth:520 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:'rgba(212,168,67,0.55)', letterSpacing:'0.18em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace", marginBottom:14, display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ width:20, height:1, background:'rgba(212,168,67,0.4)', display:'inline-block' }} />
                    Start today — it's free
                  </div>
                  <h2 style={{ fontSize:'clamp(26px,3.5vw,42px)', fontWeight:800, letterSpacing:'-0.025em', lineHeight:1.1, color:'var(--text)', marginBottom:12 }}>
                    Your edge is in your data.<br />
                    <span style={{ background:'linear-gradient(135deg,#F0C855,#D4A843)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>Start seeing it today.</span>
                  </h2>
                  <p style={{ fontSize:14, color:'var(--text-55)', lineHeight:1.75, maxWidth:420 }}>
                    Join traders who track every trade, uncover their patterns, and get AI coaching — completely free to start.
                  </p>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:12, alignItems:'flex-start' }}>
                  <button className="lp-btn-primary" onClick={onGetStarted} style={{ fontSize:13, padding:'16px 36px', whiteSpace:'nowrap' }}>
                    Start Free
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                  </button>
                  <div style={{ display:'flex', alignItems:'center', gap:8, paddingLeft:4 }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(46,204,138,0.8)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
                    <span style={{ fontSize:11, color:'var(--text-45)', fontFamily:"'DM Mono',monospace", letterSpacing:'0.08em' }}>NO CREDIT CARD · FREE FOREVER</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Main footer grid ── */}
          <div style={{ padding:'72px 24px 56px' }}>
            <div className="lp-container">
              <div className="lp-footer-grid">

                {/* Brand column */}
                <div style={{ gridColumn:'span 1' }} className="lp-footer-brand-col">
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:20 }}>
                    <div style={{ position:'relative' }}>
                      <img src={darkMode ? kfxLogo : kfxLogoLight} alt="KFX" style={{ width:40, height:40, borderRadius:10, objectFit:'contain', display:'block' }} onError={e=>{e.target.style.display='none';}} />
                    </div>
                    <div>
                      <div style={{ fontSize:16, fontWeight:800, color:'var(--text)', letterSpacing:'-0.01em', lineHeight:1.1 }}>KFX Journal</div>
                      <div style={{ fontSize:10, color:'var(--text-30)', fontFamily:"'DM Mono',monospace", letterSpacing:'0.08em', marginTop:2 }}>TRADING INTELLIGENCE</div>
                    </div>
                  </div>

                  <p style={{ fontSize:14, color:'var(--text-55)', lineHeight:1.75, marginBottom:24, maxWidth:280 }}>
                    The trading intelligence platform for serious traders. Track every trade, uncover your patterns, and grow with AI-powered coaching.
                  </p>

                  {/* Stats mini-bar */}
                  <div style={{ display:'flex', gap:24, marginBottom:28, paddingBottom:24, borderBottom:'1px solid var(--overlay-5)' }}>
                    {[['12k+','Trades'],['4.9★','Rating'],['Free','Always']].map(([n,l]) => (
                      <div key={l}>
                        <div style={{ fontSize:18, fontWeight:800, color:'var(--gold)', fontFamily:"'DM Mono',monospace", letterSpacing:'-0.02em', lineHeight:1 }}>{n}</div>
                        <div style={{ fontSize:10, color:'var(--text-45)', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.08em', marginTop:4 }}>{l}</div>
                      </div>
                    ))}
                  </div>

                  {/* Social icons */}
                  <div style={{ display:'flex', gap:8 }}>
                    {[
                      { label:'𝕏', name:'Twitter/X' },
                      { label:'✈', name:'Telegram'  },
                      { label:'◎', name:'Instagram' },
                    ].map(s => (
                      <button key={s.name} className="lp-footer-social-btn lp-social-btn" aria-label={s.name} style={{
                        width:36, height:36, borderRadius:8,
                        background:'var(--overlay-4)',
                        border:'1px solid var(--overlay-8)',
                        color:'var(--text-45)',
                        fontSize:14, cursor:'pointer',
                        display:'flex', alignItems:'center', justifyContent:'center',
                      }}
                      >{s.label}</button>
                    ))}
                  </div>
                </div>

                {/* Product links */}
                <div>
                  <div className="lp-footer-col-heading" style={{ fontSize:10, fontWeight:700, color:'rgba(212,168,67,0.6)', letterSpacing:'0.15em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace", marginBottom:20 }}>Product</div>
                  <div className="u-flex-col u-gap-13">
                    {[['Features','features'],['How it works','howitworks'],['Pricing','pricing'],['Compare','compare'],['FAQ','faq']].map(([l,id]) => (
                      <button key={l} onClick={() => scrollTo(id)} className="lp-footer-link" style={{ background:'none', border:'none', cursor:'pointer', fontSize:13.5, color:'var(--footer-link)', padding:0, textAlign:'left', fontFamily:"'Bricolage Grotesque',sans-serif", fontWeight:500 }}
                      >{l}</button>
                    ))}
                    <a href="#" className="lp-footer-link" style={{ fontSize:13.5, color:'var(--footer-link)', textDecoration:'none', fontFamily:"'Bricolage Grotesque',sans-serif", fontWeight:500 }}
                    >Partner Deals</a>
                  </div>
                </div>

                {/* Tools links */}
                <div>
                  <div className="lp-footer-col-heading" style={{ fontSize:10, fontWeight:700, color:'rgba(212,168,67,0.6)', letterSpacing:'0.15em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace", marginBottom:20 }}>Tools</div>
                  <div className="u-flex-col u-gap-13">
                    {TOOLS_ITEMS.map(t => (
                      <button key={t.label} onClick={onGetStarted} className="lp-footer-link" style={{ background:'none', border:'none', cursor:'pointer', fontSize:13.5, color:'var(--footer-link)', padding:0, textAlign:'left', display:'flex', alignItems:'center', gap:7, fontFamily:"'Bricolage Grotesque',sans-serif", fontWeight:500 }}
                      >
                        {t.label}
                        {t.badge && <span style={{ fontSize:8, padding:'2px 6px', borderRadius:3, background:'rgba(46,204,138,0.12)', color:'#2ECC8A', fontFamily:"'DM Mono',monospace", letterSpacing:'0.06em', border:'1px solid rgba(46,204,138,0.2)' }}>{t.badge}</span>}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Account links */}
                <div>
                  <div className="lp-footer-col-heading" style={{ fontSize:10, fontWeight:700, color:'rgba(212,168,67,0.6)', letterSpacing:'0.15em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace", marginBottom:20 }}>Account</div>
                  <div className="u-flex-col u-gap-13">
                    {['Create Account','Sign In','Dashboard'].map(l => (
                      <button key={l} onClick={onGetStarted} className="lp-footer-link" style={{ background:'none', border:'none', cursor:'pointer', fontSize:13.5, color:'var(--footer-link)', padding:0, textAlign:'left', fontFamily:"'Bricolage Grotesque',sans-serif", fontWeight:500, whiteSpace:'nowrap' }}
                      >{l}</button>
                    ))}
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* ── Bottom bar ── */}
          <div className="lp-footer-bottom" style={{ borderTop:'1px solid var(--overlay-5)', padding:'20px 24px' }}>
            <div className="lp-container">
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
                <div style={{ display:'flex', alignItems:'center', flexWrap:'wrap', gap:12 }}>
                  <span style={{ fontSize:11, color:'var(--text-38)', fontFamily:"'DM Mono',monospace" }}>© {new Date().getFullYear()} KFX Journal. All rights reserved.</span>
                </div>
                <div style={{ display:'flex', gap:20 }}>
                  {[['About','about'],['Privacy Policy','privacy'],['Terms of Service','terms'],['Cookie Policy','cookies']].map(([l,p]) => (
                    <button key={l} onClick={() => p !== 'cookies' && onNavigate && onNavigate(p)} className="lp-footer-link" style={{ fontSize:11, color:'var(--text-35)', fontFamily:"'DM Mono',monospace", textDecoration:'none', padding:0, background:'none', border:'none', cursor:'pointer' }}
                    >{l}</button>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </footer>
        </main>
        }
      </div>
      <FeedbackWidget guestMode={true} />
    </>
  );
}